import { useState, useMemo, useEffect, useRef } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

/*╔═══════════════════════════════════════════════════════════════╗
  ║  IMPORTRUST ESPAÑA — M1: DEALS + M2: BANK                   ║
  ║                                                              ║
  ║  Standard: Margin IT = (Fee-Ops) - VAT(21/121)              ║
  ║  REBU: VAT on trade margin only                              ║
  ║  Commission: 10% MIT + €125 if financed                      ║
  ║  Bank: paste statement → classify → auto-update deals        ║
  ╚═══════════════════════════════════════════════════════════════╝*/

const VAT_RATE = 21/121, COMM_RATE = 0.10, FIN_BONUS = 125;
const fmt = v => v==null?'—':typeof v==='number'?(v<0?'-':'')+Math.abs(v).toLocaleString('de-DE',{minimumFractionDigits:0,maximumFractionDigits:0})+'€':v;
const fmtD = v => v==null?'—':typeof v==='number'?(v<0?'-':'')+Math.abs(v).toLocaleString('de-DE',{minimumFractionDigits:2,maximumFractionDigits:2})+'€':v;
const C = {bg:'#0f0f0f',card:'#1a1a1a',border:'#2a2a2a',tx:'#e5e5e5',mt:'#777',ac:'#3b82f6',green:'#10b981',red:'#ef4444',amber:'#f59e0b',purple:'#8b5cf6',cyan:'#06b6d4'};
const loadS=async(k,fb)=>{try{const r=await window.storage.get(k);return r?JSON.parse(r.value):fb;}catch{return fb;}};
const saveS=async(k,v)=>{try{await window.storage.set(k,JSON.stringify(v));}catch{}};
const exportCSV=(headers,rows,filename)=>{const csv=[headers.join(';'),...rows.map(r=>r.map(c=>typeof c==='string'&&c.includes(';')?`"${c}"`:c??'').join(';'))].join('\n');const blob=new Blob(['\ufeff'+csv],{type:'text/csv;charset=utf-8'});const a=document.createElement('a');a.href=URL.createObjectURL(blob);a.download=filename+'.csv';a.click();};

const AUX={BD:["José Peres","Álvaro López","Javier Giralda","Oscar Guevara","Daniel Arias","Nacho Rodriguez","Jorge de Haro","Enrique Sanchez","Nuno Luz","Álvaro Blidar"],BUSINESS_LINE:["IMPORTATION","CAR SALES","LEGALIZATION","TRANSPORTATION","B2B","CAR LOANS","IN HOUSE","TRANSPORTATION + LEGALIZATION"],ENGINE:["Gasolina","Diesel","Hibrido elecrico/gasolina","Hibrido diesel/gasolina","Electrico"],CAR_BRAND:["Abarth","Alfa Romeo","Alpine","Aston Martin","Audi","BMW","BYD","Citroën","Cupra","DS Automobiles","Ferrari","Fiat","Ford","Honda","Hyundai","Jaguar","Jeep","Land Rover","Lexus","Lincoln","Maserati","Mazda","Mercedes-Benz","MG","Mini","Mitsubishi","Nissan","Opel","Peugeot","Polestar","Porsche","Renault","SEAT","Smart","Subaru","Suzuki","Tesla","Toyota","Volkswagen","Volvo"],PNL:["SPAIN","PORTUGAL","HQ","GLOBAL TRADING"],BANKS:["BANKINTER","SABADELL","SOFINCO"],SUPPLIERS:["Trapiser","CEVA","MC","Metafleet","Isal Express","Gestoria Herranz","Gestoria Pastor","Google ads","Facebook","Hubspot","Cloudtalk","Hiflow","Bolt","Finetwork","DoctorSpin","Carspector","Carvertical"],COST_TYPE:["Transporte","Gestoria IMP","Gestoria matriculacion","Gestoria S16 IVA (Tasas)","ITV","Inspecciones Veiculos","Handling","Last Mile","Matrícula","Documentacion adicional","Otras gastos Operativos","Otras repaciones CEVA","Servicios","Ingresos operaciones","Fee Importrust (VAT)","Nominas","Seguridad Social","Bank fees","Alquiler oficina","Marketing","IT","Material","Comunicaciones","Capital","IVA","Variable operaciones","gastos operaciones","Otros","Entrada de financiacion 1"],COST_LINE:["TEAM","MARKETING","IT","GENERAL","MATERIAL","LEGAL","BANK","OPERATIONS","ACCOUNTING","COMMS","TAXES","COMISSIONS","TRANSPORT (CM1)","HANDLING (CM1)","DOCS (CM1)","MAINTENANCE (CM1)","OTHERS (CM1)","COST LINE (IT)"]};
const MONTHS=["JANUARY","FEBRUARY","MARCH","APRIL","MAY","JUNE","JULY","AUGUST","SEPTEMBER","OCTOBER","NOVEMBER","DECEMBER"];

function calcFin(d){const isR=d.dealType==='rebu',fee=d.ar||0,ops=d.to||0,itv=d.itv||0,ff=d.finFee||0;let om,vat,mit;if(isR){const s=d.rebuSell||0,b=d.rebuBuy||0,tm=s-b;vat=tm>0?tm*VAT_RATE:0;om=fee-ops;mit=tm-vat-ops;}else{om=fee-ops;vat=(fee*VAT_RATE)-((ops-itv)*VAT_RATE);mit=om-vat;}const cb=mit>0?mit*COMM_RATE:0,fb=ff>0?FIN_BONUS:0;return{om,vat,mit,finFee:ff,commBase:cb,commFinBonus:fb,commTotal:cb+fb,isREBU:isR};}

const Inp=({label,...p})=>(<div>{label&&<label style={{fontSize:10,color:C.mt,display:'block',marginBottom:2,letterSpacing:'.03em'}}>{label}</label>}<input {...p} style={{width:'100%',padding:'6px 8px',borderRadius:6,border:`1px solid ${C.border}`,background:C.bg,color:C.tx,fontSize:12,boxSizing:'border-box',outline:'none',...(p.style||{})}}/></div>);
const Sel=({label,options,placeholder,...p})=>(<div>{label&&<label style={{fontSize:10,color:C.mt,display:'block',marginBottom:2,letterSpacing:'.03em'}}>{label}</label>}<select {...p} style={{width:'100%',padding:'6px 8px',borderRadius:6,border:`1px solid ${C.border}`,background:C.bg,color:C.tx,fontSize:12,boxSizing:'border-box',...(p.style||{})}}>{placeholder!==false&&<option value="">{placeholder||'Select...'}</option>}{(options||[]).map(o=><option key={o} value={o}>{o}</option>)}</select></div>);
const Badge=({t,c})=><span style={{padding:'1px 7px',borderRadius:4,fontSize:9,fontWeight:600,background:(c||C.mt)+'18',color:c||C.mt,whiteSpace:'nowrap'}}>{t}</span>;

export default function App({user,onLogout}={}){
  const [tab,setTab]=useState('bd');const [showNew,setShowNew]=useState(false);
  const [clients,setClients]=useState([]);const [bankTxs,setBankTxs]=useState([]);const [aux,setAux]=useState(AUX);const [templates,setTemplates]=useState([]);const [ready,setReady]=useState(false);
  const [isMobile,setIsMobile]=useState(()=>typeof window!=='undefined'&&window.matchMedia('(max-width: 768px)').matches);
  const [menuOpen,setMenuOpen]=useState(false);
  useEffect(()=>{const mq=window.matchMedia('(max-width: 768px)');const onChange=()=>setIsMobile(mq.matches);mq.addEventListener('change',onChange);return()=>mq.removeEventListener('change',onChange);},[]);
  const openTab=(id)=>{setTab(id);setShowNew(false);setMenuOpen(false);};
  const openNewDeal=()=>{setShowNew(true);setMenuOpen(false);};
  useEffect(()=>{Promise.all([loadS('ir_deals_v4',[]),loadS('ir_bank_v4',[]),loadS('ir_aux_v4',AUX),loadS('ir_tpl_v4',[])]).then(([d,b,a,t])=>{
    const fixedDeals=d.map(deal=>{const f=calcFin(deal);return{...deal,commTotal:f.commTotal,pct:f.commTotal};});
    // Migration: country → pnl on bank txs
    const migratedB=b.map(tx=>{if(tx.country!==undefined&&tx.pnl===undefined){const{country,...rest}=tx;return{...rest,pnl:country};}return tx;});
    // Migration: aux COUNTRY → PNL, ensure GLOBAL TRADING present
    const mAux={...a};
    if(mAux.COUNTRY&&!mAux.PNL){mAux.PNL=[...mAux.COUNTRY];delete mAux.COUNTRY;}
    if(Array.isArray(mAux.PNL)&&!mAux.PNL.includes('GLOBAL TRADING')){mAux.PNL=[...mAux.PNL,'GLOBAL TRADING'];}
    setClients(fixedDeals);setBankTxs(migratedB);setAux({...AUX,...mAux});setTemplates(t);setReady(true);
  });},[]);
  useEffect(()=>{if(ready)saveS('ir_deals_v4',clients);},[clients,ready]);useEffect(()=>{if(ready)saveS('ir_bank_v4',bankTxs);},[bankTxs,ready]);useEffect(()=>{if(ready)saveS('ir_aux_v4',aux);},[aux,ready]);useEffect(()=>{if(ready)saveS('ir_tpl_v4',templates);},[templates,ready]);
  const TABS=[{id:'bd',label:'BD Performance'},{id:'dash',label:'Dashboard'},{id:'deals',label:'Deals'},{id:'bank',label:'Bank'},{id:'invoices',label:'Invoice Reader'},{id:'settings',label:'Settings'}];
  return(<div style={{fontFamily:"'DM Sans',system-ui,sans-serif",color:C.tx,minHeight:'100vh'}}>
    <div style={{display:'flex',alignItems:'center',padding:'0 16px',borderBottom:`1px solid ${C.border}`,background:C.card,position:'sticky',top:0,zIndex:100}}>
      <div style={{display:'flex',alignItems:'center',gap:8,paddingRight:isMobile?0:20,marginRight:isMobile?0:8,borderRight:isMobile?'none':`1px solid ${C.border}`,flexShrink:0}}>
        <div style={{width:28,height:28,borderRadius:6,background:'linear-gradient(135deg,#3b82f6,#8b5cf6)',display:'flex',alignItems:'center',justifyContent:'center',fontWeight:800,fontSize:11,color:'#fff'}}>IR</div>
        {!isMobile&&<div><div style={{fontWeight:700,fontSize:13}}>Importrust ES</div><div style={{fontSize:9,color:C.mt}}>{clients.length} deals · {bankTxs.length} txs</div></div>}
      </div>
      {isMobile ? (
        <button onClick={()=>setMenuOpen(o=>!o)} aria-label={menuOpen?'Close menu':'Open menu'} style={{marginLeft:'auto',marginRight:8,width:38,height:32,padding:0,borderRadius:6,border:`1px solid ${C.border}`,background:menuOpen?C.border:'transparent',color:C.tx,fontSize:18,lineHeight:1,cursor:'pointer',flexShrink:0}}>{menuOpen?'×':'☰'}</button>
      ) : (
        <div className="ir-tabs" style={{display:'flex',alignItems:'center',gap:0,flex:'1 1 auto',minWidth:0,overflowX:'auto',overflowY:'hidden'}}>
          {TABS.map(t=>(<button key={t.id} onClick={()=>openTab(t.id)} style={{padding:'11px 14px',border:'none',borderBottom:tab===t.id&&!showNew?`2px solid ${C.ac}`:'2px solid transparent',background:'transparent',color:tab===t.id&&!showNew?C.tx:C.mt,cursor:'pointer',fontSize:12,fontWeight:tab===t.id&&!showNew?600:400,whiteSpace:'nowrap',flexShrink:0}}>{t.label}</button>))}
          <button onClick={openNewDeal} style={{padding:'7px 16px',borderRadius:7,border:'none',background:'#fff',color:'#111',fontWeight:700,fontSize:12,cursor:'pointer',boxShadow:'0 1px 3px rgba(0,0,0,.2)',marginLeft:10,flexShrink:0,whiteSpace:'nowrap'}}>+ New Deal</button>
        </div>
      )}
      <div style={{display:'flex',alignItems:'center',gap:10,flexShrink:0}}>
        {onLogout&&<button onClick={onLogout} style={{padding:'4px 10px',borderRadius:6,border:`1px solid ${C.border}`,background:'transparent',color:C.tx,fontSize:11,cursor:'pointer',whiteSpace:'nowrap'}}>Sign out</button>}
      </div>
    </div>
    {isMobile&&menuOpen&&(
      <>
        {/* Backdrop: tap-anywhere closes the drawer. */}
        <div onClick={()=>setMenuOpen(false)} style={{position:'fixed',top:45,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.4)',zIndex:98}}/>
        {/* Drawer: vertical tab list + New Deal. */}
        <div style={{position:'fixed',top:45,left:0,right:0,background:C.card,borderBottom:`1px solid ${C.border}`,zIndex:99,display:'flex',flexDirection:'column',padding:'6px 0',boxShadow:'0 8px 24px rgba(0,0,0,0.4)'}}>
          {TABS.map(t=>(<button key={t.id} onClick={()=>openTab(t.id)} style={{padding:'12px 20px',border:'none',borderLeft:tab===t.id&&!showNew?`3px solid ${C.ac}`:'3px solid transparent',background:tab===t.id&&!showNew?'rgba(59,130,246,0.08)':'transparent',color:tab===t.id&&!showNew?C.tx:C.mt,cursor:'pointer',fontSize:13,fontWeight:tab===t.id&&!showNew?600:400,textAlign:'left'}}>{t.label}</button>))}
          <div style={{padding:'8px 16px 4px'}}>
            <button onClick={openNewDeal} style={{width:'100%',padding:'10px 16px',borderRadius:7,border:'none',background:'#fff',color:'#111',fontWeight:700,fontSize:13,cursor:'pointer'}}>+ New Deal</button>
          </div>
        </div>
      </>
    )}
    <div style={{padding:'14px 18px',maxWidth:1400,margin:'0 auto'}}>
      {showNew&&<NewDeal clients={clients} setClients={setClients} aux={aux} onDone={()=>{setShowNew(false);setTab('deals');}}/>}
      {!showNew&&tab==='deals'&&<DealsList clients={clients} setClients={setClients} bankTxs={bankTxs} setBankTxs={setBankTxs} aux={aux} onNewDeal={()=>setShowNew(true)}/>}
      {!showNew&&tab==='bank'&&<BankModule bankTxs={bankTxs} setBankTxs={setBankTxs} clients={clients} setClients={setClients} aux={aux} templates={templates} setTemplates={setTemplates}/>}
      {!showNew&&tab==='dash'&&<Dashboard clients={clients} bankTxs={bankTxs}/>}
      {!showNew&&tab==='bd'&&<BDPerf clients={clients} bankTxs={bankTxs} onViewDeal={(dealId)=>{setTab('deals');/* deal detail handled in DealsList */}}/>}
      {!showNew&&tab==='invoices'&&<InvoiceReader clients={clients} bankTxs={bankTxs} setBankTxs={setBankTxs} setClients={setClients} aux={aux}/>}
      {!showNew&&tab==='settings'&&<Settings aux={aux} setAux={setAux}/>}
    </div>
  </div>);
}

// ══════════════════════════════════════════
//  CASH NEEDS (sub-component)
// ══════════════════════════════════════════
function CashNeeds({bankTxs}){
  const upcoming=useMemo(()=>{
    const unpaid=bankTxs.filter(t=>t.pagado!=='Yes'&&t.expectedDate);
    const byDay={};
    unpaid.forEach(t=>{
      const d=t.expectedDate;
      if(!byDay[d])byDay[d]={date:d,items:[],outTotal:0,inTotal:0};
      byDay[d].items.push(t);
      if(t.amount<0)byDay[d].outTotal+=Math.abs(t.amount||0);
      else byDay[d].inTotal+=(t.amount||0);
    });
    return Object.values(byDay).sort((a,b)=>a.date.localeCompare(b.date));
  },[bankTxs]);
  const grandOut=upcoming.reduce((s,d)=>s+d.outTotal,0);
  const grandIn=upcoming.reduce((s,d)=>s+d.inTotal,0);
  const grandNet=grandIn-grandOut;
  if(upcoming.length===0)return(<div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:16,marginBottom:12,textAlign:'center',color:C.mt,fontSize:12}}>No upcoming cash needs</div>);
  let runningNet=0;
  return(<div style={{background:C.card,border:`1px solid ${C.cyan}30`,borderRadius:10,padding:16,marginBottom:12}}>
    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10,flexWrap:'wrap',gap:8}}>
      <div style={{fontSize:12,fontWeight:600,color:C.cyan}}>Cash Needs — Expected Payments & Inflows</div>
      <div style={{display:'flex',gap:12,fontSize:11}}>
        <span style={{color:C.green}}>In: {fmt(Math.round(grandIn))}</span>
        <span style={{color:C.red}}>Out: {fmt(Math.round(grandOut))}</span>
        <span style={{fontWeight:700,color:grandNet>=0?C.green:C.red}}>Net: {fmt(Math.round(grandNet))}</span>
      </div>
    </div>
    <table style={{width:'100%',borderCollapse:'collapse',fontSize:11}}>
      <thead><tr style={{borderBottom:`1px solid ${C.border}`}}>{['Date','Description','Supplier','In','Out','Running Net'].map(h=><th key={h} style={{padding:'6px',textAlign:h==='In'||h==='Out'||h==='Running Net'?'right':'left',color:C.mt,fontWeight:500,fontSize:9,textTransform:'uppercase'}}>{h}</th>)}</tr></thead>
      <tbody>{upcoming.map(day=>{const dayNet=day.inTotal-day.outTotal;runningNet+=dayNet;const rows=[];
        rows.push(<tr key={day.date+'h'} style={{borderBottom:`1px solid ${C.border}`}}>
          <td style={{padding:'6px',fontWeight:700,fontSize:11,color:'#fff'}}>{day.date}</td>
          <td style={{padding:'6px',fontSize:10,color:'#aaa'}}>{day.items.length} item{day.items.length>1?'s':''}</td>
          <td/>
          <td style={{padding:'6px',textAlign:'right',color:C.green,fontWeight:700}}>{day.inTotal>0?'+'+fmt(Math.round(day.inTotal)):''}</td>
          <td style={{padding:'6px',textAlign:'right',color:C.red,fontWeight:700}}>{day.outTotal>0?'-'+fmt(Math.round(day.outTotal)):''}</td>
          <td style={{padding:'6px',textAlign:'right',fontWeight:700,color:runningNet>=0?C.green:C.red}}>{fmt(Math.round(runningNet))}</td>
        </tr>);
        day.items.forEach((t,i)=>rows.push(<tr key={(t.id||i)+'r'} style={{borderBottom:`1px solid ${C.border}08`}}>
          <td style={{padding:'3px 6px',paddingLeft:20,color:'#666',fontSize:10}}/>
          <td style={{padding:'3px 6px',fontSize:10,color:'#999'}}>{(t.desc||'').slice(0,40)}</td>
          <td style={{padding:'3px 6px',fontSize:10,color:'#888'}}>{t.supplier||'—'}</td>
          <td style={{padding:'3px 6px',textAlign:'right',color:'#999'}}>{t.amount>0?'+'+fmt(Math.round(t.amount)):''}</td>
          <td style={{padding:'3px 6px',textAlign:'right',color:'#999'}}>{t.amount<0?'-'+fmt(Math.round(Math.abs(t.amount))):''}</td>
          <td/>
        </tr>));
        return rows;
      }).flat()}</tbody>
    </table>
  </div>);
}

// ══════════════════════════════════════════
//  MODULE 2: BANK
// ══════════════════════════════════════════
function BankModule({bankTxs,setBankTxs,clients,setClients,aux,templates,setTemplates}){
  const [view,setView]=useState('list');const [paste,setPaste]=useState('');const [selTx,setSelTx]=useState(null);
  const [fC,setFC]=useState('ALL');const [fM,setFM]=useState('ALL');const [q,setQ]=useState('');
  const [fY,setFY]=useState('ALL');const [fP,setFP]=useState('ALL');const [showCash,setShowCash]=useState(false);
  const [splitMode,setSplitMode]=useState(false);
  const [splitLines,setSplitLines]=useState([{clientCode:'',amount:'',costType:'',supplier:''}]);
  const [isMulti,setIsMulti]=useState(false);
  const [singleTx,setSingleTx]=useState({date:new Date().toISOString().slice(0,10),desc:'',amount:'',flowType:'Outflow',classification:'Operations',pnl:'SPAIN',bank:'BANKINTER',clientCode:'',supplier:'',costType:'',costLine:'',businessLine:'',hasIVA:'',ivaRate:21,invoiceRef:'',pagado:'',expectedDate:'',fromTemplate:''});
  const [copyMonth,setCopyMonth]=useState('');const [copyYear,setCopyYear]=useState('');
  const [tplForm,setTplForm]=useState(null);
  const [xlData,setXlData]=useState(null);
  const [copyFilter,setCopyFilter]=useState('all');
  const [copyExclude,setCopyExclude]=useState([]);
  const fileRef=useRef(null);
  const stmtFileRef=useRef(null);

  const kpis=useMemo(()=>{const inf=bankTxs.filter(t=>t.amount>0).reduce((s,t)=>s+t.amount,0);const out=bankTxs.filter(t=>t.amount<0).reduce((s,t)=>s+Math.abs(t.amount),0);const exp=bankTxs.filter(t=>t._expected&&t.pagado!=='Yes').length;const opsAmt=bankTxs.filter(t=>t.classification==='Operations').reduce((s,t)=>s+Math.abs(t.amount||0),0);const genAmt=bankTxs.filter(t=>t.classification==='General').reduce((s,t)=>s+Math.abs(t.amount||0),0);return{n:bankTxs.length,inf,out,opsAmt,genAmt,unc:bankTxs.filter(t=>!t.classification).length,exp};},[bankTxs]);

  const list=useMemo(()=>{let f=[...bankTxs];if(q){const s=q.toLowerCase();f=f.filter(t=>(t.desc||'').toLowerCase().includes(s)||(t.clientCode||'').toLowerCase().includes(s)||(t.supplier||'').toLowerCase().includes(s));}if(fC!=='ALL'){if(fC==='Expected')f=f.filter(t=>t._expected&&t.pagado!=='Yes');else if(fC==='Unclassified')f=f.filter(t=>!t.classification);else f=f.filter(t=>t.classification===fC);}if(fM!=='ALL')f=f.filter(t=>t.month===fM);if(fY!=='ALL')f=f.filter(t=>String(t.year)===fY);if(fP!=='ALL')f=f.filter(t=>fP==='Paid'?t.pagado==='Yes':t.pagado!=='Yes');f.sort((a,b)=>new Date(b.date)-new Date(a.date));return f;},[bankTxs,q,fC,fM,fY,fP]);
  const months=useMemo(()=>[...new Set(bankTxs.map(t=>t.month).filter(Boolean))],[bankTxs]);
  const bankYears=useMemo(()=>[...new Set(bankTxs.map(t=>String(t.year)).filter(y=>y&&y!=='0'))].sort((a,b)=>+b-(+a)),[bankTxs]);
  const [preview,setPreview]=useState(null); // parsed lines with dup detection

  // ── DUPLICATE DETECTION ENGINE ──
  const normalizeDesc=(desc)=>{
    let t=(desc||'').toUpperCase();
    t=t.normalize('NFD').replace(/[\u0300-\u036f]/g,''); // remove accents
    t=t.replace(/[^A-Z0-9\s]/g,' ').replace(/\s+/g,' ').trim();
    ['SEPA','TRF','TRANSF','TRANSFERENCIA','PAG','PAGAMENTO','COMPRA','DEBITO','CREDITO','DD','REF','REFERENCIA','TRANS','OTRAS','TRANSF OT'].forEach(w=>{t=t.replace(new RegExp('\\b'+w+'\\b','g'),'');});
    t=t.replace(/\s+/g,' ').trim();
    // Known aliases
    const aliases={'CEVA FINISHED':'CEVA','CEVA LOGISTICS':'CEVA','CEVA PORTUGAL':'CEVA','GOOGLE IRELAND':'GOOGLE','BANKINTER':'BANKINTER'};
    for(const[k,v] of Object.entries(aliases)){if(t.includes(k))t=t.replace(k,v);}
    return t;
  };
  const sameDirection=(a,b)=>(a>=0)===(b>=0);
  const daysBetween=(d1,d2)=>{try{return Math.abs(Math.round((new Date(d1)-new Date(d2))/86400000));}catch{return 999;}};
  const sameMonth=(d1,d2)=>{try{const a=new Date(d1),b=new Date(d2);return a.getMonth()===b.getMonth()&&a.getFullYear()===b.getFullYear();}catch{return false;}};

  const dupCheck=(newTx)=>{
    const nDesc=normalizeDesc(newTx.desc);
    const nAmt=Math.abs(newTx.amount);
    const strong=[];const probable=[];
    for(const tx of bankTxs){
      const eDesc=normalizeDesc(tx.desc);const eAmt=Math.abs(tx.amount);
      if(!sameDirection(newTx.amount,tx.amount))continue;
      if(Math.abs(nAmt-eAmt)>0.01)continue;
      // Exact: same desc + within 1 day
      if(nDesc===eDesc&&daysBetween(newTx.date,tx.date)<=1){strong.push(tx);continue;}
      // Reference match
      if(newTx.invoiceRef&&tx.invoiceRef&&newTx.invoiceRef===tx.invoiceRef){strong.push(tx);continue;}
      // Probable: same desc + same month + >1 day apart
      if(nDesc===eDesc&&sameMonth(newTx.date,tx.date)&&daysBetween(newTx.date,tx.date)>1){probable.push(tx);continue;}
    }
    if(strong.length>0){const m=strong[0];return{status:'BLOCK',reason:'Same amount+desc within 1 day',matchDesc:(m.desc||'').slice(0,35),matchDate:m.date,matchAmt:m.amount,match:m};}
    if(probable.length>0){const m=probable[0];return{status:'WARN',reason:'Similar tx same month',matchDesc:(m.desc||'').slice(0,35),matchDate:m.date,matchAmt:m.amount,match:m};}
    return{status:'OK',reason:'',match:null};
  };
  // Legacy compat
  const isDup=(date,amount,desc)=>{const r=dupCheck({date,amount,desc:desc||'',invoiceRef:''});return r.status!=='OK'?r.match:null;};

  // Copy from previous month — takes all General txs from a given month, creates Expected copies for current month
  const doCopyMonth=()=>{
    if(!copyMonth||!copyYear)return;
    const src=bankTxs.filter(t=>t.month===copyMonth&&t.year===+copyYear);
    if(src.length===0)return;
    const now=new Date();const curMonth=MONTHS[now.getMonth()];const curYear=now.getFullYear();
    const copies=src.map(t=>({
      ...t, id:Date.now()+Math.random(), date:now.toISOString().slice(0,10),
      month:curMonth, year:curYear, pagado:'No', _expected:true,
      invoiceRef:'' // clear old invoice ref
    }));
    // Dedup against existing current month
    const toAdd=copies.filter(c=>!bankTxs.find(e=>e.month===curMonth&&e.year===curYear&&e.desc===c.desc&&e.classification===c.classification&&e.supplier===c.supplier));
    if(toAdd.length>0)setBankTxs(p=>[...toAdd,...p]);
    setView('list');setFM(curMonth);
    return toAdd.length;
  };

  // Create txs from a template
  const instantiateTemplate=(tpl)=>{
    const now=new Date();const curMonth=MONTHS[now.getMonth()];const curYear=now.getFullYear();
    const tx={...tpl, id:Date.now()+Math.random(), date:now.toISOString().slice(0,10),
      month:curMonth, year:curYear, pagado:'No', _expected:true};
    delete tx._isTemplate;
    if(!isDup(tx.date,tx.amount,tx.desc))setBankTxs(p=>[tx,...p]);
  };

  const availableMonths=useMemo(()=>{
    const my=new Set();bankTxs.forEach(t=>{if(t.month&&t.year)my.add(`${t.month}|${t.year}`);});
    return [...my].map(s=>{const[m,y]=s.split('|');return{month:m,year:+y,label:`${m} ${y}`};}).sort((a,b)=>b.year-a.year||MONTHS.indexOf(b.month)-MONTHS.indexOf(a.month));
  },[bankTxs]);

  const parsePaste=()=>{const lines=paste.trim().split('\n').filter(l=>l.trim());const nw=[];
    for(const line of lines){const sep=line.includes('\t')?'\t':line.includes(';')?';':null;if(!sep)continue;const parts=line.split(sep).map(s=>s.trim());if(parts.length<2)continue;
      let date=null,desc='',amount=0;
      for(const p of parts){if(!date){const m=p.match(/^(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{2,4})$/);if(m){const yr=m[3].length===2?'20'+m[3]:m[3];const d=new Date(`${yr}-${m[2].padStart(2,'0')}-${m[1].padStart(2,'0')}`);if(!isNaN(d)){date=d.toISOString().slice(0,10);continue;}}const d2=new Date(p);if(!isNaN(d2)&&p.length>=8){date=d2.toISOString().slice(0,10);continue;}}
        const ns=p.replace(/[€\s]/g,'').replace(/\./g,'').replace(',','.');const num=parseFloat(ns);if(!isNaN(num)&&p.replace(/[€\s\.,\-]/g,'').length>0){if(amount===0)amount=num;}
        else if(p.length>2)desc+=(desc?' ':'')+p;}
      if(!date)date=new Date().toISOString().slice(0,10);if(amount===0){const ns=parts[parts.length-1].replace(/[€\s]/g,'').replace(/\./g,'').replace(',','.');amount=parseFloat(ns)||0;}
      if(!desc)desc=parts.slice(1,-1).join(' ')||parts[1]||'Unknown';
      if(amount===0)continue;
      const dt=new Date(date);const dup=isDup(date,amount,desc.slice(0,120));
      nw.push({id:Date.now()+Math.random(),date,month:MONTHS[dt.getMonth()]||'',year:dt.getFullYear(),desc:desc.slice(0,120),amount,classification:'',pnl:'SPAIN',bank:'BANKINTER',clientCode:'',supplier:'',costType:'',costLine:'',businessLine:'',hasIVA:'',ivaRate:'',invoiceRef:'',pagado:'No',_dup:!!dup,_skip:!!dup});
    }
    if(nw.length>0)setPreview(nw);
  };

  const confirmImport=()=>{if(!preview)return;const toAdd=preview.filter(t=>!t._skip).map(({_dup,_skip,...rest})=>rest);if(toAdd.length>0)setBankTxs(p=>[...toAdd,...p]);setPreview(null);setPaste('');setView('list');};
  const toggleSkip=(id)=>setPreview(p=>p&&p.map(t=>t.id===id?{...t,_skip:!t._skip}:t));

  // ── EXCEL IMPORT ──
  const handleExcelFile=async(file)=>{
    try{
      const XLSX=await import('xlsx');
      const buf=await file.arrayBuffer();
      const wb=XLSX.read(buf,{type:'array',cellDates:true});
      const ws=wb.Sheets[wb.SheetNames[0]];
      const json=XLSX.utils.sheet_to_json(ws,{header:1,defval:''});
      if(json.length<2)return;
      const headers=json[0].map((h,i)=>String(h||`Col${i+1}`));
      const rows=json.slice(1).filter(r=>r.some(c=>c!==''));
      // Auto-detect columns
      const colMap={date:'',desc:'',amount:''};
      headers.forEach((h,i)=>{
        const hl=h.toLowerCase();
        if(!colMap.date&&(hl.includes('date')||hl.includes('fecha')||hl.includes('data')))colMap.date=h;
        if(!colMap.desc&&(hl.includes('desc')||hl.includes('concept')||hl.includes('detail')||hl.includes('referenc')||hl.includes('movimiento')))colMap.desc=h;
        if(!colMap.amount&&(hl.includes('amount')||hl.includes('importe')||hl.includes('valor')||hl.includes('monto')||hl.includes('saldo')===false&&hl.includes('cant')))colMap.amount=h;
      });
      // If no amount found, try last numeric-looking column
      if(!colMap.amount){for(let i=headers.length-1;i>=0;i--){if(rows.some(r=>typeof r[i]==='number')){colMap.amount=headers[i];break;}}}
      setXlData({headers,rows,colMap});
      setView('excel');
    }catch(err){console.error('Excel parse error:',err);}
  };

  const processExcelImport=()=>{
    if(!xlData)return;
    const{headers,rows,colMap}=xlData;
    const di=headers.indexOf(colMap.date),dsi=headers.indexOf(colMap.desc),ai=headers.indexOf(colMap.amount);
    if(di<0||ai<0)return;
    const nw=[];
    for(const row of rows){
      let date=null;const rawD=row[di];
      if(rawD instanceof Date&&!isNaN(rawD)){date=rawD.toISOString().slice(0,10);}
      else if(typeof rawD==='string'){const m=rawD.match(/^(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{2,4})$/);if(m){const yr=m[3].length===2?'20'+m[3]:m[3];const d=new Date(`${yr}-${m[2].padStart(2,'0')}-${m[1].padStart(2,'0')}`);if(!isNaN(d))date=d.toISOString().slice(0,10);}if(!date){const d2=new Date(rawD);if(!isNaN(d2))date=d2.toISOString().slice(0,10);}}
      else if(typeof rawD==='number'){const d=new Date((rawD-25569)*86400000);if(!isNaN(d))date=d.toISOString().slice(0,10);}
      if(!date)continue;
      const desc=dsi>=0?String(row[dsi]||'').slice(0,120):'';
      let amount=0;const rawA=row[ai];
      if(typeof rawA==='number')amount=rawA;
      else if(typeof rawA==='string'){amount=parseFloat(rawA.replace(/[€\s]/g,'').replace(/\./g,'').replace(',','.'))||0;}
      if(amount===0)continue;
      const dt=new Date(date);const dup=isDup(date,amount,desc);
      const dc2=dupCheck({date,amount,desc,invoiceRef:''});
      nw.push({id:Date.now()+Math.random(),date,month:MONTHS[dt.getMonth()]||'',year:dt.getFullYear(),desc,amount,classification:'',pnl:'SPAIN',bank:'BANKINTER',clientCode:'',supplier:'',costType:'',costLine:'',businessLine:'',hasIVA:'',ivaRate:'',invoiceRef:'',pagado:'No',_dupStatus:dc2.status,_dupReason:dc2.reason,_dupMatchDesc:dc2.matchDesc||'',_dupMatchDate:dc2.matchDate||'',_dupMatchAmt:dc2.matchAmt,_dup:dc2.status==='BLOCK',_skip:dc2.status==='BLOCK'});
    }
    if(nw.length>0)setPreview(nw);
    setXlData(null);setView('paste');
  };

  const updTx=(id,k,v)=>{setSelTx(p=>p&&p.id===id?{...p,[k]:v}:p);};
  const saveTxChanges=()=>{
    if(!selTx)return;
    const oldTx=bankTxs.find(t=>t.id===selTx.id);
    // If clientCode changed on an Operations tx, revert old client and apply to new
    if(oldTx&&oldTx.clientCode&&oldTx.clientCode!==selTx.clientCode&&oldTx.classification==='Operations'){
      const costMap={'TRANSPORTATION':'tr','GESTORIA':'gt','CEVA':'ct','DOC.ADICIONAL':'doc','ITV':'itv','REPARACIONES':'orep'};
      const ck=costMap[(oldTx.costType||'').toUpperCase()];
      if(ck){
        setClients(prev=>prev.map(c=>{
          if(c.c!==oldTx.clientCode)return c;
          const u={...c,[ck]:0};u[ck+'Real']=false;u.to=(u.tr||0)+(u.gt||0)+(u.ct||0)+(u.doc||0)+(u.orep||0)+(u.otherCosts||0);const f=calcFin(u);u.om=f.om;u.vat=f.vat;u.mit=f.mit;return u;
        }));
      }
      if(selTx.clientCode){applyToDeal(selTx);}
    }
    // If Operations tx with clientCode, always apply to deal (handles pagado changes, cost updates)
    if(selTx.classification==='Operations'&&selTx.clientCode){
      applyToDeal(selTx);
    }
    setBankTxs(p=>p.map(t=>t.id===selTx.id?{...selTx}:t));setSelTx(null);
  };
  const cancelTxChanges=()=>{setSelTx(null);setSplitMode(false);};

  const applyToDeal=(tx)=>{if(!tx.clientCode||tx.classification!=='Operations')return;
    setClients(p=>p.map(c=>{if(c.c!==tx.clientCode)return c;let u={...c};
      if(tx.amount>0){u.rc=(u.rc||0)+tx.amount;u.dc=(u.ar||0)-u.rc;}
      else{const m={'Transporte':'tr','Gestoria IMP':'gt','Gestoria matriculacion':'gt','Gestoria S16 IVA (Tasas)':'gt','ITV':'itv','Inspecciones Veiculos':'itv','CEVA':'ct','Handling':'hdl','Last Mile':'lm','Documentacion adicional':'doc','Otras gastos Operativos':'otherCosts','Otras repaciones CEVA':'orep'};
        const ck=m[tx.costType];if(ck){u[ck]=Math.abs(tx.amount);u[ck+'Real']=true;u.to=(u.tr||0)+(u.gt||0)+(u.ct||0)+(u.doc||0)+(u.orep||0)+(u.otherCosts||0);const f=calcFin(u);u.om=f.om;u.vat=f.vat;u.mit=f.mit;u.pct=f.commTotal;}}
      return u;}));};

  const revertTxFromDeal=(tx)=>{if(!tx.clientCode||tx.classification!=='Operations')return;
    setClients(p=>p.map(c=>{if(c.c!==tx.clientCode)return c;let u={...c};
      if(tx.amount>0){u.rc=Math.max(0,(u.rc||0)-tx.amount);u.dc=(u.ar||0)-u.rc;}
      else{const m={'Transporte':'tr','Gestoria IMP':'gt','Gestoria matriculacion':'gt','Gestoria S16 IVA (Tasas)':'gt','ITV':'itv','Inspecciones Veiculos':'itv','CEVA':'ct','Handling':'hdl','Last Mile':'lm','Documentacion adicional':'doc','Otras gastos Operativos':'otherCosts','Otras repaciones CEVA':'orep'};
        const ck=m[tx.costType];if(ck){u[ck]=0;u[ck+'Real']=false;u.to=(u.tr||0)+(u.gt||0)+(u.ct||0)+(u.doc||0)+(u.orep||0)+(u.otherCosts||0);const f=calcFin(u);u.om=f.om;u.vat=f.vat;u.mit=f.mit;u.pct=f.commTotal;}}
      return u;}));};

  const deleteTx=()=>{
    if(!selTx)return;
    const amt=fmt(Math.abs(selTx.amount||0));
    const sign=selTx.amount>=0?'+':'-';
    if(!confirm(`Delete this transaction?\n\n${selTx.desc}\n${selTx.date} · ${sign}${amt}€\n\nThis cannot be undone.`))return;
    if(selTx.classification==='Operations'&&selTx.clientCode){revertTxFromDeal(selTx);}
    setBankTxs(p=>p.filter(t=>t.id!==selTx.id));
    setSelTx(null);
  };

  // Split a bulk transaction into multiple sub-transactions, one per deal
  const executeSplit=(tx)=>{
    const subs=splitLines.filter(l=>l.clientCode&&+l.amount);
    if(subs.length<2)return;
    const newTxs=subs.map((line,i)=>({
      id:Date.now()+Math.random()+i, date:tx.date, month:tx.month, year:tx.year,
      desc:`${tx.desc} [split ${i+1}/${subs.length}]`, amount:tx.amount>=0?+line.amount:-(+line.amount),
      classification:'Operations', pnl:tx.pnl||'SPAIN', bank:tx.bank||'BANKINTER',
      clientCode:line.clientCode, supplier:line.supplier||tx.supplier||'', costType:line.costType||tx.costType||'',
      costLine:'', businessLine:tx.businessLine||'', hasIVA:tx.hasIVA||'', ivaRate:tx.ivaRate||'',
      invoiceRef:tx.invoiceRef||'', pagado:'No', parentId:tx.id
    }));
    // Remove original, add children
    setBankTxs(p=>[...newTxs,...p.filter(t=>t.id!==tx.id)]);
    setSplitMode(false);setSplitLines([{clientCode:'',amount:'',costType:'',supplier:''}]);setSelTx(null);
  };

  // ── CLASSIFY ──
  if(selTx){const tx=selTx;const isOps=tx.classification==='Operations';const isGen=tx.classification==='General';
    return(<div><button onClick={cancelTxChanges} style={{padding:'5px 12px',borderRadius:6,border:`1px solid ${C.border}`,background:'transparent',color:C.mt,cursor:'pointer',fontSize:11,marginBottom:12}}>← Cancel</button>
      <div style={{fontSize:15,fontWeight:700,marginBottom:14}}>Classify Transaction</div>
      <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:16,marginBottom:12}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
          <div><div style={{fontSize:13,fontWeight:600}}>{tx.desc}</div><div style={{fontSize:10,color:C.mt,marginTop:2}}>{tx.date} · {tx.month} {tx.year}</div></div>
          <div style={{display:'flex',alignItems:'center',gap:6}}>
            <input type="number" value={tx.amount} onChange={e=>updTx(tx.id,'amount',+e.target.value)} style={{width:120,padding:'4px 8px',borderRadius:6,border:`1px solid ${C.border}`,background:C.bg,color:tx.amount>=0?C.green:C.red,fontSize:16,fontWeight:700,textAlign:'right',boxSizing:'border-box',outline:'none'}}/>
            <span style={{fontSize:10,color:C.mt}}>€</span>
          </div>
        </div>
        <div style={{display:'flex',gap:5}}><Badge t={tx.amount>=0?'Inflow':'Outflow'} c={tx.amount>=0?C.green:C.red}/>{tx.classification&&<Badge t={tx.classification} c={tx.classification==='Operations'?C.ac:C.purple}/>}{tx.clientCode&&<Badge t={clients.find(c=>c.c===tx.clientCode)?.n||tx.clientCode.slice(0,20)} c={C.green}/>}</div>
      </div>
      <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:16}}>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10}}>
          <div><label style={{fontSize:10,color:C.mt,display:'block',marginBottom:2}}>Classification *</label><div style={{display:'flex',gap:4}}>{['Operations','General','Intracompany'].map(v=>(<button key={v} onClick={()=>updTx(tx.id,'classification',v)} style={{flex:1,padding:'6px',borderRadius:6,fontSize:11,fontWeight:tx.classification===v?600:400,cursor:'pointer',border:`1px solid ${tx.classification===v?(v==='Operations'?C.ac:C.purple):C.border}`,background:tx.classification===v?(v==='Operations'?C.ac+'15':C.purple+'15'):'transparent',color:tx.classification===v?(v==='Operations'?C.ac:C.purple):C.mt}}>{v}</button>))}</div></div>
          <Sel label="PnL" value={tx.pnl||''} onChange={e=>updTx(tx.id,'pnl',e.target.value)} options={aux.PNL||[]} placeholder={false}/>
          <Sel label="Bank" value={tx.bank||''} onChange={e=>updTx(tx.id,'bank',e.target.value)} options={aux.BANKS||[]} placeholder={false}/>
        </div>

        {isOps&&(<div style={{marginTop:12,padding:12,background:C.ac+'08',borderRadius:8,border:`1px solid ${C.ac}20`}}>
          <div style={{fontSize:10,color:C.ac,textTransform:'uppercase',letterSpacing:'.04em',marginBottom:8}}>Operations — Link to Deal</div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
            <div><label style={{fontSize:10,color:C.mt,display:'block',marginBottom:2}}>Client ID</label><input value={tx.clientCode||''} onChange={e=>updTx(tx.id,'clientCode',e.target.value)} placeholder="Type or select..." list="cl-list" style={{width:'100%',padding:'6px 8px',borderRadius:6,border:`1px solid ${C.border}`,background:C.bg,color:C.tx,fontSize:12,boxSizing:'border-box',outline:'none'}}/><datalist id="cl-list">{clients.map(c=><option key={c.c} value={c.c}>{c.n} (#{c.i})</option>)}</datalist>
              {tx.clientCode&&clients.find(c=>c.c===tx.clientCode)?<div style={{fontSize:9,color:C.green,marginTop:3}}>#{clients.find(c=>c.c===tx.clientCode).i} — {clients.find(c=>c.c===tx.clientCode).n}</div>:tx.clientCode?<div style={{fontSize:9,color:C.amber,marginTop:3}}>No match</div>:null}</div>
            <Sel label="Business Line" value={tx.businessLine||''} onChange={e=>updTx(tx.id,'businessLine',e.target.value)} options={aux.BUSINESS_LINE||[]}/>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10,marginTop:8}}>
            <Sel label="Supplier" value={tx.supplier||''} onChange={e=>updTx(tx.id,'supplier',e.target.value)} options={aux.SUPPLIERS||[]}/>
            <Sel label="Cost Type" value={tx.costType||''} onChange={e=>updTx(tx.id,'costType',e.target.value)} options={aux.COST_TYPE||[]}/>
            <Inp label="Invoice Ref" value={tx.invoiceRef||''} onChange={e=>updTx(tx.id,'invoiceRef',e.target.value)}/>
          </div>
        </div>)}

        {/* SPLIT TRANSACTION — for bulk payments like CEVA */}
        {isOps&&!splitMode&&(<div style={{marginTop:8}}><button onClick={()=>{setSplitMode(true);setSplitLines([{clientCode:'',amount:'',costType:tx.costType||'',supplier:tx.supplier||''},{clientCode:'',amount:'',costType:tx.costType||'',supplier:tx.supplier||''}]);}} style={{padding:'6px 14px',borderRadius:6,border:`1px solid ${C.amber}50`,background:C.amber+'10',color:C.amber,fontSize:11,fontWeight:500,cursor:'pointer'}}>Split into multiple deals</button></div>)}

        {isOps&&splitMode&&<SplitPanel tx={tx} splitLines={splitLines} setSplitLines={setSplitLines} setSplitMode={setSplitMode} clients={clients} aux={aux} executeSplit={executeSplit}/>}

        {isGen&&(<div style={{marginTop:12,padding:12,background:C.purple+'08',borderRadius:8,border:`1px solid ${C.purple}20`}}>
          <div style={{fontSize:10,color:C.purple,textTransform:'uppercase',letterSpacing:'.04em',marginBottom:8}}>General Cost</div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10}}>
            <Sel label="Supplier" value={tx.supplier||''} onChange={e=>updTx(tx.id,'supplier',e.target.value)} options={aux.SUPPLIERS||[]}/>
            <Sel label="Cost Type" value={tx.costType||''} onChange={e=>updTx(tx.id,'costType',e.target.value)} options={aux.COST_TYPE||[]}/>
            <Sel label="Cost Line" value={tx.costLine||''} onChange={e=>updTx(tx.id,'costLine',e.target.value)} options={aux.COST_LINE||[]}/>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginTop:8}}>
            <Inp label="Invoice Ref" value={tx.invoiceRef||''} onChange={e=>updTx(tx.id,'invoiceRef',e.target.value)}/>
            <Sel label="Business Line" value={tx.businessLine||''} onChange={e=>updTx(tx.id,'businessLine',e.target.value)} options={aux.BUSINESS_LINE||[]}/>
          </div>
        </div>)}

        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10,marginTop:12}}>
          <div><label style={{fontSize:10,color:C.mt,display:'block',marginBottom:2}}>Has IVA</label><div style={{display:'flex',gap:4}}>{['Yes','No'].map(v=>(<button key={v} onClick={()=>updTx(tx.id,'hasIVA',v)} style={{flex:1,padding:'5px',borderRadius:5,fontSize:11,cursor:'pointer',border:`1px solid ${tx.hasIVA===v?C.ac:C.border}`,background:tx.hasIVA===v?C.ac+'15':'transparent',color:tx.hasIVA===v?C.ac:C.mt}}>{v}</button>))}</div></div>
          {tx.hasIVA==='Yes'&&<Inp label="IVA %" value={tx.ivaRate||21} onChange={e=>updTx(tx.id,'ivaRate',+e.target.value)} type="number"/>}
          <div><label style={{fontSize:10,color:C.mt,display:'block',marginBottom:2}}>Already paid?</label><div style={{display:'flex',gap:4}}>{['Yes','No'].map(v=>(<button key={v} onClick={()=>{updTx(tx.id,'pagado',v);if(v==='Yes')updTx(tx.id,'_expected',false);if(v==='No')updTx(tx.id,'_expected',true);}} style={{flex:1,padding:'5px',borderRadius:5,fontSize:11,cursor:'pointer',border:`1px solid ${tx.pagado===v?(v==='Yes'?C.green:C.amber):C.border}`,background:tx.pagado===v?(v==='Yes'?C.green+'15':C.amber+'15'):'transparent',color:tx.pagado===v?(v==='Yes'?C.green:C.amber):C.mt}}>{v}</button>))}</div></div>
        </div>

        {tx.pagado==='No'&&<div style={{marginTop:8,padding:10,background:C.amber+'08',borderRadius:6,border:`1px solid ${C.amber}25`,display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,alignItems:'end'}}>
          <Inp label="Expected Payment Date" type="date" value={tx.expectedDate||''} onChange={e=>updTx(tx.id,'expectedDate',e.target.value)}/>
          <div style={{fontSize:10,color:C.amber,paddingBottom:6}}>Will appear as "Expected" until paid</div>
        </div>}

        {isOps&&tx.clientCode&&!splitMode&&<div style={{display:'flex',gap:8,marginTop:14}}><button onClick={()=>{saveTxChanges();}} style={{padding:'8px 18px',borderRadius:8,border:'none',background:C.ac,color:'#fff',fontWeight:600,fontSize:12,cursor:'pointer'}}>Save Classification</button><button onClick={()=>{const saved={...selTx,pagado:'Yes'};setBankTxs(p=>p.map(t=>t.id===saved.id?saved:t));applyToDeal(saved);setSelTx(null);}} style={{padding:'8px 18px',borderRadius:8,border:'none',background:C.green,color:'#fff',fontWeight:600,fontSize:12,cursor:'pointer'}}>Save & Apply to Deal</button><button onClick={deleteTx} style={{marginLeft:'auto',padding:'8px 18px',borderRadius:8,border:`1px solid ${C.red}50`,background:C.red+'12',color:C.red,fontWeight:600,fontSize:12,cursor:'pointer'}}>Delete</button></div>}
        {(!isOps||!tx.clientCode)&&!splitMode&&<div style={{display:'flex',gap:8,marginTop:14}}><button onClick={saveTxChanges} style={{padding:'8px 18px',borderRadius:8,border:'none',background:C.ac,color:'#fff',fontWeight:600,fontSize:12,cursor:'pointer'}}>Save Classification</button><button onClick={deleteTx} style={{marginLeft:'auto',padding:'8px 18px',borderRadius:8,border:`1px solid ${C.red}50`,background:C.red+'12',color:C.red,fontWeight:600,fontSize:12,cursor:'pointer'}}>Delete</button></div>}
      </div>
    </div>);}

  // ── PASTE ──
  if(view==='paste'){
    const dupCount=preview?preview.filter(t=>t._dup).length:0;
    const importCount=preview?preview.filter(t=>!t._skip).length:0;

    const handleStatementFile=async(file)=>{
      try{
        const XLSX=await import('xlsx');
        const buf=await file.arrayBuffer();
        const wb=XLSX.read(buf,{type:'array',cellDates:true});
        const ws=wb.Sheets[wb.SheetNames[0]];
        const json=XLSX.utils.sheet_to_json(ws,{header:1,defval:'',raw:false});
        // Find header row (look for row with FECHA CONTABLE or at least 3 header-like columns)
        let headerRow=-1;
        for(let i=0;i<Math.min(10,json.length);i++){
          const row=json[i].map(c=>String(c||'').toUpperCase());
          if(row.some(c=>c==='FECHA CONTABLE'||c==='DATE')){headerRow=i;break;}
          // Also match if row has at least 3 common header words
          const headerWords=['FECHA','DESCRIPCI','IMPORTE','DEBE','HABER','SALDO','REFERENCIA','CATEGOR','CONCEPTO'];
          const matches=headerWords.filter(w=>row.some(c=>c.includes(w)));
          if(matches.length>=3){headerRow=i;break;}
        }
        if(headerRow<0){alert('No header row found');return;}
        const headers=json[headerRow].map(c=>String(c||'').toUpperCase().trim());
        const dateIdx=headers.findIndex(h=>h.includes('FECHA CONTABLE')||h.includes('FECHA')||h==='DATE');
        const descIdx=headers.findIndex(h=>h.includes('DESCRIPCI')||h.includes('CONCEPTO')||h==='DESCRIPTION');
        const amountIdx=headers.findIndex(h=>h==='IMPORTE'||h==='AMOUNT');
        const debeIdx=headers.findIndex(h=>h==='DEBE');
        const haberIdx=headers.findIndex(h=>h==='HABER');
        if(dateIdx<0){alert('No date column found');return;}
        const nw=[];
        for(let i=headerRow+1;i<json.length;i++){
          const row=json[i];if(!row||!row[dateIdx])continue;
          let date=null;const rawD=row[dateIdx];
          if(rawD instanceof Date&&!isNaN(rawD)){date=rawD.toISOString().slice(0,10);}
          else if(typeof rawD==='string'){
            const m=rawD.match(/(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{2,4})/);
            if(m){const yr=m[3].length===2?'20'+m[3]:m[3];date=yr+'-'+m[2].padStart(2,'0')+'-'+m[1].padStart(2,'0');}
            if(!date){const d=new Date(rawD);if(!isNaN(d))date=d.toISOString().slice(0,10);}
          }
          if(!date)continue;
          const desc=descIdx>=0?String(row[descIdx]||'').slice(0,120):'';
          let amount=0;
          const parseAmt=(v)=>{if(typeof v==='number')return v;const s=String(v||'').replace(/[€\s]/g,'').replace(',','.');return parseFloat(s)||0;};
          if(amountIdx>=0){amount=parseAmt(row[amountIdx]);}
          if(amount===0&&debeIdx>=0&&haberIdx>=0){const debe=parseAmt(row[debeIdx]);const haber=parseAmt(row[haberIdx]);amount=debe!==0?debe:haber;}
          if(amount===0)continue;
          const dt=new Date(date);const dc=dupCheck({date,amount,desc,invoiceRef:''});
          nw.push({id:Date.now()+Math.random(),date,month:MONTHS[dt.getMonth()]||'',year:dt.getFullYear(),desc,amount,classification:'',pnl:'SPAIN',bank:'BANKINTER',clientCode:'',supplier:'',costType:'',costLine:'',businessLine:'',hasIVA:'',ivaRate:'',invoiceRef:'',pagado:'Yes',_dupStatus:dc.status,_dupReason:dc.reason,_dupMatchDesc:dc.matchDesc||'',_dupMatchDate:dc.matchDate||'',_dupMatchAmt:dc.matchAmt,_dup:dc.status==='BLOCK',_skip:dc.status==='BLOCK'});
        }
        if(nw.length>0)setPreview(nw);
        else alert('No transactions found in file');
      }catch(err){console.error(err);alert('Error: '+err.message);}
    };

    return(<div>
    <button onClick={()=>{setView('list');setPreview(null);}} style={{padding:'5px 12px',borderRadius:6,border:`1px solid ${C.border}`,background:'transparent',color:C.mt,cursor:'pointer',fontSize:11,marginBottom:12}}>← Back</button>
    <div style={{fontSize:15,fontWeight:700,marginBottom:6}}>Import Bank Statement</div>
    <div style={{fontSize:11,color:C.mt,marginBottom:12}}>Upload a bank statement Excel or paste rows manually. Duplicates are detected automatically.</div>
    {!preview&&<>
      <div style={{display:'flex',gap:8,marginBottom:14}}>
        <button onClick={()=>{if(stmtFileRef.current)stmtFileRef.current.click();}} style={{padding:'10px 24px',borderRadius:8,border:'none',background:C.ac,color:'#fff',fontWeight:600,fontSize:13,cursor:'pointer'}}>Upload Excel (.xlsx)</button>
        <input ref={stmtFileRef} type="file" accept=".xlsx,.xls,.csv" style={{display:'none'}} onChange={e=>{const f=e.target.files&&e.target.files[0];if(f)handleStatementFile(f);e.target.value='';}}/>
        <span style={{fontSize:11,color:C.mt,alignSelf:'center'}}>Bankinter / bank statement format</span>
      </div>
      <div style={{fontSize:10,color:C.mt,marginBottom:6}}>Or paste rows manually (tab/semicolon separated):</div>
      <textarea value={paste} onChange={e=>setPaste(e.target.value)} placeholder={"19/03/2026\\tTrans inm/juan manuel\\t300.00\\n19/03/2026\\tTrans/ceva finished\\t-236.30"} style={{width:'100%',minHeight:120,padding:12,borderRadius:10,border:`1px solid ${C.border}`,background:C.card,color:C.tx,fontSize:12,fontFamily:'monospace',resize:'vertical',boxSizing:'border-box',outline:'none'}}/>
      <div style={{display:'flex',gap:8,marginTop:10,alignItems:'center'}}><button onClick={parsePaste} disabled={!paste.trim()} style={{padding:'8px 20px',borderRadius:8,border:'none',background:C.green,color:'#fff',fontWeight:600,fontSize:12,cursor:paste.trim()?'pointer':'default',opacity:paste.trim()?1:0.4}}>Parse Paste</button><span style={{fontSize:11,color:C.mt}}>{paste.trim().split('\\n').filter(l=>l.trim()).length} lines</span></div>
    </>}

    {/* Preview with duplicate detection */}
    {preview&&<div>
      <div style={{display:'flex',gap:10,marginBottom:12,alignItems:'center'}}>
        <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:8,padding:'8px 14px'}}><div style={{fontSize:9,color:C.mt,textTransform:'uppercase'}}>Total parsed</div><div style={{fontSize:15,fontWeight:700}}>{preview.length}</div></div>
        <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:8,padding:'8px 14px'}}><div style={{fontSize:9,color:C.mt,textTransform:'uppercase'}}>Duplicates</div><div style={{fontSize:15,fontWeight:700,color:dupCount>0?C.amber:C.green}}>{dupCount}</div></div>
        <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:8,padding:'8px 14px'}}><div style={{fontSize:9,color:C.mt,textTransform:'uppercase'}}>To import</div><div style={{fontSize:15,fontWeight:700,color:C.ac}}>{importCount}</div></div>
      </div>
      {(()=>{const blocks=preview?preview.filter(t=>t._dupStatus==='BLOCK').length:0;const warns=preview?preview.filter(t=>t._dupStatus==='WARN').length:0;if(!blocks&&!warns)return null;return(<div style={{padding:'8px 12px',background:C.amber+'10',border:`1px solid ${C.amber}30`,borderRadius:8,marginBottom:12,fontSize:11}}>{blocks>0&&<div style={{color:C.red,marginBottom:warns?4:0}}>{blocks} duplicate{blocks>1?'s':''} blocked (same amount + description + date)</div>}{warns>0&&<div style={{color:C.amber}}>{warns} similar transaction{warns>1?'s':''} found this month — review before importing</div>}</div>);})()}
      <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,overflow:'hidden',marginBottom:12}}>
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:11}}>
          <thead><tr style={{borderBottom:`1px solid ${C.border}`}}>{['','Date','Description','Amount','Status'].map(h=><th key={h} style={{padding:'6px 7px',textAlign:'left',color:C.mt,fontWeight:500,fontSize:9,textTransform:'uppercase'}}>{h}</th>)}</tr></thead>
          <tbody>{preview.map((t,i)=>(<tr key={i} style={{borderBottom:`1px solid ${C.border}08`,opacity:t._skip?0.4:1,background:t._dupStatus==='BLOCK'?C.red+'08':t._dupStatus==='WARN'?C.amber+'06':'transparent'}}>
            <td style={{padding:'4px 7px',width:30}}><input type="checkbox" checked={!t._skip} onChange={()=>toggleSkip(t.id)} style={{accentColor:C.ac}}/></td>
            <td style={{padding:'4px 7px',fontSize:10,color:C.mt,whiteSpace:'nowrap'}}>{t.date}</td>
            <td style={{padding:'4px 7px',maxWidth:220,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{t.desc}</td>
            <td style={{padding:'4px 7px',textAlign:'right',fontWeight:600,color:t.amount>=0?C.green:C.red}}>{fmtD(t.amount)}</td>
            <td style={{padding:'4px 7px'}}>{t._dupStatus==='BLOCK'?<div><Badge t={t._skip?'Blocked':'Importing'} c={t._skip?C.red:C.amber}/><div style={{fontSize:8,color:C.mt,marginTop:2}}>{t._dupReason}</div><div style={{fontSize:8,color:'#999',marginTop:1}}>Match: {t._dupMatchDate} {t._dupMatchDesc} {fmtD(t._dupMatchAmt)}</div></div>:t._dupStatus==='WARN'?<div><Badge t="Similar" c={C.amber}/><div style={{fontSize:8,color:C.mt,marginTop:2}}>{t._dupReason}</div><div style={{fontSize:8,color:'#999',marginTop:1}}>Match: {t._dupMatchDate} {t._dupMatchDesc}</div></div>:<Badge t="New" c={C.green}/>}</td>
          </tr>))}</tbody>
        </table>
      </div>
      <div style={{display:'flex',gap:8}}>
        <button onClick={confirmImport} disabled={importCount===0} style={{padding:'8px 20px',borderRadius:8,border:'none',background:importCount>0?C.green:C.border,color:importCount>0?'#fff':C.mt,fontWeight:600,fontSize:13,cursor:importCount>0?'pointer':'default'}}>Import {importCount} Transaction{importCount!==1?'s':''}</button>
        <button onClick={()=>setPreview(null)} style={{padding:'8px 16px',borderRadius:8,border:`1px solid ${C.border}`,background:'transparent',color:C.mt,fontSize:12,cursor:'pointer'}}>Back to paste</button>
      </div>
    </div>}
  </div>);}

  // ── SINGLE TRANSACTION ──
  if(view==='single'){
    const sf=singleTx;
    const su=(k,v)=>setSingleTx(p=>({...p,[k]:v}));
    const isOps=sf.classification==='Operations';
    const isGen=sf.classification==='General';

    const defaultSingle={
      date:new Date().toISOString().slice(0,10),
      desc:'',
      amount:'',
      flowType:'Outflow',
      classification:'Operations',
      pnl:'SPAIN',
      bank:'BANKINTER',
      clientCode:'',
      supplier:'',
      costType:'',
      costLine:'',
      businessLine:'',
      hasIVA:'',
      ivaRate:21,
      invoiceRef:'',
      pagado:'',
      expectedDate:'',
      fromTemplate:''
    };

    const loadTemplate=(tpl)=>{
      setSingleTx({
        ...defaultSingle,
        desc:tpl.desc,
        amount:tpl.amount,
        classification:tpl.classification,
        pnl:tpl.pnl||'SPAIN',
        bank:tpl.bank||'BANKINTER',
        clientCode:tpl.clientCode||'',
        supplier:tpl.supplier||'',
        costType:tpl.costType||'',
        costLine:tpl.costLine||'',
        businessLine:tpl.businessLine||'',
        hasIVA:tpl.hasIVA||'',
        ivaRate:tpl.ivaRate||21,
        fromTemplate:tpl.desc
      });
    };

    const addSingle=()=>{
      let amt=+sf.amount;if(sf.flowType==='Outflow'&&amt>0)amt=-amt;if(sf.flowType==='Inflow'&&amt<0)amt=-amt;
      const classification=sf.flowType==='Inflow'?'Operations':sf.classification;
      if(!sf.desc || !amt || !sf.classification || !sf.pagado) return;
      if(sf.pagado==='No' && !sf.expectedDate) return;

      const dup=isDup(sf.date,amt,sf.desc);
      if(dup && !confirm('A similar transaction already exists ('+dup.date+' '+fmtD(dup.amount)+'). Import anyway?')) return;

      const dt=new Date(sf.date);
      const tx={
        id:Date.now()+Math.random(),
        date:sf.date,
        month:MONTHS[dt.getMonth()]||'',
        year:dt.getFullYear(),
        desc:sf.desc.slice(0,120),
        amount:amt,
        classification:sf.classification,
        pnl:sf.pnl,
        bank:sf.bank,
        clientCode:sf.clientCode,
        supplier:sf.supplier,
        costType:sf.costType,
        costLine:sf.costLine,
        businessLine:sf.businessLine,
        hasIVA:sf.hasIVA,
        ivaRate:sf.ivaRate,
        invoiceRef:sf.invoiceRef,
        pagado:sf.pagado,
        expectedDate:sf.pagado==='No' ? sf.expectedDate : '',
        _expected:sf.pagado==='No'
      };

      setBankTxs(p=>[tx,...p]);

      if(isOps && sf.clientCode && sf.pagado==='Yes'){
        applyToDeal(tx);
      }

      setSingleTx({...defaultSingle});
      setIsMulti(false);
      setSplitLines([{clientCode:'',amount:'',costType:'',supplier:''}]);
      setView('list');
    };

    const matchedDeal=sf.clientCode ? clients.find(c=>c.c===sf.clientCode) : null;

    const singleDate = sf.date || new Date().toISOString().slice(0,10);
    const singleDateObj = new Date(singleDate);

    const multiTx = {
      id:Date.now()+Math.random(),
      date:singleDate,
      month:MONTHS[singleDateObj.getMonth()]||'',
      year:singleDateObj.getFullYear(),
      desc:(sf.desc||'').slice(0,120),
      amount:+sf.amount || 0,
      classification:'Operations',
      pnl:sf.pnl||'SPAIN',
      bank:sf.bank||'BANKINTER',
      clientCode:'',
      supplier:sf.supplier||'',
      costType:sf.costType||'',
      costLine:'',
      businessLine:sf.businessLine||'',
      hasIVA:sf.hasIVA||'',
      ivaRate:sf.ivaRate||21,
      invoiceRef:sf.invoiceRef||'',
      pagado:sf.pagado||'No',
      expectedDate:sf.expectedDate||'',
      _expected:sf.pagado==='No'
    };

    const canUseMulti =
      !!sf.desc &&
      !!(+sf.amount) &&
      sf.classification==='Operations';

    return(
      <div>
        <button
          onClick={()=>{
            setView('list');
            setIsMulti(false);
            setSplitLines([{clientCode:'',amount:'',costType:'',supplier:''}]);
          }}
          style={{
            padding:'5px 12px',
            borderRadius:6,
            border:`1px solid ${C.border}`,
            background:'transparent',
            color:C.mt,
            cursor:'pointer',
            fontSize:11,
            marginBottom:12
          }}
        >
          ← Back
        </button>

        <div style={{fontSize:15,fontWeight:700,marginBottom:14}}>New Transaction</div>

        <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:16,maxWidth:760}}>
          {templates.length>0 && (
            <div style={{marginBottom:14}}>
              <label style={{fontSize:10,color:C.mt,display:'block',marginBottom:4}}>
                Start from template (optional)
              </label>
              <div style={{display:'flex',gap:4,flexWrap:'wrap'}}>
                <button
                  onClick={()=>{
                    setSingleTx({...defaultSingle});
                    setIsMulti(false);
                    setSplitLines([{clientCode:'',amount:'',costType:'',supplier:''}]);
                  }}
                  style={{
                    padding:'5px 12px',
                    borderRadius:6,
                    fontSize:10,
                    cursor:'pointer',
                    border:`1px solid ${!sf.fromTemplate?C.ac:C.border}`,
                    background:!sf.fromTemplate?C.ac+'15':'transparent',
                    color:!sf.fromTemplate?C.ac:C.mt,
                    fontWeight:!sf.fromTemplate?600:400
                  }}
                >
                  From scratch
                </button>

                {templates.map(tpl=>(
                  <button
                    key={tpl.id}
                    onClick={()=>loadTemplate(tpl)}
                    style={{
                      padding:'5px 12px',
                      borderRadius:6,
                      fontSize:10,
                      cursor:'pointer',
                      border:`1px solid ${sf.fromTemplate===tpl.desc?C.purple:C.border}`,
                      background:sf.fromTemplate===tpl.desc?C.purple+'15':'transparent',
                      color:sf.fromTemplate===tpl.desc?C.purple:C.mt,
                      fontWeight:sf.fromTemplate===tpl.desc?600:400
                    }}
                  >
                    {tpl.desc}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div style={{display:'flex',gap:4,marginBottom:12}}>
            {['Outflow','Inflow'].map(v=>{const active=sf.flowType===v;return(
              <button key={v} onClick={()=>su('flowType',v)} style={{flex:1,padding:'8px',borderRadius:6,fontSize:12,fontWeight:active?700:400,cursor:'pointer',border:`1px solid ${active?(v==='Inflow'?C.green:C.red):C.border}`,background:active?(v==='Inflow'?C.green+'15':C.red+'15'):'transparent',color:active?(v==='Inflow'?C.green:C.red):C.mt}}>{v==='Outflow'?'↓ Outflow (payment)':'↑ Inflow (receipt)'}</button>
            );})}
          </div>

          <div style={{display:'grid',gridTemplateColumns:'130px 1fr 120px',gap:10}}>
            <Inp label="Date" type="date" value={sf.date} onChange={e=>su('date',e.target.value)}/>
            <Inp label="Description *" value={sf.desc} onChange={e=>su('desc',e.target.value)} placeholder={sf.flowType==='Inflow'?"e.g. Client payment — Roberto":"e.g. Salary — Nacho Rodriguez"}/>
            <Inp label="Amount (€) *" type="number" value={sf.amount} onChange={e=>su('amount',e.target.value)} placeholder={sf.flowType==='Inflow'?"3000":"236.30"}/>
          </div>

          {sf.flowType!=='Inflow'&&<div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10}}>
            <div>
              <label style={{fontSize:10,color:C.mt,display:'block',marginBottom:2}}>Classification *</label>
              <div style={{display:'flex',gap:4}}>
                {['Operations','General','Intracompany'].map(v=>(
                  <button key={v} onClick={()=>{su('classification',v);if(v!=='Operations')setIsMulti(false);}}
                    style={{flex:1,padding:'6px',borderRadius:6,fontSize:11,fontWeight:sf.classification===v?600:400,cursor:'pointer',
                      border:`1px solid ${sf.classification===v?(v==='Operations'?C.ac:v==='Intracompany'?C.cyan:C.purple):C.border}`,
                      background:sf.classification===v?(v==='Operations'?C.ac+'15':v==='Intracompany'?C.cyan+'15':C.purple+'15'):'transparent',
                      color:sf.classification===v?(v==='Operations'?C.ac:v==='Intracompany'?C.cyan:C.purple):C.mt}}>{v}</button>
                ))}
              </div>
            </div>
            <Sel label="PnL" value={sf.pnl} onChange={e=>su('pnl',e.target.value)} options={aux.PNL||[]} placeholder={false}/>
            <Sel label="Bank" value={sf.bank} onChange={e=>su('bank',e.target.value)} options={aux.BANKS||[]} placeholder={false}/>
          </div>}
          {sf.flowType==='Inflow'&&<div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10,marginTop:4}}>
            <Inp label="Client ID *" value={sf.clientCode} onChange={e=>su('clientCode',e.target.value)} placeholder="Type client ID..."/>
            <Sel label="Bank" value={sf.bank} onChange={e=>su('bank',e.target.value)} options={aux.BANKS||[]} placeholder={false}/>
            <Inp label="Invoice Ref" value={sf.invoiceRef} onChange={e=>su('invoiceRef',e.target.value)}/>
          </div>}

          {isOps && sf.flowType!=='Inflow' && (
            <>
              <div style={{marginTop:12}}>
                <label style={{fontSize:10,color:C.mt,display:'block',marginBottom:4}}>Entry mode</label>
                <div style={{display:'flex',gap:4}}>
                  {['Single','Multiple'].map(v=>{
                    const active = isMulti === (v==='Multiple');
                    return (
                      <button
                        key={v}
                        onClick={()=>{
                          const nextIsMulti = v==='Multiple';
                          setIsMulti(nextIsMulti);

                          if(nextIsMulti && splitLines.length<2){
                            setSplitLines([
                              {clientCode:'',amount:'',costType:sf.costType||'',supplier:sf.supplier||''},
                              {clientCode:'',amount:'',costType:sf.costType||'',supplier:sf.supplier||''}
                            ]);
                          }
                        }}
                        style={{
                          padding:'6px 12px',
                          borderRadius:6,
                          fontSize:11,
                          cursor:'pointer',
                          border:`1px solid ${active ? C.ac : C.border}`,
                          background:active ? C.ac+'15' : 'transparent',
                          color:active ? C.ac : C.mt,
                          fontWeight:active ? 600 : 400
                        }}
                      >
                        {v}
                      </button>
                    );
                  })}
                </div>
              </div>

              {!isMulti && (
                <div style={{marginTop:10,padding:12,background:C.ac+'08',borderRadius:8,border:`1px solid ${C.ac}20`}}>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                    <div>
                      <label style={{fontSize:10,color:C.mt,display:'block',marginBottom:2}}>Client ID *</label>
                      <input
                        value={sf.clientCode}
                        onChange={e=>su('clientCode',e.target.value)}
                        placeholder="Type or select..."
                        list="single-cl"
                        style={{
                          width:'100%',
                          padding:'6px 8px',
                          borderRadius:6,
                          border:`1px solid ${C.border}`,
                          background:C.bg,
                          color:C.tx,
                          fontSize:12,
                          boxSizing:'border-box',
                          outline:'none'
                        }}
                      />
                      <datalist id="single-cl">
                        {clients.map(c=><option key={c.c} value={c.c}>{c.n} (#{c.i})</option>)}
                      </datalist>
                      {matchedDeal && <div style={{fontSize:9,color:C.green,marginTop:3}}>#{matchedDeal.i} — {matchedDeal.n}</div>}
                      {sf.clientCode && !matchedDeal && <div style={{fontSize:9,color:C.amber,marginTop:3}}>No match</div>}
                    </div>

                    <Sel label="Business Line" value={sf.businessLine} onChange={e=>su('businessLine',e.target.value)} options={aux.BUSINESS_LINE||[]}/>
                  </div>

                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10,marginTop:8}}>
                    <Sel label="Supplier" value={sf.supplier} onChange={e=>su('supplier',e.target.value)} options={aux.SUPPLIERS||[]}/>
                    <Sel label="Cost Type" value={sf.costType} onChange={e=>su('costType',e.target.value)} options={aux.COST_TYPE||[]}/>
                    <Inp label="Invoice Ref" value={sf.invoiceRef} onChange={e=>su('invoiceRef',e.target.value)}/>
                  </div>
                </div>
              )}

              {isMulti && (
                <div style={{marginTop:10,padding:12,background:C.ac+'08',borderRadius:8,border:`1px solid ${C.ac}20`}}>
                  <div style={{fontSize:10,color:C.ac,textTransform:'uppercase',letterSpacing:'.04em',marginBottom:8}}>
                    Multiple deals
                  </div>
                  <div style={{fontSize:11,color:C.mt,marginBottom:10}}>
                    Fill the base transaction and split it across multiple client IDs.
                  </div>

                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10,marginBottom:10}}>
                    <Sel label="Supplier" value={sf.supplier} onChange={e=>su('supplier',e.target.value)} options={aux.SUPPLIERS||[]}/>
                    <Sel label="Cost Type" value={sf.costType} onChange={e=>su('costType',e.target.value)} options={aux.COST_TYPE||[]}/>
                    <Inp label="Invoice Ref" value={sf.invoiceRef} onChange={e=>su('invoiceRef',e.target.value)}/>
                  </div>

                  <div style={{marginBottom:10}}>
                    <Sel label="Business Line" value={sf.businessLine} onChange={e=>su('businessLine',e.target.value)} options={aux.BUSINESS_LINE||[]}/>
                  </div>

                  {!canUseMulti ? (
                    <div style={{padding:'10px 12px',background:C.amber+'10',border:`1px solid ${C.amber}30`,borderRadius:8,fontSize:11,color:C.amber}}>
                      To use multiple mode, fill at least: Description, Amount and keep Classification = Operations.
                    </div>
                  ) : (
                    <SplitPanel
                      tx={multiTx}
                      splitLines={splitLines}
                      setSplitLines={setSplitLines}
                      setSplitMode={setSplitMode}
                      clients={clients}
                      aux={aux}
                      executeSplit={(tx)=>{
                        executeSplit(tx);
                        setSingleTx({...defaultSingle});
                        setIsMulti(false);
                        setSplitLines([{clientCode:'',amount:'',costType:'',supplier:''}]);
                        setView('list');
                      }}
                    />
                  )}
                </div>
              )}
            </>
          )}

          {(isGen || sf.classification==='Intracompany') && sf.flowType!=='Inflow' && (
            <div style={{marginTop:10,padding:12,background:sf.classification==='Intracompany'?C.cyan+'08':C.purple+'08',borderRadius:8,border:`1px solid ${sf.classification==='Intracompany'?C.cyan+'20':C.purple+'20'}`}}>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10}}>
                <Sel label="Supplier" value={sf.supplier} onChange={e=>su('supplier',e.target.value)} options={aux.SUPPLIERS||[]}/>
                <Sel label="Cost Type" value={sf.costType} onChange={e=>su('costType',e.target.value)} options={aux.COST_TYPE||[]}/>
                <Sel label="Cost Line" value={sf.costLine} onChange={e=>su('costLine',e.target.value)} options={aux.COST_LINE||[]}/>
              </div>

              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginTop:8}}>
                <Inp label="Invoice Ref" value={sf.invoiceRef} onChange={e=>su('invoiceRef',e.target.value)}/>
                <Sel label="Business Line" value={sf.businessLine} onChange={e=>su('businessLine',e.target.value)} options={aux.BUSINESS_LINE||[]}/>
              </div>
            </div>
          )}

          {sf.flowType!=='Inflow' && (
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10,marginTop:12}}>
            <div>
              <label style={{fontSize:10,color:C.mt,display:'block',marginBottom:2}}>Has IVA</label>
              <div style={{display:'flex',gap:4}}>
                {['Yes','No'].map(v=>(
                  <button
                    key={v}
                    onClick={()=>su('hasIVA',v)}
                    style={{
                      flex:1,
                      padding:'5px',
                      borderRadius:5,
                      fontSize:11,
                      cursor:'pointer',
                      border:`1px solid ${sf.hasIVA===v?C.ac:C.border}`,
                      background:sf.hasIVA===v?C.ac+'15':'transparent',
                      color:sf.hasIVA===v?C.ac:C.mt
                    }}
                  >
                    {v}
                  </button>
                ))}
              </div>
            </div>

            {sf.hasIVA==='Yes' && <Inp label="IVA %" value={sf.ivaRate} onChange={e=>su('ivaRate',+e.target.value)} type="number"/>}

            <div>
              <label style={{fontSize:10,color:C.mt,display:'block',marginBottom:2}}>Already paid? *</label>
              <div style={{display:'flex',gap:4}}>
                {['Yes','No'].map(v=>(
                  <button
                    key={v}
                    onClick={()=>su('pagado',v)}
                    style={{
                      flex:1,
                      padding:'5px',
                      borderRadius:5,
                      fontSize:11,
                      cursor:'pointer',
                      border:`1px solid ${sf.pagado===v?(v==='Yes'?C.green:C.amber):C.border}`,
                      background:sf.pagado===v?(v==='Yes'?C.green+'15':C.amber+'15'):'transparent',
                      color:sf.pagado===v?(v==='Yes'?C.green:C.amber):C.mt
                    }}
                  >
                    {v}
                  </button>
                ))}
              </div>
            </div>
          </div>
          )}

          {sf.flowType==='Inflow'&&<div style={{marginTop:10,display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10}}>
            <div><label style={{fontSize:10,color:C.mt,display:'block',marginBottom:2}}>Has IVA</label><div style={{display:'flex',gap:4}}>{['Yes','No'].map(v=>(<button key={v} onClick={()=>su('hasIVA',v)} style={{flex:1,padding:'5px',borderRadius:5,fontSize:11,cursor:'pointer',border:`1px solid ${sf.hasIVA===v?C.ac:C.border}`,background:sf.hasIVA===v?C.ac+'15':'transparent',color:sf.hasIVA===v?C.ac:C.mt}}>{v}</button>))}</div></div>
            {sf.hasIVA==='Yes'&&<Inp label="IVA %" value={sf.ivaRate} onChange={e=>su('ivaRate',+e.target.value)} type="number"/>}
            <div><label style={{fontSize:10,color:C.mt,display:'block',marginBottom:2}}>Already paid? *</label><div style={{display:'flex',gap:4}}>{['Yes','No'].map(v=>(<button key={v} onClick={()=>su('pagado',v)} style={{flex:1,padding:'5px',borderRadius:5,fontSize:11,cursor:'pointer',border:`1px solid ${sf.pagado===v?(v==='Yes'?C.green:C.amber):C.border}`,background:sf.pagado===v?(v==='Yes'?C.green+'15':C.amber+'15'):'transparent',color:sf.pagado===v?(v==='Yes'?C.green:C.amber):C.mt}}>{v}</button>))}</div></div>
          </div>}

          {sf.pagado==='No' && (
            <div style={{marginTop:10,padding:12,background:C.amber+'08',borderRadius:8,border:`1px solid ${C.amber}25`}}>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,alignItems:'end'}}>
                <Inp label="Expected Payment Date *" type="date" value={sf.expectedDate} onChange={e=>su('expectedDate',e.target.value)}/>
                <div style={{fontSize:11,color:C.amber,paddingBottom:6}}>
                  This invoice will appear as "Expected" until marked as paid
                </div>
              </div>
            </div>
          )}

          {!isMulti && (
            <button
              onClick={addSingle}
              disabled={
                !sf.desc ||
                !+sf.amount ||
                !sf.classification ||
                !sf.pagado ||
                (sf.pagado==='No' && !sf.expectedDate)
              }
              style={{
                marginTop:14,
                padding:'8px 22px',
                borderRadius:8,
                border:'none',
                background:
                  !sf.desc || !+sf.amount || !sf.pagado
                    ? C.border
                    : sf.pagado==='Yes'
                      ? C.green
                      : C.amber,
                color:!sf.desc || !+sf.amount || !sf.pagado ? C.mt : '#fff',
                fontWeight:600,
                fontSize:13,
                cursor:!sf.desc || !+sf.amount || !sf.pagado ? 'default' : 'pointer'
              }}
            >
              {sf.pagado==='No' ? 'Add as Expected' : 'Add'}
              {isOps && sf.clientCode && sf.pagado==='Yes' ? ' & Apply to Deal' : ''} Transaction
            </button>
          )}
        </div>
      </div>
    );
  }

  // ── EXCEL COLUMN MAPPING ──
  if(view==='excel'&&xlData){
    const{headers,rows,colMap}=xlData;
    const setCol=(k,v)=>setXlData(p=>({...p,colMap:{...p.colMap,[k]:v}}));
    const canProcess=colMap.date&&colMap.amount&&headers.includes(colMap.date)&&headers.includes(colMap.amount);
    return(<div>
      <button onClick={()=>{setView('list');setXlData(null);}} style={{padding:'5px 12px',borderRadius:6,border:`1px solid ${C.border}`,background:'transparent',color:C.mt,cursor:'pointer',fontSize:11,marginBottom:12}}>← Back</button>
      <div style={{fontSize:15,fontWeight:700,marginBottom:6}}>Import Excel / CSV</div>
      <div style={{fontSize:11,color:C.mt,marginBottom:14}}>Map columns from your file to Date, Description, and Amount. {rows.length} rows found.</div>
      <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:16,marginBottom:14}}>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10,marginBottom:14}}>
          <Sel label="Date Column *" value={colMap.date} onChange={e=>setCol('date',e.target.value)} options={headers} placeholder="Select..."/>
          <Sel label="Description Column" value={colMap.desc} onChange={e=>setCol('desc',e.target.value)} options={headers} placeholder="(optional)"/>
          <Sel label="Amount Column *" value={colMap.amount} onChange={e=>setCol('amount',e.target.value)} options={headers} placeholder="Select..."/>
        </div>
        <div style={{fontSize:10,color:C.mt,marginBottom:10}}>Preview (first 5 rows):</div>
        <div style={{overflowX:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:10,minWidth:400}}>
            <thead><tr style={{borderBottom:`1px solid ${C.border}`}}>{headers.map(h=><th key={h} style={{padding:'4px 6px',textAlign:'left',color:h===colMap.date||h===colMap.desc||h===colMap.amount?C.ac:C.mt,fontWeight:h===colMap.date||h===colMap.desc||h===colMap.amount?700:400,fontSize:9,whiteSpace:'nowrap'}}>{h}{h===colMap.date?' (DATE)':h===colMap.desc?' (DESC)':h===colMap.amount?' (AMT)':''}</th>)}</tr></thead>
            <tbody>{rows.slice(0,5).map((r,i)=><tr key={i} style={{borderBottom:`1px solid ${C.border}08`}}>{headers.map((h,j)=><td key={j} style={{padding:'3px 6px',color:C.tx,fontSize:10,whiteSpace:'nowrap',maxWidth:120,overflow:'hidden',textOverflow:'ellipsis'}}>{r[j] instanceof Date?r[j].toISOString().slice(0,10):String(r[j]??'')}</td>)}</tr>)}</tbody>
          </table>
        </div>
        <button onClick={processExcelImport} disabled={!canProcess} style={{marginTop:14,padding:'8px 20px',borderRadius:8,border:'none',background:canProcess?C.green:C.border,color:canProcess?'#fff':C.mt,fontWeight:600,fontSize:13,cursor:canProcess?'pointer':'default'}}>Process & Detect Duplicates</button>
      </div>
    </div>);
  }

  // ── COPY FROM MONTH ──
  if(view==='copy'){
    const srcAll=copyMonth&&copyYear?bankTxs.filter(t=>t.month===copyMonth&&t.year===+copyYear):[];
    const srcTxs=copyFilter==='general'?srcAll.filter(t=>t.classification==='General'):srcAll;
    const included=srcTxs.filter(t=>!copyExclude.includes(t.id));
    const toggleExclude=(id)=>setCopyExclude(prev=>prev.includes(id)?prev.filter(x=>x!==id):[...prev,id]);
    const doCopyFiltered=()=>{
      const now=new Date();const curMonth=MONTHS[now.getMonth()];const curYear=now.getFullYear();
      const copies=included.map(t=>({...t,id:Date.now()+Math.random(),date:now.toISOString().slice(0,10),month:curMonth,year:curYear,pagado:'No',_expected:true,invoiceRef:''}));
      const existing=bankTxs.filter(e=>e.month===curMonth&&e.year===curYear);
      const toAdd=copies.filter(c=>!existing.find(e=>e.desc===c.desc&&e.classification===c.classification&&e.supplier===c.supplier));
      if(toAdd.length>0)setBankTxs(p=>[...toAdd,...p]);
      setView('list');setCopyExclude([]);
    };
    return(<div>
      <button onClick={()=>{setView('list');setCopyExclude([]);}} style={{padding:'5px 12px',borderRadius:6,border:`1px solid ${C.border}`,background:'transparent',color:C.mt,cursor:'pointer',fontSize:11,marginBottom:12}}>← Back</button>
      <div style={{fontSize:15,fontWeight:700,marginBottom:6}}>Copy from Previous Month</div>
      <div style={{fontSize:11,color:C.mt,marginBottom:14}}>Select transactions to copy as "Expected". Uncheck ones you don't want.</div>
      <div style={{display:'flex',gap:10,alignItems:'end',marginBottom:14}}>
        <Sel label="Source Month" value={copyMonth?`${copyMonth}|${copyYear}`:''} onChange={e=>{if(e.target.value){const[m,y]=e.target.value.split('|');setCopyMonth(m);setCopyYear(y);setCopyExclude([]);}else{setCopyMonth('');setCopyYear('');}}} options={availableMonths.map(m=>`${m.month}|${m.year}`)} placeholder="Select month..."/>
        <div style={{display:'flex',gap:4}}>
          {['all','general'].map(v=>(<button key={v} onClick={()=>setCopyFilter(v)} style={{padding:'5px 12px',borderRadius:5,fontSize:10,cursor:'pointer',border:`1px solid ${copyFilter===v?C.ac:C.border}`,background:copyFilter===v?C.ac+'15':'transparent',color:copyFilter===v?C.ac:C.mt,fontWeight:copyFilter===v?600:400}}>{v==='all'?'All':'General only'}</button>))}
        </div>
        {srcTxs.length>0&&<div style={{fontSize:11,color:C.tx}}>{included.length} of {srcTxs.length} selected</div>}
      </div>
      {srcTxs.length>0&&<>
        <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,overflow:'hidden',marginBottom:12,maxHeight:'50vh',overflowY:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:11}}>
            <thead><tr style={{borderBottom:`1px solid ${C.border}`}}>{['','Description','Amount','Type','Supplier','Cost Type'].map(h=><th key={h} style={{padding:'6px 7px',textAlign:'left',color:C.mt,fontWeight:500,fontSize:9,textTransform:'uppercase',position:'sticky',top:0,background:C.card}}>{h}</th>)}</tr></thead>
            <tbody>{srcTxs.map((t,i)=>{const excluded=copyExclude.includes(t.id);return(<tr key={i} style={{borderBottom:`1px solid ${C.border}08`,opacity:excluded?0.35:1}}>
              <td style={{padding:'4px 7px',width:30}}><input type="checkbox" checked={!excluded} onChange={()=>toggleExclude(t.id)} style={{accentColor:C.ac}}/></td>
              <td style={{padding:'4px 7px',maxWidth:200,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{t.desc}</td>
              <td style={{padding:'4px 7px',textAlign:'right',fontWeight:600,color:t.amount>=0?C.green:C.red}}>{fmtD(t.amount)}</td>
              <td style={{padding:'4px 7px'}}><Badge t={t.classification||'—'} c={t.classification==='Operations'?C.ac:C.purple}/></td>
              <td style={{padding:'4px 7px',fontSize:10,color:C.mt}}>{t.supplier||'—'}</td>
              <td style={{padding:'4px 7px',fontSize:10,color:C.mt}}>{t.costType||t.costLine||'—'}</td>
            </tr>);})}</tbody>
          </table>
        </div>
        <button onClick={doCopyFiltered} disabled={included.length===0} style={{padding:'8px 20px',borderRadius:8,border:'none',background:included.length>0?C.green:C.border,color:included.length>0?'#fff':C.mt,fontWeight:600,fontSize:13,cursor:included.length>0?'pointer':'default'}}>Copy {included.length} Transactions to Current Month</button>
      </>}
    </div>);
  }

  // ── TEMPLATES ──
  if(view==='templates'){
    const emptyTpl={desc:'',amount:0,classification:'General',pnl:'SPAIN',bank:'BANKINTER',clientCode:'',supplier:'',costType:'',costLine:'',businessLine:'',hasIVA:'No',ivaRate:21,invoiceRef:'',_isTemplate:true};
    const editing=tplForm!==null;
    const tf=tplForm||emptyTpl;const tu=(k,v)=>setTplForm(p=>({...(p||emptyTpl),[k]:v}));
    const saveTpl=()=>{if(!tf.desc)return;if(tf.id){setTemplates(p=>p.map(t=>t.id===tf.id?tf:t));}else{setTemplates(p=>[...p,{...tf,id:Date.now()+Math.random()}]);}setTplForm(null);};
    const delTpl=(id)=>setTemplates(p=>p.filter(t=>t.id!==id));
    const isOps=tf.classification==='Operations';const isGen=tf.classification==='General';

    return(<div>
      <button onClick={()=>{setView('list');setTplForm(null);}} style={{padding:'5px 12px',borderRadius:6,border:`1px solid ${C.border}`,background:'transparent',color:C.mt,cursor:'pointer',fontSize:11,marginBottom:12}}>← Back</button>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14}}>
        <div><div style={{fontSize:15,fontWeight:700}}>Transaction Templates</div><div style={{fontSize:11,color:C.mt,marginTop:2}}>Create reusable templates for recurring transactions (salaries, suppliers, rent...)</div></div>
        {!editing&&<button onClick={()=>setTplForm({...emptyTpl})} style={{padding:'6px 14px',borderRadius:6,border:'none',background:C.ac,color:'#fff',fontWeight:600,fontSize:11,cursor:'pointer'}}>+ New Template</button>}
      </div>

      {/* Template form */}
      {editing&&<div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:16,marginBottom:14}}>
        <div style={{fontSize:12,fontWeight:600,marginBottom:10}}>{tf.id?'Edit':'New'} Template</div>
        <div style={{display:'grid',gridTemplateColumns:'2fr 1fr 1fr',gap:10}}>
          <Inp label="Description *" value={tf.desc} onChange={e=>tu('desc',e.target.value)} placeholder="e.g. Salary — Nacho Rodriguez"/>
          <Inp label="Default Amount (€)" type="number" value={tf.amount} onChange={e=>tu('amount',+e.target.value)}/>
          <div><label style={{fontSize:10,color:C.mt,display:'block',marginBottom:2}}>Type</label><div style={{display:'flex',gap:4}}>{['Operations','General'].map(v=>(<button key={v} onClick={()=>tu('classification',v)} style={{flex:1,padding:'5px',borderRadius:5,fontSize:11,cursor:'pointer',border:`1px solid ${tf.classification===v?(v==='Operations'?C.ac:C.purple):C.border}`,background:tf.classification===v?(v==='Operations'?C.ac+'15':C.purple+'15'):'transparent',color:tf.classification===v?(v==='Operations'?C.ac:C.purple):C.mt}}>{v}</button>))}</div></div>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr 1fr',gap:10,marginTop:8}}>
          <Sel label="Supplier" value={tf.supplier} onChange={e=>tu('supplier',e.target.value)} options={aux.SUPPLIERS||[]}/>
          <Sel label="Cost Type" value={tf.costType} onChange={e=>tu('costType',e.target.value)} options={aux.COST_TYPE||[]}/>
          {isGen&&<Sel label="Cost Line" value={tf.costLine} onChange={e=>tu('costLine',e.target.value)} options={aux.COST_LINE||[]}/>}
          {isOps&&<div><label style={{fontSize:10,color:C.mt,display:'block',marginBottom:2}}>Client ID</label><input value={tf.clientCode||''} onChange={e=>tu('clientCode',e.target.value)} list="tpl-cl" style={{width:'100%',padding:'6px 8px',borderRadius:6,border:`1px solid ${C.border}`,background:C.bg,color:C.tx,fontSize:12,boxSizing:'border-box',outline:'none'}}/><datalist id="tpl-cl">{clients.map(c=><option key={c.c} value={c.c}>{c.n}</option>)}</datalist></div>}
          <Sel label="Business Line" value={tf.businessLine} onChange={e=>tu('businessLine',e.target.value)} options={aux.BUSINESS_LINE||[]}/>
        </div>
        <div style={{display:'flex',gap:6,marginTop:12}}>
          <button onClick={saveTpl} disabled={!tf.desc} style={{padding:'7px 18px',borderRadius:7,border:'none',background:tf.desc?C.green:C.border,color:tf.desc?'#fff':C.mt,fontWeight:600,fontSize:12,cursor:tf.desc?'pointer':'default'}}>Save Template</button>
          <button onClick={()=>setTplForm(null)} style={{padding:'7px 14px',borderRadius:7,border:`1px solid ${C.border}`,background:'transparent',color:C.mt,fontSize:12,cursor:'pointer'}}>Cancel</button>
        </div>
      </div>}

      {/* Template list */}
      {templates.length>0?<div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,overflow:'hidden'}}>
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:11}}>
          <thead><tr style={{borderBottom:`1px solid ${C.border}`}}>{['Description','Amount','Type','Supplier','Cost Type','',''].map((h,i)=><th key={i} style={{padding:'7px',textAlign:'left',color:C.mt,fontWeight:500,fontSize:9,textTransform:'uppercase'}}>{h}</th>)}</tr></thead>
          <tbody>{templates.map(t=>(<tr key={t.id} style={{borderBottom:`1px solid ${C.border}08`}}>
            <td style={{padding:'5px 7px',fontWeight:500}}>{t.desc}</td>
            <td style={{padding:'5px 7px',textAlign:'right',color:t.amount>=0?C.green:C.red,fontWeight:600}}>{fmtD(t.amount)}</td>
            <td style={{padding:'5px 7px'}}><Badge t={t.classification} c={t.classification==='Operations'?C.ac:C.purple}/></td>
            <td style={{padding:'5px 7px',fontSize:10,color:C.mt}}>{t.supplier||'—'}</td>
            <td style={{padding:'5px 7px',fontSize:10,color:C.mt}}>{t.costType||t.costLine||'—'}</td>
            <td style={{padding:'5px 7px'}}><button onClick={()=>instantiateTemplate(t)} style={{padding:'3px 10px',borderRadius:5,border:'none',background:C.green+'20',color:C.green,fontSize:10,cursor:'pointer',fontWeight:600}}>Use</button></td>
            <td style={{padding:'5px 7px'}}>
              <button onClick={()=>setTplForm({...t})} style={{padding:'3px 8px',borderRadius:5,border:`1px solid ${C.border}`,background:'transparent',color:C.mt,fontSize:10,cursor:'pointer',marginRight:4}}>Edit</button>
              <button onClick={()=>delTpl(t.id)} style={{padding:'3px 8px',borderRadius:5,border:'none',background:C.red+'20',color:C.red,fontSize:10,cursor:'pointer'}}>Del</button>
            </td>
          </tr>))}</tbody>
        </table>
      </div>:<div style={{padding:20,textAlign:'center',color:C.mt,fontSize:12}}>No templates yet. Create one to get started.</div>}
    </div>);
  }

  // ── LIST ──
  return(<div>
    <div style={{display:'flex',gap:5,marginBottom:10,flexWrap:'wrap',alignItems:'center'}}>
      <button onClick={()=>setView('paste')} style={{padding:'6px 14px',borderRadius:6,border:'none',background:C.ac,color:'#fff',fontWeight:600,fontSize:11,cursor:'pointer'}}>Import Statement</button>
      <button onClick={()=>setView('single')} style={{padding:'6px 14px',borderRadius:6,border:`1px solid ${C.ac}`,background:'transparent',color:C.ac,fontWeight:600,fontSize:11,cursor:'pointer'}}>+ New Transaction</button>
      <button onClick={()=>setView('copy')} style={{padding:'6px 14px',borderRadius:6,border:`1px solid ${C.amber}`,background:'transparent',color:C.amber,fontWeight:600,fontSize:11,cursor:'pointer'}}>Copy from Month</button>
      <button onClick={()=>setView('templates')} style={{padding:'6px 14px',borderRadius:6,border:`1px solid ${C.purple}`,background:'transparent',color:C.purple,fontWeight:600,fontSize:11,cursor:'pointer'}}>Templates{templates.length>0?` (${templates.length})`:''}</button>
      <button onClick={()=>exportCSV(['Date','Description','Amount','Classification','Client/Code','Supplier','Cost Type','Cost Line','Business Line','IVA','Paid','Expected Date','Invoice'],list.map(t=>[t.date,t.desc,t.amount,t.classification,t.clientCode,t.supplier,t.costType,t.costLine,t.businessLine,t.hasIVA,t.pagado,t.expectedDate||'',t.invoiceRef]),'importrust_bank')} style={{padding:'6px 14px',borderRadius:6,border:`1px solid ${C.border}`,background:'transparent',color:C.mt,fontWeight:500,fontSize:11,cursor:'pointer'}}>Export CSV</button>
      <button onClick={()=>setShowCash(!showCash)} style={{padding:'6px 14px',borderRadius:6,border:`1px solid ${showCash?C.cyan:C.border}`,background:showCash?C.cyan+'15':'transparent',color:showCash?C.cyan:C.mt,fontWeight:600,fontSize:11,cursor:'pointer'}}>Cash Needs</button>
    </div>
    <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))',gap:8,marginBottom:14}}>
      {[{l:'Transactions',v:kpis.n},{l:'Inflows',v:fmt(Math.round(kpis.inf)),c:C.green},{l:'Outflows',v:fmt(Math.round(kpis.out)),c:C.red},{l:'Operations',v:fmt(Math.round(kpis.opsAmt)),c:C.ac},{l:'General',v:fmt(Math.round(kpis.genAmt)),c:C.purple},{l:'Expected',v:kpis.exp,c:kpis.exp>0?C.cyan:C.green},{l:'Unclassified',v:kpis.unc,c:kpis.unc>0?C.amber:C.green}].map((k,i)=>(<div key={i} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:8,padding:'10px 12px'}}><div style={{fontSize:9,color:C.mt,textTransform:'uppercase',letterSpacing:'.05em',marginBottom:3}}>{k.l}</div><div style={{fontSize:17,fontWeight:700,color:k.c||C.tx}}>{k.v}</div></div>))}
    </div>
    <div style={{display:'flex',gap:5,marginBottom:10,flexWrap:'wrap',alignItems:'center'}}>
      <Inp value={q} onChange={e=>setQ(e.target.value)} placeholder="Search..." style={{width:180}}/>
      <Sel value={fC} onChange={e=>setFC(e.target.value)} options={['Operations','General','Intracompany','Expected','Unclassified']} placeholder="All Types" style={{width:120}}/>
      <Sel value={fM} onChange={e=>setFM(e.target.value)} options={months} placeholder="All Months" style={{width:110}}/>
      <Sel value={fY} onChange={e=>setFY(e.target.value)} options={bankYears} placeholder="All Years" style={{width:90}}/>
      <Sel value={fP} onChange={e=>setFP(e.target.value)} options={['Paid','Unpaid']} placeholder="All Status" style={{width:100}}/>
      <div style={{marginLeft:'auto',fontSize:10,color:C.mt}}>{list.length} txs</div>
    </div>
    {showCash&&<CashNeeds bankTxs={bankTxs}/>}
    <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,overflow:'hidden'}}>
      <div style={{maxHeight:'65vh',overflowY:'auto'}}>
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:11}}>
          <thead><tr style={{borderBottom:`1px solid ${C.border}`}}>{['Date','Description','Amount','Type','Client/Supplier','Cost Type','Status'].map(h=>(<th key={h} style={{padding:'7px',textAlign:'left',color:C.mt,fontWeight:500,fontSize:9,textTransform:'uppercase',position:'sticky',top:0,background:C.card}}>{h}</th>))}</tr></thead>
          <tbody>{list.slice(0,300).map((t,i)=>{const unc=!t.classification;return(<tr key={t.id||i} onClick={()=>setSelTx({...t})} style={{borderBottom:`1px solid ${C.border}08`,cursor:'pointer',background:unc?C.amber+'06':'transparent'}} onMouseEnter={e=>e.currentTarget.style.background=C.border+'22'} onMouseLeave={e=>e.currentTarget.style.background=unc?C.amber+'06':'transparent'}>
            <td style={{padding:'5px 7px',fontSize:10,color:C.mt,whiteSpace:'nowrap'}}>{t.date}</td>
            <td style={{padding:'5px 7px',maxWidth:200,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{t.desc}</td>
            <td style={{padding:'5px 7px',textAlign:'right',fontWeight:600,color:t.amount>=0?C.green:C.red}}>{fmtD(t.amount)}</td>
            <td style={{padding:'5px 7px'}}>{t.classification?<Badge t={t.classification} c={t.classification==='Operations'?C.ac:C.purple}/>:<Badge t="—" c={C.amber}/>}</td>
            <td style={{padding:'5px 7px',fontSize:10,color:C.mt,maxWidth:120,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{t.clientCode?clients.find(c=>c.c===t.clientCode)?.n||t.clientCode.slice(0,20):t.supplier||'—'}</td>
            <td style={{padding:'5px 7px',fontSize:10,color:C.mt}}>{t.costType||t.costLine||'—'}</td>
            <td style={{padding:'5px 7px'}}>{t._expected&&t.pagado!=='Yes'?<span style={{display:'flex',alignItems:'center',gap:4}}><Badge t="Expected" c={C.cyan}/>{t.expectedDate&&<span style={{fontSize:8,color:C.mt}}>{t.expectedDate}</span>}</span>:t.pagado==='Yes'?<Badge t="Paid" c={C.green}/>:t.pagado==='No'?<span style={{display:'flex',alignItems:'center',gap:4}}><Badge t="To pay" c={C.amber}/>{t.expectedDate&&<span style={{fontSize:8,color:C.mt}}>{t.expectedDate}</span>}</span>:<Badge t="Pending" c={C.mt}/>}</td>
          </tr>);})}</tbody>
        </table>
      </div>
      {list.length===0&&<div style={{padding:30,textAlign:'center',color:C.mt,fontSize:12}}>No transactions yet. Click "Import Statement" to upload a bank file.</div>}
      {list.length>300&&<div style={{padding:8,textAlign:'center',color:C.mt,fontSize:10}}>Showing 300 of {list.length}</div>}
    </div>
  </div>);
}

// ══════════════════════════════════════════
//  SPLIT PANEL (sub-component for Bank)
// ══════════════════════════════════════════
function SplitPanel({tx,splitLines,setSplitLines,setSplitMode,clients,aux,executeSplit}){
  const total=Math.abs(tx.amount);
  const allocated=splitLines.reduce((s,l)=>s+(+l.amount||0),0);
  const diff=Math.round((total-allocated)*100)/100;
  const isValid=Math.abs(diff)<0.01&&splitLines.filter(l=>l.clientCode&&+l.amount).length>=2;
  const updLine=(i,k,v)=>setSplitLines(p=>p.map((l,j)=>j===i?{...l,[k]:v}:l));
  const addLine=()=>setSplitLines(p=>[...p,{clientCode:'',amount:'',costType:tx.costType||'',supplier:tx.supplier||''}]);
  const rmLine=i=>setSplitLines(p=>p.filter((_,j)=>j!==i));
  return(
    <div style={{marginTop:12,padding:14,background:C.amber+'08',borderRadius:8,border:`1px solid ${C.amber}25`}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
        <div style={{fontSize:11,fontWeight:600,color:C.amber}}>Split Transaction ({fmtD(tx.amount)})</div>
        <button onClick={()=>{setSplitMode(false);setSplitLines([{clientCode:'',amount:'',costType:'',supplier:''}]);}} style={{padding:'3px 10px',borderRadius:5,border:`1px solid ${C.border}`,background:'transparent',color:C.mt,cursor:'pointer',fontSize:10}}>Cancel</button>
      </div>
      {splitLines.map((line,i)=>{const deal=clients.find(c=>c.c===line.clientCode);return(
        <div key={i} style={{display:'grid',gridTemplateColumns:'2fr 1fr 1fr auto',gap:6,marginBottom:6,alignItems:'end'}}>
          <div><label style={{fontSize:9,color:C.mt,display:'block',marginBottom:1}}>Client ID</label>
            <input value={line.clientCode} onChange={e=>updLine(i,'clientCode',e.target.value)} list={`sp-cl-${i}`} placeholder="Search deal..." style={{width:'100%',padding:'5px 7px',borderRadius:5,border:`1px solid ${C.border}`,background:C.bg,color:C.tx,fontSize:11,boxSizing:'border-box',outline:'none'}}/>
            <datalist id={`sp-cl-${i}`}>{clients.map(c=><option key={c.c} value={c.c}>{c.n} (#{c.i})</option>)}</datalist>
            {deal&&<div style={{fontSize:8,color:C.green,marginTop:1}}>#{deal.i} {deal.n}</div>}
          </div>
          <div><label style={{fontSize:9,color:C.mt,display:'block',marginBottom:1}}>Amount (€)</label>
            <input type="number" value={line.amount} onChange={e=>updLine(i,'amount',e.target.value)} style={{width:'100%',padding:'5px 7px',borderRadius:5,border:`1px solid ${C.border}`,background:C.bg,color:C.tx,fontSize:11,boxSizing:'border-box',outline:'none'}}/>
          </div>
          <Sel label="Cost Type" value={line.costType} onChange={e=>updLine(i,'costType',e.target.value)} options={aux.COST_TYPE||[]}/>
          {splitLines.length>2&&<button onClick={()=>rmLine(i)} style={{padding:'5px 8px',borderRadius:5,border:'none',background:C.red+'20',color:C.red,cursor:'pointer',fontSize:12,marginBottom:1}}>×</button>}
        </div>);})}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginTop:8}}>
        <button onClick={addLine} style={{padding:'4px 12px',borderRadius:5,border:`1px solid ${C.border}`,background:'transparent',color:C.mt,cursor:'pointer',fontSize:10}}>+ Add line</button>
        <div style={{fontSize:11}}>
          <span style={{color:C.mt}}>Allocated: </span><span style={{fontWeight:600,color:allocated>0?C.tx:C.mt}}>{fmtD(allocated)}</span>
          <span style={{color:C.mt}}> / {fmtD(total)}</span>
          {diff!==0&&<span style={{color:Math.abs(diff)<0.01?C.green:C.red,fontWeight:600,marginLeft:6}}>{diff>0?`${fmtD(diff)} remaining`:`${fmtD(Math.abs(diff))} over`}</span>}
          {Math.abs(diff)<0.01&&allocated>0&&<span style={{color:C.green,fontWeight:600,marginLeft:6}}>Balanced</span>}
        </div>
      </div>
      <button onClick={()=>executeSplit(tx)} disabled={!isValid} style={{marginTop:10,padding:'7px 18px',borderRadius:7,border:'none',background:isValid?C.green:C.border,color:isValid?'#fff':C.mt,fontWeight:600,fontSize:12,cursor:isValid?'pointer':'default',opacity:isValid?1:0.5}}>Confirm Split ({splitLines.filter(l=>l.clientCode&&+l.amount).length} deals)</button>
    </div>
  );
}

// ══════════════════════════════════════════
//  DEALS LIST (M1)
// ══════════════════════════════════════════
function DealsList({clients,setClients,bankTxs,setBankTxs,aux,onNewDeal}){
  const [q,setQ]=useState('');const [fBD,setFBD]=useState('ALL');const [fSvc,setFSvc]=useState('ALL');const [fSt,setFSt]=useState('ALL');const [fYr,setFYr]=useState('ALL');const [fType,setFType]=useState('ALL');const [sel,setSel]=useState(null);const [sC,setSC]=useState('i');const [sD,setSD]=useState(-1);
  const years=useMemo(()=>[...new Set(clients.map(c=>c.y).filter(Boolean))].sort(),[clients]);const bds=useMemo(()=>[...new Set(clients.map(c=>c.o||c.ow).filter(Boolean))].sort(),[clients]);
  const list=useMemo(()=>{let f=[...clients];if(q){const s=q.toLowerCase();f=f.filter(c=>(c.n||'').toLowerCase().includes(s)||(c.br||'').toLowerCase().includes(s)||String(c.i).includes(s));}if(fBD!=='ALL')f=f.filter(c=>(c.o||c.ow)===fBD);if(fSvc!=='ALL')f=f.filter(c=>c.sv===fSvc);if(fSt!=='ALL')f=f.filter(c=>c.st===fSt);if(fYr!=='ALL')f=f.filter(c=>c.y===+fYr);if(fType!=='ALL')f=f.filter(c=>fType==='B2C'?c.b2===1:c.b2===0);f.sort((a,b)=>{const av=a[sC],bv=b[sC];if(av==null&&bv==null)return 0;if(av==null)return 1;if(bv==null)return-1;return(av<bv?-1:av>bv?1:0)*sD;});return f;},[clients,q,fBD,fSvc,fSt,fYr,fType,sC,sD]);
  const tSort=col=>{if(sC===col)setSD(d=>d*-1);else{setSC(col);setSD(-1);}};
  const kpis=useMemo(()=>{const tMIT=list.reduce((s,c)=>s+(c.mit||0)+(c.finFee||0),0),tAR=list.reduce((s,c)=>s+(c.ar||0),0),tDC=list.reduce((s,c)=>s+Math.max(c.dc||0,0),0);const b2c=list.filter(c=>c.b2===1),b2b=list.filter(c=>c.b2===0);const now45=new Date();now45.setDate(now45.getDate()-45);const tDC45=list.reduce((s,c)=>{if((c.dc||0)<=0)return s;const sig=c.s?new Date(c.s):null;return sig&&sig<now45?s+(c.dc||0):s;},0);
    return{n:list.length,tMIT,tAR,tDC,tDC45,b2cN:b2c.length,b2bN:b2b.length,b2cM:b2c.reduce((s,c)=>s+(c.mit||0)+(c.finFee||0),0),b2bM:b2b.reduce((s,c)=>s+(c.mit||0)+(c.finFee||0),0)};},[list]);

  if(sel){const sameId=clients.filter(c=>c.i===sel.i);const isMulti=sameId.length>1;const fin=calcFin(sel);const bankRc=bankTxs.filter(t=>t.clientCode===sel.c&&t.amount>0).reduce((s,t)=>s+t.amount,0);const bankPaid=bankRc>=(sel.ar||0);const upF=(k,v)=>{if(!confirm(`Change ${k} to "${v}"?`))return;setClients(p=>p.map(c=>c===sel?{...c,[k]:v}:c));setSel(s=>({...s,[k]:v}));};
    const CR=({l,v,ok})=>(<div style={{display:'flex',justifyContent:'space-between',padding:'4px 0',borderBottom:`1px solid ${C.border}08`,fontSize:12}}><span style={{color:C.mt,display:'flex',alignItems:'center',gap:5}}>{l}<span style={{width:6,height:6,borderRadius:3,background:ok?C.green:C.border,display:'inline-block'}}/></span><span style={{fontWeight:500,color:ok?C.green:C.tx}}>{fmt(v)}</span></div>);
    const dealTxs=bankTxs.filter(t=>t.clientCode===sel.c);
    const deleteDeal=()=>{
      const linkedCount=dealTxs.length;
      const msg=`Delete deal #${sel.i} — ${sel.n}?\n\n`+(linkedCount>0?`${linkedCount} linked bank transaction(s) will be unlinked from this deal (the transactions themselves stay in Bank, with the client cleared).\n\n`:'')+'This cannot be undone.';
      if(!confirm(msg))return;
      if(linkedCount>0&&setBankTxs){
        setBankTxs(p=>p.map(t=>t.clientCode===sel.c?{...t,clientCode:'',costType:''}:t));
      }
      setClients(p=>p.filter(c=>c.c!==sel.c));
      setSel(null);
    };
    return(<div><div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}><button onClick={()=>setSel(null)} style={{padding:'5px 12px',borderRadius:6,border:`1px solid ${C.border}`,background:'transparent',color:C.mt,cursor:'pointer',fontSize:11}}>← Back</button><button onClick={deleteDeal} style={{padding:'5px 14px',borderRadius:6,border:`1px solid ${C.red}50`,background:C.red+'12',color:C.red,fontWeight:600,fontSize:11,cursor:'pointer'}}>Delete Deal</button></div>
      {isMulti&&<div style={{background:C.purple+'12',border:`1px solid ${C.purple}30`,borderRadius:8,padding:'8px 12px',marginBottom:12,fontSize:11,color:C.purple}}>B2B — {sameId.length} lines ID #{sel.i}</div>}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
        <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:16}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'start',marginBottom:10}}><div><div style={{fontSize:15,fontWeight:700}}>#{sel.i} — {sel.n}</div><div style={{display:'flex',gap:5,marginTop:5}}><Badge t={sel.st||'—'} c={sel.st==='CLOSED'?C.green:C.amber}/><Badge t={sel.sv||'—'} c={C.ac}/><Badge t={sel.b2===0?'B2B':'B2C'} c={sel.b2===0?C.purple:C.ac}/>{fin.isREBU&&<Badge t="REBU" c={C.cyan}/>}</div></div><select value={sel.st||''} onChange={e=>upF('st',e.target.value)} style={{padding:'3px 6px',borderRadius:5,border:`1px solid ${C.border}`,background:C.bg,color:C.tx,fontSize:10}}><option value="ONGOING">ONGOING</option><option value="CLOSED">CLOSED</option></select></div>
          {[['BD',sel.o||sel.ow],['Signature',sel.s],['Month/Year',`${sel.m} ${sel.y}`],['Brand',sel.br],['Model',sel.md],['Engine',sel.en],['PVP',fmt(sel.pv)],['Car Price',fmt(sel.cp)],['Fee',fmt(sel.ar)]].map(([l,v])=>(<div key={l} style={{display:'flex',justifyContent:'space-between',padding:'4px 0',borderBottom:`1px solid ${C.border}08`,fontSize:12}}><span style={{color:C.mt}}>{l}</span><span style={{fontWeight:500}}>{v||'—'}</span></div>))}
          {fin.isREBU&&<>{[['Buy Price',fmt(sel.rebuBuy)],['Sell Price',fmt(sel.rebuSell)],['Trade Margin',fmt((sel.rebuSell||0)-(sel.rebuBuy||0))]].map(([l,v])=>(<div key={l} style={{display:'flex',justifyContent:'space-between',padding:'4px 0',borderBottom:`1px solid ${C.border}08`,fontSize:12}}><span style={{color:C.mt}}>{l}</span><span style={{color:C.cyan,fontWeight:500}}>{v}</span></div>))}</>}
        </div>
        <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:16}}>
          <div style={{fontSize:13,fontWeight:600,marginBottom:10}}>Financials</div>
          <div style={{fontSize:10,color:C.mt,marginBottom:4,textTransform:'uppercase'}}>Operational Costs</div>
          <CR l="Transport" v={sel.tr} ok={!!sel.trReal}/><CR l="Gestoría" v={sel.gt} ok={!!sel.gtReal}/><CR l="CEVA" v={sel.ct} ok={!!sel.ctReal}/><CR l="Doc. adicional" v={sel.doc} ok={!!sel.docReal}/><CR l="Otras reparaciones" v={sel.orep} ok={!!sel.orepReal}/><CR l="Other Costs" v={sel.otherCosts} ok={!!sel.otherCostsReal}/><CR l="Total Ops" v={sel.to} ok={false}/>
          <div style={{height:8}}/><div style={{fontSize:10,color:C.mt,marginBottom:4,textTransform:'uppercase'}}>Margins</div>
          {[['Op. Margin',fin.om,fin.om>=0?C.green:C.red,true],['VAT (21/121)',fin.vat,C.tx,false],['Margin IT',fin.mit,fin.mit>=0?C.green:C.red,true]].map(([l,v,clr,b])=>(<div key={l} style={{display:'flex',justifyContent:'space-between',padding:'5px 0',borderBottom:`1px solid ${C.border}08`,fontSize:12}}><span style={{color:C.mt,fontWeight:b?600:400}}>{l}</span><span style={{fontWeight:b?700:500,color:clr}}>{fmtD(v)}</span></div>))}
          <div style={{display:'flex',justifyContent:'space-between',padding:'5px 0',borderBottom:`1px solid ${C.border}08`,fontSize:12}}><span style={{color:C.mt}}>Financing Fee</span><span style={{color:fin.finFee>0?C.cyan:C.mt}}>{fin.finFee>0?fmtD(fin.finFee):'—'}</span></div>
          <div style={{display:'flex',justifyContent:'space-between',padding:'5px 0',borderBottom:`1px solid ${C.border}08`,fontSize:12}}><span style={{color:C.cyan,fontWeight:600}}>Margin IT Total</span><span style={{fontWeight:700,color:(fin.mit+(fin.finFee||0))>=0?C.green:C.red}}>{fmtD(fin.mit+(fin.finFee||0))}</span></div>
          <div style={{height:6}}/><div style={{fontSize:10,color:C.mt,marginBottom:4,textTransform:'uppercase'}}>BD Commission</div>
          {[['10% Margin IT',fin.commBase,C.ac,false],...(fin.finFee>0?[['Financing Bonus',FIN_BONUS,C.cyan,false]]:[]),['Total Commission',fin.commTotal,C.ac,true]].map(([l,v,clr,b])=>(<div key={l} style={{display:'flex',justifyContent:'space-between',padding:'4px 0',borderBottom:`1px solid ${C.border}08`,fontSize:12}}><span style={{color:C.mt}}>{l}</span><span style={{fontWeight:b?700:500,color:clr}}>{fmtD(v)}</span></div>))}
          <div style={{height:8}}/><div style={{fontSize:10,color:C.mt,marginBottom:4,textTransform:'uppercase'}}>Payment</div>
          {(sel.tranches||1)>=2&&<>{[['1st Tranche',fmt(sel.t1||sel.ar)],['2nd Tranche',fmt(sel.t2||0)]].map(([l,v])=>(<div key={l} style={{display:'flex',justifyContent:'space-between',padding:'4px 0',borderBottom:`1px solid ${C.border}08`,fontSize:12}}><span style={{color:C.mt}}>{l}</span><span>{v}</span></div>))}</>}
          {[['Fee',sel.ar,C.tx],['Received',sel.rc,C.green],['Client Debt',sel.dc,(sel.dc||0)>0?C.amber:C.green],['Bank Inflows',bankRc,bankRc>0?C.green:C.mt]].map(([l,v,clr])=>(<div key={l} style={{display:'flex',justifyContent:'space-between',padding:'4px 0',borderBottom:`1px solid ${C.border}08`,fontSize:12}}><span style={{color:C.mt}}>{l}</span><span style={{fontWeight:600,color:clr}}>{fmt(v)}</span></div>))}
          <div style={{marginTop:8,padding:8,borderRadius:6,background:bankPaid?C.green+'12':(sel.dc||0)>0?C.amber+'12':C.border+'22',fontSize:11,display:'flex',justifyContent:'space-between',alignItems:'center'}}><span style={{color:bankPaid?C.green:(sel.dc||0)>0?C.amber:C.mt}}>{bankPaid?'Paid — confirmed':(sel.dc||0)>0?`${fmt(sel.dc)} pending`:'No debt'}</span>{bankPaid&&<span style={{color:C.green,fontSize:14}}>✓</span>}</div>
        </div>
      </div>
      {/* Invoice history for this deal */}
      {(()=>{const invLines=(bankTxs||[]).filter(t=>t.clientCode===sel.c&&t.classification==='Operations'&&t.amount<0);if(invLines.length===0)return null;
        const byInvoice={};invLines.forEach(t=>{const ref=t.invoiceRef||'No ref';if(!byInvoice[ref])byInvoice[ref]={ref,lines:[],total:0};byInvoice[ref].lines.push(t);byInvoice[ref].total+=Math.abs(t.amount);});
        const invoices=Object.values(byInvoice);
        // Detect duplicates: same amount appearing multiple times
        const amountCounts={};invLines.forEach(t=>{const k=Math.round(Math.abs(t.amount)*100);amountCounts[k]=(amountCounts[k]||0)+1;});
        const dupAmounts=new Set(Object.entries(amountCounts).filter(([k,v])=>v>1).map(([k])=>+k));
        return(<div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:16,marginTop:12}}>
          <div style={{fontSize:13,fontWeight:600,marginBottom:10}}>Invoiced Costs ({invLines.length} lines, {fmt(Math.round(invLines.reduce((s,t)=>s+Math.abs(t.amount),0)))} total)</div>
          {dupAmounts.size>0&&<div style={{background:C.amber+'12',border:`1px solid ${C.amber}30`,borderRadius:6,padding:'6px 10px',marginBottom:10,fontSize:10,color:C.amber}}>⚠ Possible duplicates: {dupAmounts.size} amount(s) appear more than once</div>}
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:11}}>
            <thead><tr style={{borderBottom:`1px solid ${C.border}`}}>{['Date','Description','Amount','Invoice','Supplier'].map(h=><th key={h} style={{padding:'5px 6px',textAlign:'left',color:C.mt,fontWeight:500,fontSize:9,textTransform:'uppercase'}}>{h}</th>)}</tr></thead>
            <tbody>{invLines.sort((a,b)=>Math.abs(b.amount)-Math.abs(a.amount)).map((t,i)=>{
              const isDup=dupAmounts.has(Math.round(Math.abs(t.amount)*100));
              return(<tr key={i} style={{borderBottom:`1px solid ${C.border}08`,background:isDup?C.amber+'08':'transparent'}}>
                <td style={{padding:'4px 6px',color:C.mt,fontSize:10}}>{t.date}</td>
                <td style={{padding:'4px 6px'}}>{t.desc}{isDup&&<span style={{marginLeft:4,fontSize:8,color:C.amber,fontWeight:600}}>DUP?</span>}</td>
                <td style={{padding:'4px 6px',textAlign:'right',fontWeight:600,color:C.red}}>{fmtD(Math.abs(t.amount))}</td>
                <td style={{padding:'4px 6px',fontSize:9,color:C.mt}}>{t.invoiceRef||'—'}</td>
                <td style={{padding:'4px 6px',fontSize:10,color:C.mt}}>{t.supplier||'—'}</td>
              </tr>);
            })}</tbody>
          </table>
        </div>);
      })()}
      {dealTxs.length>0&&<div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:16,marginTop:12}}><div style={{fontSize:13,fontWeight:600,marginBottom:10}}>Bank Transactions ({dealTxs.length})</div><table style={{width:'100%',borderCollapse:'collapse',fontSize:11}}><thead><tr style={{borderBottom:`1px solid ${C.border}`}}>{['Date','Description','Amount','Cost Type','Status'].map(h=><th key={h} style={{padding:'6px',textAlign:'left',color:C.mt,fontWeight:500,fontSize:9,textTransform:'uppercase'}}>{h}</th>)}</tr></thead><tbody>{dealTxs.map((t,i)=>(<tr key={i} style={{borderBottom:`1px solid ${C.border}08`}}><td style={{padding:'4px 6px',color:C.mt,fontSize:10}}>{t.date}</td><td style={{padding:'4px 6px',maxWidth:180,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{t.desc}</td><td style={{padding:'4px 6px',textAlign:'right',fontWeight:600,color:t.amount>=0?C.green:C.red}}>{fmtD(t.amount)}</td><td style={{padding:'4px 6px',fontSize:10,color:C.mt}}>{t.costType||'—'}</td><td style={{padding:'4px 6px'}}><Badge t={t.pagado==='Yes'?'Paid':'Pending'} c={t.pagado==='Yes'?C.green:C.mt}/></td></tr>))}</tbody></table></div>}
      {isMulti&&<div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:16,marginTop:12}}><div style={{fontSize:13,fontWeight:600,marginBottom:10}}>B2B Lines (#{sel.i})</div><table style={{width:'100%',borderCollapse:'collapse',fontSize:11}}><thead><tr style={{borderBottom:`1px solid ${C.border}`}}>{['Vehicle','Brand','Margin IT','Status'].map(h=><th key={h} style={{padding:'6px 8px',textAlign:'left',color:C.mt,fontSize:10,textTransform:'uppercase'}}>{h}</th>)}</tr></thead><tbody>{sameId.map((c,i)=>{const f2=calcFin(c);return(<tr key={i} onClick={()=>setSel(c)} style={{borderBottom:`1px solid ${C.border}08`,cursor:'pointer',background:c===sel?C.ac+'10':'transparent'}}><td style={{padding:'5px 8px',fontWeight:500}}>{c.n}</td><td style={{padding:'5px 8px'}}>{c.br}</td><td style={{padding:'5px 8px',textAlign:'right',color:f2.mit>=0?C.green:C.red,fontWeight:600}}>{fmtD(f2.mit)}</td><td style={{padding:'5px 8px'}}><Badge t={c.st||'—'} c={c.st==='CLOSED'?C.green:C.amber}/></td></tr>);})}</tbody></table></div>}
    </div>);}

  return(<div>
    <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))',gap:8,marginBottom:14}}>{[{l:'Deals',v:kpis.n,s:`${kpis.b2cN} B2C · ${kpis.b2bN} B2B`},{l:'Margin IT Total',v:fmt(Math.round(kpis.tMIT)),c:kpis.tMIT>=0?C.green:C.red},{l:'B2C Margin IT',v:fmt(Math.round(kpis.b2cM)),c:C.ac},{l:'B2B Margin IT',v:fmt(Math.round(kpis.b2bM)),c:C.purple},{l:'Fee Total',v:fmt(Math.round(kpis.tAR))},{l:'Total Debt',v:fmt(Math.round(kpis.tDC)),c:kpis.tDC>0?C.amber:C.green},{l:'Debt > 45 days',v:fmt(Math.round(kpis.tDC45)),c:kpis.tDC45>0?C.red:C.green}].map((k,i)=>(<div key={i} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:8,padding:'10px 12px'}}><div style={{fontSize:9,color:C.mt,textTransform:'uppercase',letterSpacing:'.05em',marginBottom:3}}>{k.l}</div><div style={{fontSize:17,fontWeight:700,color:k.c||C.tx}}>{k.v}</div>{k.s&&<div style={{fontSize:9,color:C.mt,marginTop:2}}>{k.s}</div>}</div>))}</div>
    <div style={{display:'flex',gap:5,marginBottom:10,flexWrap:'wrap',alignItems:'center'}}>{onNewDeal&&<button onClick={onNewDeal} style={{padding:'6px 14px',borderRadius:6,border:'none',background:C.ac,color:'#fff',fontWeight:600,fontSize:11,cursor:'pointer'}}>+ New Deal</button>}<button onClick={()=>exportCSV(['ID','Client','BD','Brand','Model','Month','Year','Service','Fee','Margin IT','Op Margin','VAT','Total Ops','Debt','Status','Type'],list.map(c=>[c.i,c.n,c.o||c.ow,c.br,c.md,c.m,c.y,c.sv,c.ar,c.mit,c.om,c.vat,c.to,c.dc,c.st,c.b2===1?'B2C':'B2B']),'importrust_deals')} style={{padding:'6px 14px',borderRadius:6,border:`1px solid ${C.border}`,background:'transparent',color:C.mt,fontWeight:500,fontSize:11,cursor:'pointer'}}>Export CSV</button><Inp value={q} onChange={e=>setQ(e.target.value)} placeholder="Search..." style={{width:200}}/><Sel value={fBD} onChange={e=>setFBD(e.target.value)} options={bds} placeholder="All BDs" style={{width:130}}/><Sel value={fSvc} onChange={e=>setFSvc(e.target.value)} options={aux.BUSINESS_LINE||[]} placeholder="All Services" style={{width:130}}/><Sel value={fSt} onChange={e=>setFSt(e.target.value)} options={['CLOSED','ONGOING']} placeholder="All Status" style={{width:110}}/><Sel value={fYr} onChange={e=>setFYr(e.target.value)} options={years.map(String)} placeholder="All Years" style={{width:90}}/><Sel value={fType} onChange={e=>setFType(e.target.value)} options={['B2C','B2B']} placeholder="B2C/B2B" style={{width:90}}/><div style={{marginLeft:'auto',fontSize:10,color:C.mt}}>{list.length} deals</div></div>
    <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,overflow:'hidden'}}><div style={{maxHeight:'65vh',overflowY:'auto'}}><table style={{width:'100%',borderCollapse:'collapse',fontSize:11}}><thead><tr style={{borderBottom:`1px solid ${C.border}`}}>{[{k:'i',l:'ID'},{k:'n',l:'Client'},{k:'o',l:'BD'},{k:'br',l:'Brand'},{k:'m',l:'Month'},{k:'sv',l:'Service'},{k:'mit',l:'MIT Total'},{k:'ar',l:'Fee'},{k:'dc',l:'Debt'},{k:'st',l:'Status'},{k:'b2',l:'Type'}].map(h=>(<th key={h.k} onClick={()=>tSort(h.k)} style={{padding:'7px',textAlign:'left',color:C.mt,fontWeight:500,fontSize:9,textTransform:'uppercase',cursor:'pointer',userSelect:'none',whiteSpace:'nowrap',position:'sticky',top:0,background:C.card}}>{h.l}{sC===h.k?(sD===1?' ↑':' ↓'):''}</th>))}</tr></thead>
      <tbody>{list.slice(0,200).map((c,i)=>{const hasDC=(c.dc||0)>100;return(<tr key={i} onClick={()=>setSel(c)} style={{borderBottom:`1px solid ${C.border}08`,cursor:'pointer',background:hasDC?C.amber+'06':'transparent'}} onMouseEnter={e=>e.currentTarget.style.background=C.border+'22'} onMouseLeave={e=>e.currentTarget.style.background=hasDC?C.amber+'06':'transparent'}><td style={{padding:'5px 7px',fontWeight:600,color:C.ac}}>{c.i}</td><td style={{padding:'5px 7px',fontWeight:500,maxWidth:150,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{c.n}</td><td style={{padding:'5px 7px',color:C.mt,fontSize:10}}>{c.o||c.ow}</td><td style={{padding:'5px 7px',fontSize:10}}>{c.br}</td><td style={{padding:'5px 7px',fontSize:9,color:C.mt}}>{c.m} {c.y}</td><td style={{padding:'5px 7px'}}><Badge t={c.sv||'—'} c={c.sv==='IMPORTATION'?C.ac:c.sv==='B2B'?C.purple:C.amber}/></td><td style={{padding:'5px 7px',textAlign:'right',color:((c.mit||0)+(c.finFee||0))>=0?C.green:C.red,fontWeight:600}}>{fmt((c.mit||0)+(c.finFee||0))}</td><td style={{padding:'5px 7px',textAlign:'right'}}>{fmt(c.ar)}</td><td style={{padding:'5px 7px',textAlign:'right',fontWeight:hasDC?700:400,color:hasDC?C.amber:C.green}}>{hasDC?fmt(c.dc):'—'}</td><td style={{padding:'5px 7px'}}><Badge t={c.st||'—'} c={c.st==='CLOSED'?C.green:C.amber}/></td><td style={{padding:'5px 7px'}}><Badge t={c.b2===0?'B2B':'B2C'} c={c.b2===0?C.purple:C.ac}/></td></tr>);})}</tbody></table></div>{list.length===0&&<div style={{padding:30,textAlign:'center',color:C.mt,fontSize:12}}>No deals yet.</div>}</div>
  </div>);
}

// ══════════════════════════════════════════
//  NEW DEAL (M1)
// ══════════════════════════════════════════
function NewDeal({clients,setClients,aux,onDone}){
  const [f,setF]=useState({owner:'',month:'',year:2026,name:'',brand:'',model:'',engine:'',service:'IMPORTATION',pvp:'',carPrice:'',tax:0,transport:750,gestoria:150.64,itv:150,ceva:316,doc:0,orep:0,otherCosts:0,finFee:0,fee:'',tranches:1,t1:'',b2c:1,isMulti:false,b2bLines:1,dealType:'standard',rebuBuy:'',rebuSell:''});
  const u=(k,v)=>setF(p=>({...p,[k]:v}));const nid=Math.max(...clients.map(c=>c.i||0),0)+1;
  const fee=+f.fee||0,finFee=+f.finFee||0,ops=(+f.transport||0)+(+f.gestoria||0)+(+f.ceva||0)+(+f.doc||0)+(+f.orep||0)+(+f.otherCosts||0),itv=+f.itv||0,isREBU=f.dealType==='rebu';
  let om,vat,mit;if(isREBU){const s=+f.rebuSell||0,b=+f.rebuBuy||0,tm=s-b;vat=tm>0?tm*VAT_RATE:0;om=fee-ops;mit=tm-vat-ops;}else{om=fee-ops;vat=(fee*VAT_RATE)-((ops-itv)*VAT_RATE);mit=om-vat;}
  const cb=mit>0?mit*COMM_RATE:0,fb=finFee>0?FIN_BONUS:0,ct=cb+fb,t1V=+f.t1||fee,t2V=+f.tranches>=2?fee-t1V:0;
  const create=()=>{const lines=f.isMulti?Math.max(1,+f.b2bLines||1):1;const nd=[];for(let l=0;l<lines;l++){nd.push({i:nid,o:f.owner,s:new Date().toISOString().slice(0,10),m:f.month,y:+f.year,n:lines>1?`${f.name} ${l+1}`:f.name,c:`${nid}${f.name}${lines>1?` ${l+1}`:''}`,br:f.brand,md:f.model,en:f.engine,sv:f.service,pv:+f.pvp||0,cp:+f.carPrice||0,tx:+f.tax||0,tr:+f.transport||0,gt:+f.gestoria||0,itv,ct:+f.ceva||0,doc:+f.doc||0,orep:+f.orep||0,otherCosts:+f.otherCosts||0,finFee,dealType:f.dealType,rebuBuy:+f.rebuBuy||0,rebuSell:+f.rebuSell||0,to:ops,om,vat,mit,pct:ct,commTotal:ct,ar:fee,t1:t1V,t2:t2V,tranches:+f.tranches||1,rc:0,dc:fee,st:'ONGOING',ow:f.owner,pd:'No',b2:+f.b2c});}setClients(p=>[...p,...nd]);onDone();};
  return(<div style={{maxWidth:740}}><div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14}}><div style={{fontSize:15,fontWeight:700}}>New Deal <span style={{color:C.mt,fontWeight:400,fontSize:12}}>ID #{nid}</span></div><button onClick={onDone} style={{padding:'5px 12px',borderRadius:6,border:`1px solid ${C.border}`,background:'transparent',color:C.mt,cursor:'pointer',fontSize:11}}>Cancel</button></div>
    <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:18}}>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10}}><Inp label="Client Name *" value={f.name} onChange={e=>u('name',e.target.value)}/><Sel label="BD Owner *" value={f.owner} onChange={e=>u('owner',e.target.value)} options={aux.BD||[]}/><Sel label="Service" value={f.service} onChange={e=>u('service',e.target.value)} options={aux.BUSINESS_LINE||[]} placeholder={false}/><Sel label="Brand" value={f.brand} onChange={e=>u('brand',e.target.value)} options={aux.CAR_BRAND||[]}/><Inp label="Model" value={f.model} onChange={e=>u('model',e.target.value)}/><Sel label="Engine" value={f.engine} onChange={e=>u('engine',e.target.value)} options={aux.ENGINE||[]}/><Sel label="Month" value={f.month} onChange={e=>u('month',e.target.value)} options={MONTHS}/><Inp label="Year" type="number" value={f.year} onChange={e=>u('year',+e.target.value)}/><div><label style={{fontSize:10,color:C.mt,display:'block',marginBottom:2}}>Type</label><select value={String(f.b2c)} onChange={e=>{u('b2c',+e.target.value);if(+e.target.value===0)u('isMulti',true);}} style={{width:'100%',padding:'6px 8px',borderRadius:6,border:`1px solid ${C.border}`,background:C.bg,color:C.tx,fontSize:12,boxSizing:'border-box'}}><option value="1">B2C</option><option value="0">B2B</option></select></div></div>
      <div style={{marginTop:10,display:'flex',gap:8}}>{[['standard','Standard (IVA on fee)'],['rebu','REBU — Buy & Sell (IVA on margin)']].map(([v,l])=>(<button key={v} onClick={()=>u('dealType',v)} style={{padding:'6px 14px',borderRadius:6,fontSize:11,fontWeight:f.dealType===v?600:400,cursor:'pointer',border:`1px solid ${f.dealType===v?(v==='rebu'?C.cyan:C.ac):C.border}`,background:f.dealType===v?(v==='rebu'?C.cyan+'12':C.ac+'12'):'transparent',color:f.dealType===v?(v==='rebu'?C.cyan:C.ac):C.mt}}>{l}</button>))}</div>
      {isREBU&&<div style={{marginTop:10,padding:12,background:C.cyan+'08',borderRadius:8,border:`1px solid ${C.cyan}25`}}><div style={{fontSize:10,color:C.cyan,textTransform:'uppercase',marginBottom:6}}>REBU — Buy & Sell (IVA included)</div><div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10}}><Inp label="Buy Price (€)" type="number" value={f.rebuBuy} onChange={e=>u('rebuBuy',e.target.value)}/><Inp label="Sell Price (€)" type="number" value={f.rebuSell} onChange={e=>u('rebuSell',e.target.value)}/><div><label style={{fontSize:10,color:C.mt,display:'block',marginBottom:2}}>Trade Margin</label><div style={{padding:'6px 8px',borderRadius:6,border:`1px solid ${C.border}`,background:C.border+'30',color:C.cyan,fontSize:12,fontWeight:600}}>{fmt((+f.rebuSell||0)-(+f.rebuBuy||0))}</div></div></div></div>}
      {f.b2c===0&&<div style={{marginTop:10,padding:10,background:C.purple+'08',borderRadius:6,border:`1px solid ${C.purple}20`,display:'flex',gap:12,alignItems:'center'}}><label style={{fontSize:11,color:C.purple}}><input type="checkbox" checked={f.isMulti} onChange={e=>u('isMulti',e.target.checked)} style={{marginRight:4}}/>Multi-line B2B</label>{f.isMulti&&<Inp label="Lines" type="number" value={f.b2bLines} onChange={e=>u('b2bLines',+e.target.value)} style={{width:60}}/>}</div>}
      <div style={{marginTop:14,fontSize:10,color:C.mt,textTransform:'uppercase',marginBottom:6}}>Financial Inputs</div>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr 1fr 1fr',gap:10}}><Inp label="PVP (€)" type="number" value={f.pvp} onChange={e=>u('pvp',e.target.value)}/><Inp label="Car Price (€)" type="number" value={f.carPrice} onChange={e=>u('carPrice',e.target.value)}/><Inp label="Tax (€)" type="number" value={f.tax} onChange={e=>u('tax',+e.target.value)}/><Inp label="Fee (€) *" type="number" value={f.fee} onChange={e=>u('fee',e.target.value)}/><div><label style={{fontSize:10,color:C.cyan,display:'block',marginBottom:2}}>Financing Fee (€)</label><input type="number" value={f.finFee} onChange={e=>u('finFee',+e.target.value)} style={{width:'100%',padding:'6px 8px',borderRadius:6,border:`1px solid ${f.finFee>0?C.cyan:C.border}`,background:f.finFee>0?C.cyan+'08':C.bg,color:C.tx,fontSize:12,boxSizing:'border-box',outline:'none'}}/></div></div>
      <div style={{marginTop:10,display:'flex',gap:10,alignItems:'end'}}><Sel label="Tranches" value={String(f.tranches)} onChange={e=>u('tranches',+e.target.value)} options={['1','2']} placeholder={false} style={{width:80}}/>{+f.tranches>=2&&<><Inp label="1st Tranche (€)" type="number" value={f.t1} onChange={e=>u('t1',e.target.value)} style={{width:130}}/><div><label style={{fontSize:10,color:C.mt,display:'block',marginBottom:2}}>2nd Tranche</label><div style={{padding:'6px 8px',borderRadius:6,border:`1px solid ${C.border}`,background:C.border+'30',color:C.mt,fontSize:12}}>{fmt(t2V)}</div></div></>}</div>
      <div style={{marginTop:14,fontSize:10,color:C.mt,textTransform:'uppercase',marginBottom:6}}>Estimated Operational Costs</div>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr 1fr',gap:10}}><Inp label="Transport (€)" type="number" value={f.transport} onChange={e=>u('transport',+e.target.value)}/><Inp label="Gestoría Total" type="number" value={f.gestoria} onChange={e=>u('gestoria',+e.target.value)}/><Inp label="ITV (€)" type="number" value={f.itv} onChange={e=>u('itv',+e.target.value)}/><Inp label="CEVA (€)" type="number" value={f.ceva} onChange={e=>u('ceva',+e.target.value)}/><Inp label="Doc. adicional (COC)" type="number" value={f.doc} onChange={e=>u('doc',+e.target.value)}/><Inp label="Otras reparaciones" type="number" value={f.orep} onChange={e=>u('orep',+e.target.value)}/><Inp label="Other Costs" type="number" value={f.otherCosts} onChange={e=>u('otherCosts',+e.target.value)}/></div>
      <div style={{marginTop:14,padding:12,background:C.bg,borderRadius:8,border:`1px solid ${C.border}`}}><div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr 1fr 1fr 1fr',gap:8,fontSize:11}}><div><span style={{color:C.mt}}>Total Ops</span><div style={{fontWeight:700}}>{fmt(ops)}</div></div><div><span style={{color:C.mt}}>Op Margin</span><div style={{fontWeight:700,color:om>=0?C.green:C.red}}>{fmt(om)}</div></div><div><span style={{color:C.mt}}>VAT</span><div style={{fontWeight:500}}>{fmtD(vat)}</div></div><div><span style={{color:C.mt}}>Margin IT</span><div style={{fontWeight:700,color:mit>=0?C.green:C.red}}>{fmtD(mit)}</div></div><div><span style={{color:C.cyan}}>MIT Total</span><div style={{fontWeight:700,color:(mit+finFee)>=0?C.green:C.red}}>{fmtD(mit+finFee)}</div></div><div><span style={{color:C.mt}}>BD Commission</span><div style={{fontWeight:700,color:C.ac}}>{fmtD(ct)}</div></div></div>{finFee>0&&<div style={{marginTop:6,fontSize:10,color:C.cyan}}>Financing: {fmtD(finFee)} · MIT Total = MIT ({fmtD(mit)}) + Financing ({fmtD(finFee)})</div>}{isREBU&&<div style={{marginTop:4,fontSize:10,color:C.cyan}}>REBU: VAT on trade margin ({fmt((+f.rebuSell||0)-(+f.rebuBuy||0))})</div>}{f.isMulti&&+f.b2bLines>1&&<div style={{marginTop:4,fontSize:10,color:C.purple}}>Creates {f.b2bLines} lines ID #{nid}</div>}</div>
      <button onClick={create} disabled={!f.name||!f.owner||!fee} style={{marginTop:14,padding:'8px 22px',borderRadius:8,border:'none',background:C.ac,color:'#fff',fontWeight:600,fontSize:13,cursor:!f.name||!f.owner||!fee?'default':'pointer',opacity:!f.name||!f.owner||!fee?0.4:1}}>Create Deal{f.isMulti&&+f.b2bLines>1?` (${f.b2bLines} lines)`:''}</button>
    </div></div>);
}

// ══════════════════════════════════════════
//  BD KPI CARDS (sub-component, no IIFEs)
// ══════════════════════════════════════════
function BDKpiCards({totals,month,year}){
  const today=new Date();
  const isCurrentMonth=month===MONTHS[today.getMonth()]&&+year===today.getFullYear();
  const dayOfMonth=isCurrentMonth?today.getDate():new Date(+year,MONTHS.indexOf(month)+1,0).getDate();
  const daysInMonth=new Date(+year,MONTHS.indexOf(month)+1,0).getDate();
  const runRate=dayOfMonth>0?((totals.mit||0)/dayOfMonth)*daysInMonth:0;
  const runRatePct=(totals.goal||0)>0?runRate/totals.goal:0;
  const achievePct=(totals.goal||0)>0?(totals.mit||0)/totals.goal:0;
  const mitColor=(totals.goal||0)>0?(achievePct>=1?C.green:C.red):((totals.mit||0)>=0?C.green:C.red);
  const cards=[
    {l:'€ Goal',v:fmt(Math.round(totals.goal||0)),c:C.tx},
    {l:'€ MIT Closed',v:fmt(Math.round(totals.mit||0)),c:mitColor},
    {l:'% Achievement',v:`${Math.round(achievePct*100)}%`,c:achievePct>=1?C.green:C.amber},
    {l:'€ MIT Run Rate',v:fmt(Math.round(runRate)),c:C.cyan,s:isCurrentMonth?`Day ${dayOfMonth}/${daysInMonth}`:'Full month'},
    {l:'% Run Rate',v:`${Math.round(runRatePct*100)}%`,c:runRatePct>=1?C.green:C.amber},
    {l:'# Cars',v:totals.deals||0,c:C.tx},
  ];
  return(<div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))',gap:8,marginBottom:14}}>
    {cards.map((k,i)=>(<div key={i} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:8,padding:'10px 12px'}}>
      <div style={{fontSize:9,color:C.mt,textTransform:'uppercase',letterSpacing:'.05em',marginBottom:3}}>{k.l}</div>
      <div style={{fontSize:17,fontWeight:700,color:k.c||C.tx}}>{k.v}</div>
      {k.s&&<div style={{fontSize:9,color:C.mt,marginTop:2}}>{k.s}</div>}
    </div>))}
  </div>);
}

// ══════════════════════════════════════════
//  BD PERFORMANCE (M1)
// ══════════════════════════════════════════
function BDPerf({clients,bankTxs,onViewDeal}){
  const now=new Date();
  const [selBD,setSelBD]=useState(null);
  const [month,setMonth]=useState(MONTHS[now.getMonth()]);
  const [year,setYear]=useState(now.getFullYear());
  const [targets,setTargets]=useState([]);
  const [ready,setReady]=useState(false);
  const [addingBD,setAddingBD]=useState(false);

  useEffect(()=>{
    loadS('ir_bd_targets_v1',[]).then(v=>{
      setTargets(Array.isArray(v)?v:[]);
      setReady(true);
    });
  },[]);

  useEffect(()=>{
    if(ready) saveS('ir_bd_targets_v1',targets);
  },[targets,ready]);

  const setGoal=(bd,val)=>{
    const goal=val==='' ? '' : +val;
    setTargets(prev=>{
      const ix=prev.findIndex(t=>t.bd===bd&&t.month===month&&t.year===+year);
      if(ix>=0){
        const next=[...prev];
        next[ix]={...next[ix],goal};
        return next;
      }
      return [...prev,{bd,month,year:+year,goal}];
    });
  };

  const getGoal=(bd)=>{
    const row=targets.find(t=>t.bd===bd&&t.month===month&&t.year===+year);
    return row?.goal ?? '';
  };

  // Add a BD to this month's roster (creates a target entry with goal 0)
  const addBDToMonth=(bdName)=>{
    if(!bdName)return;
    const exists=targets.find(t=>t.bd===bdName&&t.month===month&&t.year===+year);
    if(!exists){setTargets(prev=>[...prev,{bd:bdName,month,year:+year,goal:0}]);}
    setAddingBD(false);
  };

  // Remove a BD from this month (remove target entry)
  const removeBDFromMonth=(bdName)=>{
    setTargets(prev=>prev.filter(t=>!(t.bd===bdName&&t.month===month&&t.year===+year)));
  };

  // Copy previous month's BD roster
  const copyRosterFromPrev=()=>{
    const mi=MONTHS.indexOf(month);
    const pm=mi>0?MONTHS[mi-1]:MONTHS[11];
    const py=mi>0?+year:+year-1;
    // Get BDs from previous month: from targets OR from deals
    const prevTargetBDs=targets.filter(t=>t.month===pm&&t.year===py);
    const prevDealBDs=[...new Set(clients.filter(c=>(c.m||c.month||'').toUpperCase()===pm&&+(c.y||c.year||0)===py).map(c=>c.ow||c.o).filter(Boolean))];
    // Merge: use target goal if exists, otherwise goal=0
    const allPrevBDs=new Map();
    prevTargetBDs.forEach(t=>allPrevBDs.set(t.bd,t.goal||0));
    prevDealBDs.forEach(bd=>{if(!allPrevBDs.has(bd))allPrevBDs.set(bd,0);});
    if(allPrevBDs.size===0){alert('No BDs found in '+pm+' '+py+'.');return;}
    const currentBDs=new Set(targets.filter(t=>t.month===month&&t.year===+year).map(t=>t.bd));
    const toAdd=[];
    allPrevBDs.forEach((goal,bd)=>{if(!currentBDs.has(bd))toAdd.push({bd,month,year:+year,goal});});
    if(toAdd.length>0){setTargets(prev=>[...prev,...toAdd]);alert(`Added ${toAdd.length} BDs to ${month} ${year}`);}
    else alert('All BDs from '+pm+' are already in this month');
  };

  const monthDeals=useMemo(
    ()=>clients.filter(c=>{const dm=(c.m||c.month||'').toUpperCase();return dm===month&&+(c.y||c.year||0)===+year;}),
    [clients,month,year]
  );

  const monthBank=useMemo(
    ()=>bankTxs.filter(t=>(t.month||'').toUpperCase()===month&&+(t.year||0)===+year),
    [bankTxs,month,year]
  );

  const data=useMemo(()=>{
    // Active BDs = those with a target this month OR deals this month
    const rosterBDs=targets.filter(t=>t.month===month&&t.year===+year).map(t=>t.bd);
    const dealBDs=monthDeals.map(c=>c.ow||c.o).filter(Boolean);
    const bdNames=[...new Set([...rosterBDs,...dealBDs])].filter(n=>n&&n!=='BD');

    const map={};
    bdNames.forEach(name=>{
      if(!name || name==='BD') return;
      map[name]={
        name,
        goal:+(getGoal(name)||0),
        deals:0,
        lines:[],
        tMIT:0,
        tComm:0,
        commPaid:0
      };
    });

    monthDeals.forEach(c=>{
      const bd=c.ow||c.o||'';
      if(!bd || bd==='BD') return;
      if(!map[bd]){
        map[bd]={name:bd,goal:0,deals:0,lines:[],tMIT:0,tComm:0,commPaid:0};
      }
      map[bd].deals++;
      map[bd].lines.push(c);
      map[bd].tMIT+=+(c.mit||0);
      const cf=calcFin(c);map[bd].tComm+=cf.commTotal;
    });

    monthBank.forEach(t=>{
      const supplier=t.supplier||'';
      const desc=(t.desc||'').toUpperCase();
      const costLine=(t.costLine||'').toUpperCase();
      const isCommission =
        costLine==='COMISSIONS' ||
        desc.includes('COMISS');

      if(isCommission && supplier && map[supplier]){
        map[supplier].commPaid+=Math.abs(+(t.amount||0));
      }
    });

    return Object.values(map)
      .map(b=>({
        ...b,
        due:(b.tComm||0)-(b.commPaid||0),
        pctGoal:b.goal>0 ? (b.tMIT||0)/b.goal : 0,
        avgMIT:b.deals>0 ? (b.tMIT||0)/b.deals : 0
      }))
      .sort((a,b)=>(b.tMIT||0)-(a.tMIT||0));
  },[clients,monthDeals,monthBank,targets,month,year]);

  const totals=useMemo(()=>{
    const goal=data.reduce((s,b)=>s+(+b.goal||0),0);
    const mit=data.reduce((s,b)=>s+(+b.tMIT||0),0);
    const comm=data.reduce((s,b)=>s+(+b.tComm||0),0);
    const paid=data.reduce((s,b)=>s+(+b.commPaid||0),0);
    const due=comm-paid;
    const deals=data.reduce((s,b)=>s+(+b.deals||0),0);
    return {
      goal,
      mit,
      comm,
      paid,
      due,
      deals,
      pctGoal:goal>0?mit/goal:0
    };
  },[data]);

  const yearOptions=useMemo(()=>{
    const ys=new Set([
      now.getFullYear()-1,
      now.getFullYear(),
      now.getFullYear()+1,
      ...clients.map(c=>+c.year).filter(Boolean),
      ...bankTxs.map(t=>+t.year).filter(Boolean),
      ...targets.map(t=>+t.year).filter(Boolean)
    ]);
    return [...ys].sort((a,b)=>b-a);
  },[clients,bankTxs,targets]);

  if(selBD){
    const bd=data.find(b=>b.name===selBD);
    if(!bd){
      setSelBD(null);
      return null;
    }

    const bdPayments=monthBank
      .filter(t=>{
        const supplier=t.supplier||'';
        const desc=(t.desc||'').toUpperCase();
        const costLine=(t.costLine||'').toUpperCase();
        return supplier===bd.name && (costLine==='COMISSIONS' || desc.includes('COMISS'));
      })
      .sort((a,b)=>new Date(b.date)-new Date(a.date));

    return(
      <div>
        <button
          onClick={()=>setSelBD(null)}
          style={{
            padding:'5px 12px',
            borderRadius:6,
            border:`1px solid ${C.border}`,
            background:'transparent',
            color:C.mt,
            cursor:'pointer',
            fontSize:11,
            marginBottom:12
          }}
        >
          ← Back
        </button>

        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14,flexWrap:'wrap',gap:10}}>
          <div>
            <div style={{fontSize:15,fontWeight:700}}>{bd.name}</div>
            <div style={{fontSize:11,color:C.mt,marginTop:2}}>{month} {year}</div>
          </div>
          <div style={{display:'flex',gap:8,alignItems:'center'}}>
            <div style={{fontSize:11,color:C.mt}}>Goal</div>
            <input
              type="number"
              value={getGoal(bd.name)}
              onChange={e=>setGoal(bd.name,e.target.value)}
              placeholder="0"
              style={{
                width:120,
                padding:'6px 8px',
                borderRadius:6,
                border:`1px solid ${C.border}`,
                background:C.bg,
                color:C.tx,
                fontSize:12,
                boxSizing:'border-box',
                outline:'none'
              }}
            />
          </div>
        </div>

        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))',gap:8,marginBottom:14}}>
          {[
            {l:'Goal',v:fmt(Math.round(bd.goal||0)),c:C.tx},
            {l:'Deals',v:bd.deals,c:C.tx},
            {l:'Margin IT',v:fmt(Math.round(bd.tMIT||0)),c:C.green},
            {l:'% Goal',v:`${Math.round((bd.pctGoal||0)*100)}%`,c:(bd.pctGoal||0)>=1?C.green:C.amber},
            {l:'Commission Earned',v:fmt(Math.round(bd.tComm||0)),c:C.ac},
            {l:'Commission Paid',v:fmt(Math.round(bd.commPaid||0)),c:C.green},
            {l:'Commission Due',v:fmt(Math.round(bd.due||0)),c:(bd.due||0)>0?C.amber:C.green},
            {l:'Avg MIT / Deal',v:fmt(Math.round(bd.avgMIT||0)),c:C.mt}
          ].map((k,i)=>(
            <div key={i} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:8,padding:'10px 12px'}}>
              <div style={{fontSize:9,color:C.mt,textTransform:'uppercase'}}>{k.l}</div>
              <div style={{fontSize:17,fontWeight:700,color:k.c||C.tx}}>{k.v}</div>
            </div>
          ))}
        </div>

        <div style={{display:'grid',gridTemplateColumns:'1.3fr .8fr',gap:14}}>
          <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,overflow:'hidden'}}>
            <div style={{padding:'10px 12px',borderBottom:`1px solid ${C.border}`,fontSize:12,fontWeight:600}}>Deals</div>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:11}}>
              <thead>
                <tr style={{borderBottom:`1px solid ${C.border}`}}>
                  {['ID','Client','Service','Margin IT','Commission','Status'].map(h=>(
                    <th key={h} style={{padding:'7px',textAlign:'left',color:C.mt,fontSize:9,textTransform:'uppercase'}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {bd.lines.length>0 ? bd.lines.map((c,i)=>(
                  <tr key={i} onClick={()=>{if(onViewDeal)onViewDeal(c.i);}} style={{borderBottom:`1px solid ${C.border}08`,cursor:'pointer'}} onMouseEnter={e=>e.currentTarget.style.background=C.border+'22'} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                    <td style={{padding:'5px 7px',color:C.ac}}>{c.i}</td>
                    <td style={{padding:'5px 7px',maxWidth:180,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{c.n}</td>
                    <td style={{padding:'5px 7px'}}><Badge t={c.sv||'—'} c={C.ac}/></td>
                    <td style={{padding:'5px 7px',textAlign:'right',color:(c.mit||0)>=0?C.green:C.red}}>{fmtD(c.mit||0)}</td>
                    <td style={{padding:'5px 7px',textAlign:'right',color:C.ac}}>{fmtD(calcFin(c).commTotal)}</td>
                    <td style={{padding:'5px 7px'}}><Badge t={c.st||'—'} c={c.st==='CLOSED'?C.green:C.amber}/></td>
                  </tr>
                )) : (
                  <tr><td colSpan={6} style={{padding:16,textAlign:'center',color:C.mt}}>No deals for this BD in {month} {year}</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,overflow:'hidden'}}>
            <div style={{padding:'10px 12px',borderBottom:`1px solid ${C.border}`,fontSize:12,fontWeight:600}}>Commission payments</div>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:11}}>
              <thead>
                <tr style={{borderBottom:`1px solid ${C.border}`}}>
                  {['Date','Description','Paid'].map(h=>(
                    <th key={h} style={{padding:'7px',textAlign:'left',color:C.mt,fontSize:9,textTransform:'uppercase'}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {bdPayments.length>0 ? bdPayments.map((t,i)=>(
                  <tr key={i} style={{borderBottom:`1px solid ${C.border}08`}}>
                    <td style={{padding:'5px 7px',whiteSpace:'nowrap',color:C.mt}}>{t.date}</td>
                    <td style={{padding:'5px 7px',maxWidth:180,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{t.desc||'—'}</td>
                    <td style={{padding:'5px 7px',textAlign:'right',color:C.green,fontWeight:600}}>{fmtD(Math.abs(t.amount||0))}</td>
                  </tr>
                )) : (
                  <tr><td colSpan={3} style={{padding:16,textAlign:'center',color:C.mt}}>No commission payments found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  return(
    <div>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14,flexWrap:'wrap',gap:10}}>
        <div>
          <div style={{fontSize:15,fontWeight:700}}>BD Performance & Commissions</div>
          <div style={{fontSize:11,color:C.mt,marginTop:2}}>Targets mensais, Margin IT fechada, comissões ganhas, pagas e em dívida</div>
        </div>

        <div style={{display:'flex',gap:8,alignItems:'end'}}>
          <Sel
            label="Month"
            value={month}
            onChange={e=>setMonth(e.target.value)}
            options={MONTHS}
            placeholder={false}
            style={{width:150}}
          />
          <Sel
            label="Year"
            value={year}
            onChange={e=>setYear(+e.target.value)}
            options={yearOptions}
            placeholder={false}
            style={{width:100}}
          />
        </div>
      </div>

      {/* BD Roster management */}
      <div style={{display:'flex',gap:6,marginBottom:12,alignItems:'center',flexWrap:'wrap'}}>
        <div style={{fontSize:10,color:C.mt}}>{data.length} BDs active in {month} {year}</div>
        <button onClick={()=>setAddingBD(!addingBD)} style={{padding:'4px 12px',borderRadius:5,border:`1px solid ${C.ac}`,background:'transparent',color:C.ac,fontSize:10,cursor:'pointer',fontWeight:600}}>+ Add BD</button>
        <button onClick={copyRosterFromPrev} style={{padding:'4px 12px',borderRadius:5,border:`1px solid ${C.border}`,background:'transparent',color:C.mt,fontSize:10,cursor:'pointer'}}>Copy from previous month</button>
      </div>
      {addingBD&&<div style={{display:'flex',gap:6,marginBottom:12,alignItems:'end'}}>
        <Sel label="Select BD" value="" onChange={e=>{if(e.target.value)addBDToMonth(e.target.value);}} options={(AUX.BD||[]).filter(bd=>!data.find(d=>d.name===bd))} placeholder="Choose..." style={{width:200}}/>
        <button onClick={()=>setAddingBD(false)} style={{padding:'5px 10px',borderRadius:5,border:`1px solid ${C.border}`,background:'transparent',color:C.mt,fontSize:10,cursor:'pointer'}}>Cancel</button>
      </div>}

      {/* Run rate and KPIs */}
      <BDKpiCards totals={totals} month={month} year={year}/>

      <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:16,marginBottom:14}}>
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke={C.border}/>
            <XAxis dataKey="name" tick={{fontSize:9,fill:C.mt}} angle={-30} textAnchor="end" height={60}/>
            <YAxis yAxisId="l" tick={{fontSize:9,fill:C.mt}}/>
            <YAxis yAxisId="r" orientation="right" tick={{fontSize:9,fill:C.mt}}/>
            <Tooltip contentStyle={{background:C.card,border:`1px solid ${C.border}`,borderRadius:8,fontSize:11}}/>
            <Bar yAxisId="l" dataKey="tMIT" name="Margin IT" fill={C.ac} radius={[3,3,0,0]}/>
            <Bar yAxisId="r" dataKey="tComm" name="Commission" fill={C.green} radius={[3,3,0,0]}/>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,overflow:'hidden'}}>
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
          <thead>
            <tr style={{borderBottom:`1px solid ${C.border}`}}>
              {['BD','Goal','MIT Closed','% Goal','Deals','Comm Earned','Paid','Due','Avg MIT',''].map(h=>(
                <th key={h} style={{padding:'8px',textAlign:'left',color:C.mt,fontSize:10,textTransform:'uppercase'}}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((b,i)=>(
              <tr
                key={i}
                onClick={()=>setSelBD(b.name)}
                style={{borderBottom:`1px solid ${C.border}10`,cursor:'pointer'}}
                onMouseEnter={e=>e.currentTarget.style.background=C.border+'22'}
                onMouseLeave={e=>e.currentTarget.style.background='transparent'}
              >
                <td style={{padding:'7px 8px',fontWeight:600}}>{b.name}</td>
                <td style={{padding:'7px 8px'}} onClick={e=>e.stopPropagation()}>
                  <input
                    type="number"
                    value={getGoal(b.name)}
                    onChange={e=>setGoal(b.name,e.target.value)}
                    placeholder="0"
                    style={{
                      width:100,
                      padding:'5px 7px',
                      borderRadius:6,
                      border:`1px solid ${C.border}`,
                      background:C.bg,
                      color:C.tx,
                      fontSize:11,
                      boxSizing:'border-box',
                      outline:'none'
                    }}
                  />
                </td>
                <td style={{padding:'7px 8px',color:(b.tMIT||0)>=0?C.green:C.red}}>{fmt(Math.round(b.tMIT||0))}</td>
                <td style={{padding:'7px 8px',fontWeight:600,color:(b.pctGoal||0)>=1?C.green:C.amber}}>
                  {b.goal>0 ? `${Math.round((b.pctGoal||0)*100)}%` : '—'}
                </td>
                <td style={{padding:'7px 8px'}}>{b.deals}</td>
                <td style={{padding:'7px 8px',color:C.ac}}>{fmt(Math.round(b.tComm||0))}</td>
                <td style={{padding:'7px 8px',color:C.green}}>{fmt(Math.round(b.commPaid||0))}</td>
                <td style={{padding:'7px 8px',fontWeight:600,color:(b.due||0)>10?C.amber:C.green}}>{fmt(Math.round(b.due||0))}</td>
                <td style={{padding:'7px 8px',color:C.mt}}>{b.deals>0?fmt(Math.round(b.avgMIT||0)):'—'}</td>
                <td style={{padding:'7px 8px'}} onClick={e=>{e.stopPropagation();if(b.deals===0)removeBDFromMonth(b.name);}}>{b.deals===0&&<span style={{fontSize:9,color:C.red,cursor:'pointer'}}>✕</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {data.length===0 && (
          <div style={{padding:30,textAlign:'center',color:C.mt,fontSize:12}}>
            No BD data for {month} {year}
          </div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════
//  MODULE 4: DASHBOARD
// ══════════════════════════════════════════
const IRC_RATE=0.19;

function Dashboard({clients,bankTxs}){
  const now=new Date();
  const [month,setMonth]=useState(MONTHS[now.getMonth()]);
  const [year,setYear]=useState(now.getFullYear());
  const [goals,setGoals]=useState([]);
  const [ready,setReady]=useState(false);
  const [b2cOnly,setB2cOnly]=useState(false);
  const [b2bOnly,setB2bOnly]=useState(false);

  useEffect(()=>{loadS('ir_dash_goals_v1',[]).then(v=>{setGoals(Array.isArray(v)?v:[]);setReady(true);});},[]);
  useEffect(()=>{if(ready)saveS('ir_dash_goals_v1',goals);},[goals,ready]);

  const setGoal=(metric,val)=>{
    setGoals(prev=>{
      const ix=prev.findIndex(r=>r.metric===metric&&r.month===month&&r.year===+year);
      if(ix>=0){const n=[...prev];n[ix]={...n[ix],goal:val};return n;}
      return[...prev,{metric,month,year:+year,goal:val}];
    });
  };
  const getGoal=(metric)=>{const r=goals.find(g=>g.metric===metric&&g.month===month&&g.year===+year);return r?.goal??'';};

  // Monthly deals = deals closed in this month (optionally B2C only)
  const mDeals=useMemo(()=>clients.filter(c=>{
    const dm=(c.m||c.month||'').toUpperCase();
    const dy=+(c.y||c.year||0);
    if(dm!==month||dy!==+year)return false;
    if(b2cOnly&&c.b2===0)return false;
    if(b2bOnly&&c.b2===1)return false;
    return true;
  }),[clients,month,year,b2cOnly,b2bOnly]);

  // Monthly bank = transactions PAID in this month
  const mBank=useMemo(()=>bankTxs.filter(t=>{
    const tm=(t.month||'').toUpperCase();
    const ty=+(t.year||0);
    return tm===month&&ty===+year&&t.pagado==='Yes';
  }),[bankTxs,month,year]);

  // General costs = bank General txs paid this month
  const mGeneral=useMemo(()=>mBank.filter(t=>t.classification==='General'),[mBank]);
  const mOps=useMemo(()=>mBank.filter(t=>t.classification==='Operations'),[mBank]);

  // Metrics
  const metrics=useMemo(()=>{
    const deals=mDeals.length;
    const feeCVAT=mDeals.reduce((s,c)=>s+(c.ar||0),0);
    const feeSVAT=mDeals.reduce((s,c)=>{const f=calcFin(c);return s+(f.mit+f.vat)||0;},0); // simplified: Fee s/VAT ≈ Fee / 1.21... actually from Excel it's Fee s/VAT from CLIENTS.AO
    // Better: Fee s/VAT = sum of (fee * 100/121) for each deal
    const feeSVATCalc=mDeals.reduce((s,c)=>s+((c.ar||0)*100/121),0);
    const eurPerDeal=deals>0?feeSVATCalc/deals:0;
    const mitTotal=mDeals.reduce((s,c)=>s+(c.mit||0),0);
    const finTotal=mDeals.reduce((s,c)=>s+(c.finFee||0),0);
    const mitGrandTotal=mitTotal+finTotal; // Margin IT Total = Margin IT + Financing
    const commTotal=mDeals.reduce((s,c)=>s+calcFin(c).commTotal,0);

    // General costs paid this month (amounts are negative for outflows)
    const generalCVAT=mGeneral.reduce((s,t)=>s+Math.abs(t.amount||0),0);
    const generalSVAT=mGeneral.reduce((s,t)=>{
      const amt=Math.abs(t.amount||0);
      if(t.hasIVA==='Yes'){const rate=+(t.ivaRate||21)/100;return s+amt/(1+rate);}
      return s+amt;
    },0);
    const generalVAT=generalCVAT-generalSVAT;

    // Ops costs from deals (sum of total ops)
    const opsCosts=mDeals.reduce((s,c)=>s+(c.to||0),0);

    // PT costs for ES (monthly input from goals storage)
    const ptCosts=+(getGoal('ptCosts'))||0;

    // Exclude intracompany from general costs
    const intraAmt=mGeneral.filter(t=>false).reduce((s,t)=>s+Math.abs(t.amount||0),0); // intracompany handled via classification

    // EBITDA = Margin IT Total - General costs s/VAT + PT costs for ES
    const ebitda=mitGrandTotal-generalSVAT+ptCosts;
    const ebitdaPct=mitGrandTotal>0?ebitda/mitGrandTotal:0;
    const irc=ebitda>0?ebitda*IRC_RATE:0;
    const lucro=ebitda-irc;

    // General costs breakdown by cost line
    const costByLine={};
    mGeneral.forEach(t=>{
      const line=t.costLine||t.costType||'Other';
      if(!costByLine[line])costByLine[line]=0;
      costByLine[line]+=Math.abs(t.amount||0);
    });

    // B2C vs B2B
    const b2c=mDeals.filter(c=>c.b2===1);
    const b2b=mDeals.filter(c=>c.b2===0);

    return{deals,feeCVAT,feeSVAT:feeSVATCalc,eurPerDeal,mitTotal,finTotal,mitGrandTotal,commTotal,generalCVAT,generalSVAT,generalVAT,opsCosts,ptCosts,ebitda,ebitdaPct,irc,lucro,costByLine,b2cN:b2c.length,b2bN:b2b.length,b2cMIT:b2c.reduce((s,c)=>s+(c.mit||0),0),b2bMIT:b2b.reduce((s,c)=>s+(c.mit||0),0)};
  },[mDeals,mGeneral,goals,month,year]);

  // Previous month for growth
  const prevMonth=useMemo(()=>{
    const mi=MONTHS.indexOf(month);
    const pm=mi>0?MONTHS[mi-1]:MONTHS[11];
    const py=mi>0?+year:+year-1;
    const pDeals=clients.filter(c=>(c.m||c.month||'').toUpperCase()===pm&&+(c.y||c.year||0)===py);
    const pFee=pDeals.reduce((s,c)=>s+((c.ar||0)*100/121),0);
    const pGenS=bankTxs.filter(t=>(t.month||'').toUpperCase()===pm&&+(t.year||0)===py&&t.pagado==='Yes'&&t.classification==='General')
      .reduce((s,t)=>{const a=Math.abs(t.amount||0);if(t.hasIVA==='Yes'){const r=+(t.ivaRate||21)/100;return s+a/(1+r);}return s+a;},0);
    return{deals:pDeals.length,fee:pFee,genS:pGenS};
  },[clients,bankTxs,month,year]);

  const growth=(curr,prev)=>prev>0?((curr-prev)/prev):null;
  const fmtPct=v=>v==null?'—':`${v>=0?'+':''}${Math.round(v*100)}%`;

  // Chart data — last 6 months
  const chartData=useMemo(()=>{
    const mi=MONTHS.indexOf(month);
    const result=[];
    for(let i=5;i>=0;i--){
      let cm=mi-i,cy=+year;
      if(cm<0){cm+=12;cy-=1;}
      const mo=MONTHS[cm];
      const md=clients.filter(c=>(c.m||c.month||'').toUpperCase()===mo&&+(c.y||c.year||0)===cy&&(!b2cOnly||c.b2!==0)&&(!b2bOnly||c.b2!==1));
      const mit=md.reduce((s,c)=>s+(c.mit||0),0);
      const fin=md.reduce((s,c)=>s+(c.finFee||0),0);
      const mitT=mit+fin;
      const gen=bankTxs.filter(t=>(t.month||'').toUpperCase()===mo&&+(t.year||0)===cy&&t.pagado==='Yes'&&t.classification==='General')
        .reduce((s,t)=>{const a=Math.abs(t.amount||0);if(t.hasIVA==='Yes'){const r=+(t.ivaRate||21)/100;return s+a/(1+r);}return s+a;},0);
      result.push({name:mo.slice(0,3),mit:Math.round(mit),fin:Math.round(fin),mitTotal:Math.round(mitT),gen:Math.round(gen),ebitda:Math.round(mitT-gen),deals:md.length});
    }
    return result;
  },[clients,bankTxs,month,year,b2cOnly,b2bOnly]);

  const yearOptions=useMemo(()=>{
    const ys=new Set([now.getFullYear()-1,now.getFullYear(),now.getFullYear()+1,...clients.map(c=>+(c.y||c.year)).filter(Boolean)]);
    return[...ys].sort((a,b)=>b-a);
  },[clients]);

  const M=metrics;
  const GoalInput=({metric})=>{
    const g=getGoal(metric);
    const [localVal,setLocalVal]=useState(String(g));
    const [focused,setFocused]=useState(false);
    useEffect(()=>{if(!focused)setLocalVal(String(g));},[g,focused]);
    return(<div style={{display:'flex',alignItems:'center',gap:4}}>
      <span style={{fontSize:9,color:C.mt,minWidth:30}}>Goal</span>
      <input value={focused?localVal:g} 
        onFocus={()=>{setFocused(true);setLocalVal(String(g));}}
        onChange={e=>{const v=e.target.value.replace(/[^0-9.-]/g,'');setLocalVal(v);}}
        onBlur={()=>{setFocused(false);setGoal(metric,localVal);}}
        placeholder="—"
        style={{width:80,padding:'3px 6px',borderRadius:5,border:`1px solid ${C.border}`,background:C.bg,color:C.tx,fontSize:10,boxSizing:'border-box',outline:'none',textAlign:'right'}}/>
    </div>);
  };
  const pctOfGoal=(actual,metric)=>{const g=+getGoal(metric);return g>0?actual/g:null;};
  const GoalBar=({actual,metric,color})=>{const pct=pctOfGoal(actual,metric);if(pct==null)return null;const w=Math.min(pct*100,100);return(<div style={{marginTop:4,height:4,borderRadius:2,background:C.border,overflow:'hidden'}}><div style={{height:'100%',width:`${w}%`,borderRadius:2,background:pct>=1?C.green:color||C.ac,transition:'width .3s'}}/></div>);};

  return(
    <div>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14,flexWrap:'wrap',gap:10}}>
        <div><div style={{fontSize:15,fontWeight:700}}>Dashboard</div><div style={{fontSize:11,color:C.mt,marginTop:2}}>Monthly P&L — deals closed + general costs paid in period</div></div>
        <div style={{display:'flex',gap:8,alignItems:'end'}}>
          <div style={{display:'flex',gap:3}}>{['All','B2C','B2B'].map(v=>{const active=(v==='All'&&!b2cOnly&&!b2bOnly)||(v==='B2C'&&b2cOnly)||(v==='B2B'&&b2bOnly);return(<button key={v} onClick={()=>{if(v==='All'){setB2cOnly(false);setB2bOnly(false);}else if(v==='B2C'){setB2cOnly(true);setB2bOnly(false);}else{setB2cOnly(false);setB2bOnly(true);}}} style={{padding:'5px 10px',borderRadius:5,fontSize:10,fontWeight:active?600:400,cursor:'pointer',border:`1px solid ${active?C.ac:C.border}`,background:active?C.ac+'15':'transparent',color:active?C.ac:C.mt}}>{v}</button>);})}</div>
          <div><label style={{fontSize:9,color:C.cyan,display:'block',marginBottom:1}}>ES paid by PT (+€)</label><input value={getGoal('ptCosts')||''} 
        onFocus={e=>e.target.dataset.editing='1'}
        onChange={e=>{e.target.value=e.target.value.replace(/[^0-9.-]/g,'');setGoal('ptCosts',e.target.value);}}
        placeholder="0" style={{width:90,padding:'4px 7px',borderRadius:5,border:`1px solid ${C.cyan}40`,background:C.bg,color:C.cyan,fontSize:11,fontWeight:600,boxSizing:'border-box',outline:'none',textAlign:'right'}}/></div>
          <Sel label="Month" value={month} onChange={e=>setMonth(e.target.value)} options={MONTHS} placeholder={false} style={{width:140}}/>
          <Sel label="Year" value={year} onChange={e=>setYear(+e.target.value)} options={yearOptions} placeholder={false} style={{width:90}}/>
        </div>
      </div>

      {/* Top KPIs row */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(145px,1fr))',gap:8,marginBottom:14}}>
        {[
          {l:'Margin IT',v:fmt(Math.round(M.mitTotal)),c:C.ac,m:'mit'},
          {l:'Financing',v:fmt(Math.round(M.finTotal)),c:C.cyan,m:'fin'},
          {l:'Margin IT Total',v:fmt(Math.round(M.mitGrandTotal)),c:M.mitGrandTotal>=0?C.green:C.red,m:'mitTotal'},
          {l:'EBITDA',v:fmt(Math.round(M.ebitda)),c:M.ebitda>=0?C.green:C.red,m:'ebitda',sub:`${Math.round(M.ebitdaPct*100)}% margin`},
          {l:'Lucro (post IRC)',v:fmt(Math.round(M.lucro)),c:M.lucro>=0?C.green:C.red,m:'lucro'},
          {l:'Deals',v:M.deals,c:C.tx,m:'deals',sub:`${M.b2cN} B2C · ${M.b2bN} B2B`},
          {l:'General Costs',v:fmt(Math.round(M.generalSVAT)),c:C.red,m:'general',sub:fmtPct(growth(M.generalSVAT,prevMonth.genS))+' MoM'},
        ].map((k,i)=>(
          <div key={i} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:8,padding:'10px 12px'}}>
            <div style={{fontSize:9,color:C.mt,textTransform:'uppercase',letterSpacing:'.05em',marginBottom:3}}>{k.l}</div>
            <div style={{fontSize:18,fontWeight:700,color:k.c||C.tx}}>{k.v}</div>
            {k.sub&&<div style={{fontSize:9,color:C.mt,marginTop:2}}>{k.sub}</div>}
            {k.m&&<GoalInput metric={k.m}/>}
            {k.m&&<GoalBar actual={k.l==='General Costs'?M.generalSVAT:k.l==='EBITDA'?M.ebitda:k.l==='Lucro (post IRC)'?M.lucro:k.l==='Margin IT'?M.mitTotal:k.l==='Financing'?M.finTotal:k.l==='Margin IT Total'?M.mitGrandTotal:M.deals} metric={k.m} color={k.c}/>}
          </div>
        ))}
      </div>

      {/* Charts row */}
      <div style={{display:'grid',gridTemplateColumns:'1.5fr 1fr',gap:12,marginBottom:14}}>
        <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:16}}>
          <div style={{fontSize:12,fontWeight:600,marginBottom:8}}>EBITDA Trend (6 months)</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border}/>
              <XAxis dataKey="name" tick={{fontSize:10,fill:C.mt}}/>
              <YAxis tick={{fontSize:9,fill:C.mt}}/>
              <Tooltip contentStyle={{background:C.card,border:`1px solid ${C.border}`,borderRadius:8,fontSize:11}}/>
              <Bar dataKey="mit" name="Margin IT" fill={C.ac} radius={[3,3,0,0]}/>
              <Bar dataKey="fin" name="Financing" fill={C.cyan} radius={[3,3,0,0]}/>
              <Bar dataKey="gen" name="General Costs" fill={C.red+'80'} radius={[3,3,0,0]}/>
              <Bar dataKey="ebitda" name="EBITDA" fill={C.green} radius={[3,3,0,0]}/>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:16}}>
          <div style={{fontSize:12,fontWeight:600,marginBottom:8}}>General Costs Breakdown</div>
          {Object.entries(M.costByLine).sort((a,b)=>b[1]-a[1]).map(([line,val])=>{
            const pct=M.generalCVAT>0?val/M.generalCVAT:0;
            return(<div key={line} style={{marginBottom:6}}>
              <div style={{display:'flex',justifyContent:'space-between',fontSize:11,marginBottom:2}}>
                <span style={{color:C.tx}}>{line}</span>
                <span style={{color:C.mt}}>{fmt(Math.round(val))} ({Math.round(pct*100)}%)</span>
              </div>
              <div style={{height:5,borderRadius:3,background:C.border,overflow:'hidden'}}>
                <div style={{height:'100%',width:`${pct*100}%`,borderRadius:3,background:C.purple}}/>
              </div>
            </div>);
          })}
          {Object.keys(M.costByLine).length===0&&<div style={{padding:20,textAlign:'center',color:C.mt,fontSize:11}}>No general costs this month</div>}
        </div>
      </div>

      {/* Monthly P&L table */}
      <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:16}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}><div style={{fontSize:12,fontWeight:600}}>Monthly P&L Summary</div><div style={{display:'flex',gap:4}}>{['All','B2C','B2B'].map(v=>{const active=(v==='All'&&!b2cOnly&&!b2bOnly)||(v==='B2C'&&b2cOnly)||(v==='B2B'&&b2bOnly);return(<button key={v} onClick={()=>{if(v==='All'){setB2cOnly(false);setB2bOnly(false);}else if(v==='B2C'){setB2cOnly(true);setB2bOnly(false);}else{setB2cOnly(false);setB2bOnly(true);}}} style={{padding:'4px 12px',borderRadius:5,fontSize:10,fontWeight:active?600:400,cursor:'pointer',border:`1px solid ${active?C.ac:C.border}`,background:active?C.ac+'15':'transparent',color:active?C.ac:C.mt}}>{v}</button>);})}</div></div>
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
          <tbody>
            {[
              {l:'Margin',items:[
                {l:'Margin IT',v:M.mitTotal,bold:true},
                {l:'Financing Revenue',v:M.finTotal,bold:false},
                {l:'Margin IT Total',v:M.mitGrandTotal,bold:true,highlight:true},
              ]},
              {l:'Revenue (ref)',items:[
                {l:'Fee c/VAT',v:M.feeCVAT},
                {l:'Fee s/VAT',v:M.feeSVAT},
                {l:'€ / Deal',v:M.eurPerDeal},
              ]},
              {l:'Costs',items:[
                {l:'Operational Costs (from deals)',v:-M.opsCosts},
                {l:'General Costs c/VAT',v:-M.generalCVAT},
                {l:'General Costs s/VAT',v:-M.generalSVAT,bold:true},
                {l:'General VAT',v:-M.generalVAT},
                {l:'ES costs paid by PT',v:M.ptCosts,bold:false,ptStyle:true},
              ]},
              {l:'Results',items:[
                {l:'EBITDA',v:M.ebitda,bold:true,highlight:true},
                {l:'EBITDA %',v:null,pct:M.ebitdaPct},
                {l:'IRC (19%)',v:-M.irc},
                {l:'Lucro',v:M.lucro,bold:true,highlight:true},
              ]},
              {l:'Mix',items:[
                {l:'Deals',v:null,n:M.deals},
                {l:'B2C MIT',v:M.b2cMIT},{l:'B2B MIT',v:M.b2bMIT},
                {l:'BD Commissions',v:-M.commTotal},
              ]},
            ].map(section=>[
              <tr key={section.l+'h'}><td colSpan={3} style={{padding:'10px 0 4px',fontSize:10,color:C.mt,textTransform:'uppercase',letterSpacing:'.05em',borderBottom:`1px solid ${C.border}`}}>{section.l}</td></tr>,
              ...section.items.map(item=>(
                <tr key={item.l} style={{borderBottom:`1px solid ${C.border}08`}}>
                  <td style={{padding:'5px 8px',color:item.bold?C.tx:C.mt,fontWeight:item.bold?600:400,paddingLeft:item.bold?8:20}}>{item.l}</td>
                  <td style={{padding:'5px 8px',textAlign:'right',fontWeight:item.bold?700:500,color:item.ptStyle?C.cyan:item.highlight?(item.v>=0?C.green:C.red):C.tx}}>
                    {item.pct!=null?`${Math.round(item.pct*100)}%`:item.n!=null?item.n:fmtD(item.v)}
                  </td>
                  <td style={{padding:'5px 8px',textAlign:'right',fontSize:10,color:C.mt}}>
                    {item.bold&&getGoal(item.l==='EBITDA'?'ebitda':item.l==='Lucro'?'lucro':item.l==='Fee s/VAT'?'fee':'')?`Goal: ${fmt(+getGoal(item.l==='EBITDA'?'ebitda':item.l==='Lucro'?'lucro':item.l==='Fee s/VAT'?'fee':''))}`:''}
                  </td>
                </tr>
              ))
            ]).flat()}
          </tbody>
        </table>
      </div>

      {/* ── 12-MONTH OVERVIEW ── */}
      <MonthlyOverview clients={clients} bankTxs={bankTxs} year={+year} goals={goals} b2cOnly={b2cOnly} b2bOnly={b2bOnly}/>
    </div>
  );
}

// ══════════════════════════════════════════
//  12-MONTH OVERVIEW (sub-component)
// ══════════════════════════════════════════
function calcMonthMetrics(clients,bankTxs,mo,yr,b2cOnly,b2bOnly,goals){
  const mDeals=clients.filter(c=>(c.m||c.month||'').toUpperCase()===mo&&+(c.y||c.year||0)===yr&&(!b2cOnly||c.b2!==0)&&(!b2bOnly||c.b2!==1));
  const mGen=bankTxs.filter(t=>(t.month||'').toUpperCase()===mo&&+(t.year||0)===yr&&t.pagado==='Yes'&&t.classification==='General');
  const deals=mDeals.length;
  const feeSVAT=mDeals.reduce((s,c)=>s+((c.ar||0)*100/121),0);
  const mitTotal=mDeals.reduce((s,c)=>s+(c.mit||0),0);
  const finTotal=mDeals.reduce((s,c)=>s+(c.finFee||0),0);
  const mitGrand=mitTotal+finTotal;
  const genSVAT=mGen.reduce((s,t)=>{const a=Math.abs(t.amount||0);if(t.hasIVA==='Yes'){const r=+(t.ivaRate||21)/100;return s+a/(1+r);}return s+a;},0);
  const ptRow=(goals||[]).find(g=>g.metric==='ptCosts'&&g.month===mo&&g.year===yr);
  const ptCosts=+(ptRow?.goal||0);
  const ebitda=mitGrand-genSVAT+ptCosts;
  const irc=ebitda>0?ebitda*IRC_RATE:0;
  const lucro=ebitda-irc;
  const eurPerDeal=deals>0?mitGrand/deals:0;
  const commTotal=mDeals.reduce((s,c)=>s+calcFin(c).commTotal,0);
  return{mo:mo.slice(0,3),deals,feeSVAT,mitTotal,finTotal,mitGrand,genSVAT,ptCosts,ebitda,ebitdaPct:mitGrand>0?ebitda/mitGrand:0,irc,lucro,eurPerDeal,commTotal};
}

function MonthlyOverview({clients,bankTxs,year,goals,b2cOnly,b2bOnly}){
  const months12=useMemo(()=>{
    const result=[];
    for(let i=0;i<12;i++){result.push(calcMonthMetrics(clients,bankTxs,MONTHS[i],year,b2cOnly,b2bOnly,goals));}
    // YTD totals
    const ytd={mo:'YTD',deals:0,feeSVAT:0,mitTotal:0,finTotal:0,mitGrand:0,genSVAT:0,ptCosts:0,ebitda:0,irc:0,lucro:0,eurPerDeal:0,commTotal:0};
    result.forEach(m=>{ytd.deals+=m.deals;ytd.feeSVAT+=m.feeSVAT;ytd.mitTotal+=m.mitTotal;ytd.finTotal+=m.finTotal;ytd.mitGrand+=m.mitGrand;ytd.genSVAT+=m.genSVAT;ytd.ptCosts+=m.ptCosts;ytd.ebitda+=m.ebitda;ytd.irc+=m.irc;ytd.lucro+=m.lucro;ytd.commTotal+=m.commTotal;});
    ytd.eurPerDeal=ytd.deals>0?ytd.mitGrand/ytd.deals:0;
    ytd.ebitdaPct=ytd.mitGrand>0?ytd.ebitda/ytd.mitGrand:0;
    return{months:result,ytd};
  },[clients,bankTxs,year,goals,b2cOnly,b2bOnly]);

  const rows=[
    {l:'Deals',k:'deals',fmt:v=>v||'—',bold:false},
    {l:'Margin IT',k:'mitTotal',fmt:v=>fmt(Math.round(v)),bold:true,hasGoal:true,goalKey:'mit'},
    {l:'Financing',k:'finTotal',fmt:v=>v>0?fmt(Math.round(v)):'—',bold:false,hasGoal:true,goalKey:'fin'},
    {l:'Margin IT Total',k:'mitGrand',fmt:v=>fmt(Math.round(v)),bold:true,color:true,hasGoal:true,goalKey:'mitTotal'},
    {l:'General Costs',k:'genSVAT',fmt:v=>v>0?'-'+fmt(Math.round(v)):'—',bold:false,neg:true},
    {l:'ES costs paid by PT',k:'ptCosts',fmt:v=>v?fmt(Math.round(Math.abs(v))):'—',bold:false,ptStyle:true},
    {l:'EBITDA',k:'ebitda',fmt:v=>fmt(Math.round(v)),bold:true,color:true,hasGoal:true,goalKey:'ebitda'},
    {l:'EBITDA %',k:'ebitdaPct',fmt:v=>v?Math.round(v*100)+'%':'—',bold:false},
    {l:'IRC',k:'irc',fmt:v=>v>0?'-'+fmt(Math.round(v)):'—',bold:false},
    {l:'Lucro',k:'lucro',fmt:v=>fmt(Math.round(v)),bold:true,color:true},
    {l:'Commissions',k:'commTotal',fmt:v=>v>0?'-'+fmt(Math.round(v)):'—',bold:false},
  ];

  const all=[...months12.months,months12.ytd];

  return(
    <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:16,marginTop:14}}>
      <div style={{fontSize:12,fontWeight:600,marginBottom:10}}>12-Month Overview — {year}</div>
      <div style={{overflowX:'auto'}}>
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:10,minWidth:900}}>
          <thead>
            <tr style={{borderBottom:`1px solid ${C.border}`}}>
              <th style={{padding:'6px 8px',textAlign:'left',color:C.mt,fontWeight:500,fontSize:9,textTransform:'uppercase',position:'sticky',left:0,background:C.card,minWidth:90}}/>
              {all.map((m,i)=>(
                <th key={i} style={{padding:'6px 4px',textAlign:'right',color:i===12?C.ac:C.mt,fontWeight:i===12?700:500,fontSize:9,textTransform:'uppercase',minWidth:65}}>{m.mo}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(row=>{
              const getG=(mo)=>{const r=(goals||[]).find(g=>g.metric===row.goalKey&&g.month===MONTHS.find(m=>m.slice(0,3)===mo)&&g.year===year);return r?.goal||0;};
              const hasAnyGoal=row.hasGoal&&(goals||[]).some(g=>g.metric===row.goalKey&&g.year===year&&+g.goal>0);
              return[
                <tr key={row.l} style={{borderBottom:`1px solid ${C.border}08`}}>
                  <td style={{padding:'5px 8px',color:row.bold?C.tx:C.mt,fontWeight:row.bold?600:400,fontSize:10,position:'sticky',left:0,background:C.card,whiteSpace:'nowrap'}}>{row.l}</td>
                  {all.map((m,i)=>{const v=m[row.k]||0;const isYTD=i===12;return(
                    <td key={i} style={{padding:'5px 4px',textAlign:'right',fontWeight:row.bold||isYTD?700:400,color:row.color?(v>=0?C.green:C.red):row.neg?C.red:isYTD?C.ac:C.tx,fontSize:isYTD?11:10,borderLeft:isYTD?`2px solid ${C.border}`:'none'}}>{row.fmt(v)}</td>
                  );})}
                </tr>,
                hasAnyGoal&&<tr key={row.l+'_goal'} style={{borderBottom:`1px solid ${C.border}04`}}>
                  <td style={{padding:'2px 8px',fontSize:9,color:C.mt,fontStyle:'italic',position:'sticky',left:0,background:C.card}}>  goal</td>
                  {all.map((m,i)=>{const g=i<12?getG(m.mo):0;const isYTD=i===12;if(isYTD){const ytdG=MONTHS.reduce((s,mo)=>{const r=(goals||[]).find(x=>x.metric===row.goalKey&&x.month===mo&&x.year===year);return s+(+(r?.goal)||0);},0);return <td key={i} style={{padding:'2px 4px',textAlign:'right',fontSize:9,color:C.mt,fontStyle:'italic',borderLeft:`2px solid ${C.border}`}}>{ytdG>0?fmt(Math.round(ytdG)):'—'}</td>;}
                    return <td key={i} style={{padding:'2px 4px',textAlign:'right',fontSize:9,color:C.mt,fontStyle:'italic'}}>{g>0?fmt(Math.round(g)):'—'}</td>;})}
                </tr>,
                hasAnyGoal&&<tr key={row.l+'_var'} style={{borderBottom:`1px solid ${C.border}08`}}>
                  <td style={{padding:'2px 8px',fontSize:9,color:C.mt,fontStyle:'italic',position:'sticky',left:0,background:C.card}}>  vs budget</td>
                  {all.map((m,i)=>{const v=m[row.k]||0;const isYTD=i===12;
                    let g;if(isYTD){g=MONTHS.reduce((s,mo)=>{const r=(goals||[]).find(x=>x.metric===row.goalKey&&x.month===mo&&x.year===year);return s+(+(r?.goal)||0);},0);}else{g=getG(m.mo);}
                    if(!g||g===0)return <td key={i} style={{padding:'2px 4px',textAlign:'right',fontSize:9,color:C.mt,borderLeft:isYTD?`2px solid ${C.border}`:'none'}}>—</td>;
                    const diff=v-g;const pct=Math.round((diff/g)*100);
                    return <td key={i} style={{padding:'2px 4px',textAlign:'right',fontSize:9,fontWeight:600,color:row.neg?(diff<=0?C.green:C.red):(diff>=0?C.green:C.red),borderLeft:isYTD?`2px solid ${C.border}`:'none'}}>{diff>=0?'+':''}{pct}%</td>;})}
                </tr>
              ].filter(Boolean);
            }).flat()}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════
//  MODULE 6: INVOICE READER
// ══════════════════════════════════════════
const CEVA_TEMPLATES=[
  {id:'ceva_servicios',label:'CEVA — Servicios (Handling/Lavado/Movimientos)',supplier:'CEVA',costType:'CEVA',sheets:['servicios'],cols:{chassis:7,plate:8,version:9,service:11,units:12,unitPrice:13,total:14,date:15,invoiceSAP:18}},
  {id:'ceva_stock',label:'CEVA — Stock (Estancia)',supplier:'CEVA',costType:'CEVA',sheets:['stock'],cols:{chassis:7,plate:8,version:9,units:11,unitPrice:12,total:13,service:14,startDate:16,endDate:17,invoiceSAP:22}},
  {id:'ceva_traslado',label:'CEVA — Traslado Grua',supplier:'CEVA',costType:'TRANSPORTATION',sheets:['Sheet1',0],cols:{chassis:0,plate:null,version:null,brand:1,model:2,date:3,origin:4,dest:5,total:6,month:7}},
];

function InvoiceReader({clients,bankTxs,setBankTxs,setClients,aux}){
  const [tpl,setTpl]=useState(null);
  const [lines,setLines]=useState([]);
  const [imported,setImported]=useState([]);
  const [ready,setReady]=useState(false);
  const [bulkClass,setBulkClass]=useState('Operations');
  const [bulkSupplier,setBulkSupplier]=useState('CEVA');
  const [bulkCostType,setBulkCostType]=useState('CEVA');
  const [invoiceRef,setInvoiceRef]=useState('');
  const [invoiceDate,setInvoiceDate]=useState(new Date().toISOString().slice(0,10));
  const [payOnImport,setPayOnImport]=useState('No');
  const [expectedPayDate,setExpectedPayDate]=useState('');
  const [viewHistory,setViewHistory]=useState(false);
  const fileRef=useRef(null);
  const vinFileRef=useRef(null);
  const [vinLookup,setVinLookup]=useState([]);

  useEffect(()=>{loadS('ir_invoices_v1',[]).then(v=>{setImported(Array.isArray(v)?v:[]);setReady(true);});},[]);
  useEffect(()=>{if(ready)saveS('ir_invoices_v1',imported);},[imported,ready]);
  useEffect(()=>{loadS('ir_vin_map_v1',[]).then(v=>{if(Array.isArray(v)&&v.length>0)setVinLookup(v);});},[]);
  useEffect(()=>{if(vinLookup.length>0)saveS('ir_vin_map_v1',vinLookup);},[vinLookup]);

  // Upload VIN CSV or Excel
  const handleVinUpload=async(file)=>{
    try{
      let rows=[];
      const isExcel=file.name.match(/\.xlsx?$/i);
      if(isExcel){
        const XLSX=await import('xlsx');
        const buf=await file.arrayBuffer();
        const wb=XLSX.read(buf,{type:'array',cellDates:true});
        const ws=wb.Sheets[wb.SheetNames[0]];
        rows=XLSX.utils.sheet_to_json(ws,{defval:''});
      }else{
        const text=await file.text();
        const lns=text.split('\n').filter(r=>r.trim());if(lns.length<2)return;
        const parseCSV=(line)=>{const res=[];let cur='',inQ=false;for(const ch of line){if(ch==='"'){inQ=!inQ;}else if(ch===','&&!inQ){res.push(cur.trim());cur='';}else{cur+=ch;}}res.push(cur.trim());return res;};
        const headers=parseCSV(lns[0]);
        for(let i=1;i<lns.length;i++){const cols=parseCSV(lns[i]);const obj={};headers.forEach((h,j)=>{obj[h.replace(/"/g,'')]=cols[j]?cols[j].replace(/"/g,''):''});rows.push(obj);}
      }
      if(rows.length===0)return;
      const keys=Object.keys(rows[0]);
      const findCol=(...terms)=>keys.find(k=>terms.some(t=>k.toLowerCase().includes(t)))||'';
      const chassisCol=findCol('chassis','vin','bastidor');
      const nameCol=findCol('deal name','name','nombre');
      const idCol=findCol('id deal','deal id','id');
      if(!chassisCol){alert('No Chassis/VIN column found. Columns: '+keys.join(', '));return;}
      const map=[];
      for(const r of rows){
        const vin=String(r[chassisCol]||'').trim();
        const name=String(r[nameCol]||'').trim();
        const idStr=String(r[idCol]||'').trim();
        if(!vin||vin.length<5)continue;
        let dealId=+idStr||0;
        if(!dealId&&name){const m=name.match(/^(\d+)[_\s]/);if(m)dealId=+m[1];}
        if(dealId>0)map.push({vin:vin.toUpperCase(),dealId,dealName:name});
      }
      setVinLookup(map);
      alert('Loaded '+map.length+' VIN mappings from '+rows.length+' rows');
    }catch(err){console.error(err);alert('Error: '+err.message);}
  };

  // Find deal by chassis or plate
  const findDeal=(chassis,plate)=>{
    if(!chassis&&!plate)return null;
    if(chassis&&vinLookup.length>0){
      const ch=chassis.toUpperCase();
      const match=vinLookup.find(v=>v.vin===ch||ch.endsWith(v.vin.slice(-8))||v.vin.endsWith(ch.slice(-8)));
      if(match){
        const deal=clients.find(c=>c.i===match.dealId);
        if(deal)return{id:deal.i,name:deal.n,code:deal.c};
        return{id:match.dealId,name:match.dealName||'Deal #'+match.dealId,code:match.dealId+''};
      }
    }
    for(const c of clients){
      const cc=(c.c||'').toUpperCase();const cn=(c.n||'').toUpperCase();
      if(chassis){const ch=chassis.toUpperCase();if(cc.includes(ch.slice(-6)))return{id:c.i,name:c.n,code:c.c};}
      if(plate){const pl=plate.toUpperCase().replace(/\s/g,'');if(cc.includes(pl)||cn.includes(pl))return{id:c.i,name:c.n,code:c.c};}
    }
    return null;
  };

  // Check duplicates in: 1) imported history, 2) bank txs, 3) deal existing costs
  const checkDup=(chassis,service,amount,dealCode)=>{
    const fromHistory=imported.find(inv=>inv.chassis===chassis&&inv.service===service&&Math.abs((inv.amount||0)-amount)<0.01);
    if(fromHistory)return{source:'history',ref:fromHistory.invoiceSAP||'prev import'};
    const fromBank=bankTxs.find(t=>{
      const desc=(t.desc||'').toUpperCase();
      return desc.includes((chassis||'').slice(-6).toUpperCase())&&desc.includes((service||'').slice(0,10).toUpperCase())&&Math.abs(Math.abs(t.amount||0)-amount)<0.01;
    });
    if(fromBank)return{source:'bank',ref:'Bank tx '+fromBank.date};
    // Check if deal already has a similar cost in bank
    if(dealCode){
      const dealTxs=bankTxs.filter(t=>t.clientCode===dealCode&&t.classification==='Operations');
      const similar=dealTxs.find(t=>Math.abs(Math.abs(t.amount||0)-amount)<0.01);
      if(similar)return{source:'deal',ref:'Deal already has '+fmtD(Math.abs(similar.amount))+' ('+similar.desc.slice(0,25)+')'};
    }
    return null;
  };

  const handleFile=async(file)=>{
    if(!tpl)return;
    try{
      const XLSX=await import('xlsx');
      const buf=await file.arrayBuffer();
      const wb=XLSX.read(buf,{type:'array',cellDates:true});
      const template=CEVA_TEMPLATES.find(t=>t.id===tpl);if(!template)return;
      const parsed=[];
      let sheetName=wb.SheetNames[0];
      if(template.sheets){for(const s of template.sheets){if(typeof s==='string'&&wb.SheetNames.includes(s)){sheetName=s;break;}if(typeof s==='number'&&wb.SheetNames[s]){sheetName=wb.SheetNames[s];break;}}}
      const ws=wb.Sheets[sheetName];
      const json=XLSX.utils.sheet_to_json(ws,{header:1,defval:''});
      if(json.length<2)return;
      for(let i=1;i<json.length;i++){
        const row=json[i];if(!row||row.every(c=>c===''))continue;
        const tc=template.cols;
        const chassis=tc.chassis!=null?String(row[tc.chassis]||'').trim():'';
        const plate=tc.plate!=null?String(row[tc.plate]||'').trim():'';
        const version=tc.version!=null?String(row[tc.version]||'').trim():'';
        const service=tc.service!=null?String(row[tc.service]||'').trim():'';
        const units=tc.units!=null?+(row[tc.units])||0:1;
        const unitPrice=tc.unitPrice!=null?+(row[tc.unitPrice])||0:0;
        let total=tc.total!=null?+(row[tc.total])||0:0;
        if(!total&&units&&unitPrice)total=units*unitPrice;
        const invoiceSAP=tc.invoiceSAP!=null?String(row[tc.invoiceSAP]||'').trim():'';
        const brand=tc.brand!=null?String(row[tc.brand]||'').trim():'';
        const model=tc.model!=null?String(row[tc.model]||'').trim():'';
        if(!total&&!chassis)continue;
        const deal=findDeal(chassis,plate);
        const dup=checkDup(chassis,service,total,deal?deal.code:'');
        parsed.push({_id:Date.now()+Math.random(),chassis,plate,version:version||((brand+' '+model).trim()),service:service||(tpl==='ceva_traslado'?'Traslado Grua':''),units,unitPrice,total,invoiceSAP:invoiceSAP||invoiceRef,dealId:deal?String(deal.id):'',dealName:deal?deal.name:'',dealCode:deal?deal.code:'',classification:bulkClass,supplier:bulkSupplier,costType:bulkCostType,_dup:!!dup,_dupRef:dup?dup.source+': '+dup.ref:''});
      }
      setLines(parsed);
    }catch(err){console.error(err);}
  };

  const updateLine=(id,key,val)=>setLines(prev=>prev.map(l=>l._id===id?{...l,[key]:val}:l));

  const confirmImport=()=>{
    const toImport=lines.filter(l=>!l._dup&&l.dealCode);
    const isPaid=payOnImport==='Yes';
    setImported(prev=>[...prev,...toImport.map(l=>({chassis:l.chassis,service:l.service,amount:l.total,invoiceSAP:l.invoiceSAP||invoiceRef,dealId:l.dealId,date:invoiceDate}))]);
    const txs=toImport.map(l=>({id:Date.now()+Math.random(),date:invoiceDate,month:MONTHS[new Date(invoiceDate).getMonth()]||'',year:new Date(invoiceDate).getFullYear(),desc:l.supplier+' — '+l.service+' ['+(l.plate||l.chassis.slice(-6))+']',amount:-(l.total),classification:l.classification,pnl:'SPAIN',bank:'BANKINTER',clientCode:l.dealCode,supplier:l.supplier,costType:l.costType,costLine:'',businessLine:'IMPORTATION',hasIVA:'Yes',ivaRate:21,invoiceRef:l.invoiceSAP||invoiceRef,pagado:isPaid?'Yes':'No',expectedDate:isPaid?'':expectedPayDate,_expected:!isPaid}));
    if(txs.length>0){
      setBankTxs(prev=>[...txs,...prev]);
      txs.forEach(tx=>{if(tx.clientCode&&tx.classification==='Operations'){
        setClients(prev=>prev.map(c=>{if(c.c!==tx.clientCode)return c;const costMap={'CEVA':'ct','TRANSPORTATION':'tr','GESTORIA':'gt'};const ck=costMap[(tx.costType||'').toUpperCase()];if(!ck)return c;const u={...c,[ck]:(c[ck]||0)+Math.abs(tx.amount)};u[ck+'Real']=true;u.to=(u.tr||0)+(u.gt||0)+(u.ct||0)+(u.doc||0)+(u.orep||0)+(u.otherCosts||0);const f=calcFin(u);u.om=f.om;u.vat=f.vat;u.mit=f.mit;return u;}));
      }});
    }
    alert('Imported '+txs.length+' transactions'+(isPaid?' as Paid':' as Unpaid'));
    setLines([]);
  };

  // Clear history AND revert bank/deal changes
  const clearHistory=()=>{
    if(!confirm('Clear all import history? This also removes imported transactions from Bank.'))return;
    const importedSAPs=new Set(imported.map(inv=>inv.invoiceSAP).filter(Boolean));
    if(importedSAPs.size>0){
      setBankTxs(prev=>prev.filter(t=>!importedSAPs.has(t.invoiceRef)||!t.desc.includes('CEVA')));
    }
    setImported([]);saveS('ir_invoices_v1',[]);
  };

  // ── HISTORY VIEW ──
  if(viewHistory){
    const unmatched=imported.filter(inv=>!inv.dealId);
    return(<div>
      <button onClick={()=>setViewHistory(false)} style={{padding:'5px 12px',borderRadius:6,border:'1px solid '+C.border,background:'transparent',color:C.mt,cursor:'pointer',fontSize:11,marginBottom:12}}>← Back</button>
      <div style={{fontSize:15,fontWeight:700,marginBottom:14}}>Import History ({imported.length} lines)</div>
      {unmatched.length>0&&<div style={{background:C.amber+'10',border:'1px solid '+C.amber+'30',borderRadius:8,padding:'8px 12px',marginBottom:12,fontSize:11,color:C.amber}}>{unmatched.length} lines without Deal ID — edit below</div>}
      <div style={{overflowX:'auto',maxHeight:'65vh',overflowY:'auto'}}>
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:10,minWidth:700}}>
          <thead><tr style={{borderBottom:'1px solid '+C.border,position:'sticky',top:0,background:C.card}}>{['Chassis','Service','Amount','Invoice','Deal ID','Date'].map(h=><th key={h} style={{padding:'6px',textAlign:'left',color:C.mt,fontWeight:500,fontSize:9,textTransform:'uppercase'}}>{h}</th>)}</tr></thead>
          <tbody>{imported.map((inv,i)=>(
            <tr key={i} style={{borderBottom:'1px solid '+C.border+'08',background:!inv.dealId?C.amber+'06':'transparent'}}>
              <td style={{padding:'4px 6px',fontSize:9,fontFamily:'monospace'}}>{(inv.chassis||'').slice(-8)}</td>
              <td style={{padding:'4px 6px'}}>{inv.service}</td>
              <td style={{padding:'4px 6px',textAlign:'right',color:C.red}}>{fmtD(inv.amount)}</td>
              <td style={{padding:'4px 6px',fontSize:9,color:C.mt}}>{inv.invoiceSAP}</td>
              <td style={{padding:'4px 6px'}}>{inv.dealId||<span style={{color:C.amber}}>—</span>}</td>
              <td style={{padding:'4px 6px',color:C.mt}}>{inv.date}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
      <div style={{marginTop:14,display:'flex',gap:8}}>
        <button onClick={clearHistory} style={{padding:'6px 14px',borderRadius:6,border:'1px solid '+C.red+'40',background:'transparent',color:C.red,fontSize:11,cursor:'pointer'}}>Clear History & Remove Bank Txs</button>
        <button onClick={()=>exportCSV(['Chassis','Service','Amount','Invoice','Deal ID','Date'],imported.map(inv=>[inv.chassis,inv.service,inv.amount,inv.invoiceSAP,inv.dealId,inv.date]),'invoice_history')} style={{padding:'6px 14px',borderRadius:6,border:'1px solid '+C.border,background:'transparent',color:C.mt,fontSize:11,cursor:'pointer'}}>Export CSV</button>
      </div>
    </div>);
  }

  // ── MAIN VIEW ──
  return(<div>
    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14}}>
      <div><div style={{fontSize:15,fontWeight:700}}>Invoice Reader</div><div style={{fontSize:11,color:C.mt,marginTop:2}}>Import supplier invoices, detect duplicates, create bank transactions</div></div>
      <div style={{display:'flex',gap:6}}>
        {imported.length>0&&<button onClick={()=>setViewHistory(true)} style={{padding:'6px 14px',borderRadius:6,border:'1px solid '+C.purple,background:'transparent',color:C.purple,fontSize:11,cursor:'pointer',fontWeight:600}}>History ({imported.length})</button>}
      </div>
    </div>

    {/* STEP 1: VIN Map */}
    <div style={{background:C.card,border:'1px solid '+C.border,borderRadius:10,padding:16,marginBottom:14}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
        <div style={{fontSize:12,fontWeight:600}}>1. VIN → Deal Mapping</div>
        {vinLookup.length>0&&<Badge t={vinLookup.length+' VINs loaded'} c={C.green}/>}
      </div>
      <div style={{display:'flex',gap:8,alignItems:'center'}}>
        <button onClick={()=>{if(vinFileRef.current)vinFileRef.current.click();}} style={{padding:'6px 14px',borderRadius:6,border:'1px solid '+C.cyan,background:vinLookup.length>0?C.cyan+'15':'transparent',color:C.cyan,fontWeight:600,fontSize:11,cursor:'pointer'}}>{vinLookup.length>0?'Re-upload VIN Map':'Upload VIN Map'}</button>
        <input ref={vinFileRef} type="file" accept=".csv,.xlsx,.xls" style={{display:'none'}} onChange={e=>{const f=e.target.files&&e.target.files[0];if(f)handleVinUpload(f);e.target.value='';}}/>
        <span style={{fontSize:10,color:C.mt}}>CSV or Excel with Chassis/VIN and Deal ID columns</span>
        {vinLookup.length>0&&<button onClick={()=>{setVinLookup([]);saveS('ir_vin_map_v1',[]);}} style={{padding:'4px 8px',borderRadius:4,border:'1px solid '+C.border,background:'transparent',color:C.mt,fontSize:9,cursor:'pointer'}}>Clear</button>}
      </div>
    </div>

    {/* STEP 2: Import Invoice */}
    <div style={{background:C.card,border:'1px solid '+C.border,borderRadius:10,padding:16,marginBottom:14}}>
      <div style={{fontSize:12,fontWeight:600,marginBottom:10}}>2. Import Invoice</div>
      <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:12}}>
        {CEVA_TEMPLATES.map(t=>(
          <button key={t.id} onClick={()=>{setTpl(t.id);setBulkSupplier(t.supplier);setBulkCostType(t.costType);setLines([]);}} style={{padding:'8px 16px',borderRadius:6,fontSize:11,fontWeight:tpl===t.id?700:400,cursor:'pointer',border:'1px solid '+(tpl===t.id?C.ac:C.border),background:tpl===t.id?C.ac+'15':'transparent',color:tpl===t.id?C.ac:C.mt}}>{t.label}</button>
        ))}
      </div>
      {tpl&&<>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr 1fr',gap:10,marginBottom:12}}>
          <Inp label="Invoice Ref / SAP" value={invoiceRef} onChange={e=>setInvoiceRef(e.target.value)} placeholder="e.g. 9016786913"/>
          <Inp label="Invoice Date" type="date" value={invoiceDate} onChange={e=>setInvoiceDate(e.target.value)}/>
          <Sel label="Classification" value={bulkClass} onChange={e=>setBulkClass(e.target.value)} options={['Operations','General']} placeholder={false}/>
          <Sel label="Supplier" value={bulkSupplier} onChange={e=>setBulkSupplier(e.target.value)} options={aux.SUPPLIERS||[]} placeholder={false}/>
        </div>
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          <button onClick={()=>{if(fileRef.current)fileRef.current.click();}} style={{padding:'8px 20px',borderRadius:8,border:'none',background:C.green,color:'#fff',fontWeight:600,fontSize:12,cursor:'pointer'}}>Upload Excel (.xlsx)</button>
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" style={{display:'none'}} onChange={e=>{const f=e.target.files&&e.target.files[0];if(f)handleFile(f);e.target.value='';}}/>
          <span style={{fontSize:10,color:C.mt}}>CEVA Excel detail with chassis per line</span>
        </div>
      </>}
    </div>

    {/* STEP 3: Review */}
    {lines.length>0&&<div style={{background:C.card,border:'1px solid '+C.border,borderRadius:10,padding:16,marginBottom:14}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
        <div style={{fontSize:12,fontWeight:600}}>3. Review ({lines.length} lines)</div>
        <div style={{display:'flex',gap:8,fontSize:11}}>
          <span style={{color:C.green}}>{lines.filter(l=>l.dealCode&&!l._dup).length} ready</span>
          <span style={{color:C.amber}}>{lines.filter(l=>!l.dealCode).length} no ID</span>
          <span style={{color:C.red}}>{lines.filter(l=>l._dup).length} duplicates</span>
        </div>
      </div>
      <div style={{overflowX:'auto',maxHeight:'45vh',overflowY:'auto'}}>
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:10,minWidth:900}}>
          <thead><tr style={{borderBottom:'1px solid '+C.border,position:'sticky',top:0,background:C.card}}>
            {['Chassis','Plate','Vehicle','Service','Qty','Total','Deal ID','Client','Status'].map(h=>(
              <th key={h} style={{padding:'6px',textAlign:'left',color:C.mt,fontWeight:500,fontSize:9,textTransform:'uppercase'}}>{h}</th>
            ))}
          </tr></thead>
          <tbody>{lines.map((l,i)=>{
            const isDup=l._dup;const noMatch=!l.dealCode;
            return(<tr key={i} style={{borderBottom:'1px solid '+C.border+'08',opacity:isDup?0.4:1,background:isDup?C.red+'06':noMatch?C.amber+'06':'transparent'}}>
              <td style={{padding:'4px 6px',fontSize:9,fontFamily:'monospace',color:C.mt}}>{(l.chassis||'').slice(-8)}</td>
              <td style={{padding:'4px 6px',fontSize:10}}>{l.plate||'—'}</td>
              <td style={{padding:'4px 6px',fontSize:10,color:C.mt,maxWidth:120,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{l.version||'—'}</td>
              <td style={{padding:'4px 6px',fontSize:10}}>{l.service}</td>
              <td style={{padding:'4px 6px',textAlign:'right'}}>{l.units}</td>
              <td style={{padding:'4px 6px',textAlign:'right',fontWeight:600,color:C.red}}>{fmtD(l.total)}</td>
              <td style={{padding:'4px 6px'}}><input value={l.dealId||''} onChange={e=>{const v=e.target.value.replace(/[^0-9]/g,'');updateLine(l._id,'dealId',v);const deal=clients.find(c=>c.i===+v);if(deal){updateLine(l._id,'dealName',deal.n);updateLine(l._id,'dealCode',deal.c);}else if(v){updateLine(l._id,'dealName','#'+v);updateLine(l._id,'dealCode',v);}else{updateLine(l._id,'dealName','');updateLine(l._id,'dealCode','');}}} placeholder="ID" style={{width:45,padding:'2px 4px',borderRadius:4,border:'1px solid '+C.border,background:C.bg,color:l.dealCode?C.green:C.tx,fontSize:10,outline:'none',textAlign:'center'}}/></td>
              <td style={{padding:'4px 6px',fontSize:10,color:l.dealName?C.green:C.amber}}>{l.dealName||'—'}</td>
              <td style={{padding:'4px 6px'}}>{isDup?<span><Badge t="DUP" c={C.red}/><span style={{fontSize:8,color:C.mt,marginLeft:3}}>{l._dupRef}</span></span>:noMatch?<Badge t="NO ID" c={C.amber}/>:<Badge t="OK" c={C.green}/>}</td>
            </tr>);
          })}</tbody>
        </table>
      </div>

      {/* Import options */}
      <div style={{marginTop:14,padding:12,background:C.bg,borderRadius:8,border:'1px solid '+C.border}}>
        <div style={{display:'flex',gap:12,alignItems:'end',flexWrap:'wrap'}}>
          <div><label style={{fontSize:10,color:C.mt,display:'block',marginBottom:2}}>Payment Status</label><div style={{display:'flex',gap:4}}>{['Yes','No'].map(v=>(<button key={v} onClick={()=>setPayOnImport(v)} style={{padding:'5px 12px',borderRadius:5,fontSize:11,cursor:'pointer',border:'1px solid '+(payOnImport===v?(v==='Yes'?C.green:C.amber):C.border),background:payOnImport===v?(v==='Yes'?C.green+'15':C.amber+'15'):'transparent',color:payOnImport===v?(v==='Yes'?C.green:C.amber):C.mt}}>{v==='Yes'?'Import as Paid':'Import as Unpaid'}</button>))}</div></div>
          {payOnImport==='No'&&<Inp label="Expected Payment Date" type="date" value={expectedPayDate} onChange={e=>setExpectedPayDate(e.target.value)} style={{width:160}}/>}
          <button onClick={confirmImport} disabled={lines.filter(l=>l.dealCode&&!l._dup).length===0} style={{padding:'8px 20px',borderRadius:8,border:'none',background:lines.filter(l=>l.dealCode&&!l._dup).length>0?C.green:C.border,color:lines.filter(l=>l.dealCode&&!l._dup).length>0?'#fff':C.mt,fontWeight:600,fontSize:12,cursor:lines.filter(l=>l.dealCode&&!l._dup).length>0?'pointer':'default'}}>Import {lines.filter(l=>l.dealCode&&!l._dup).length} Transactions</button>
          <button onClick={()=>setLines([])} style={{padding:'8px 14px',borderRadius:8,border:'1px solid '+C.border,background:'transparent',color:C.mt,fontSize:11,cursor:'pointer'}}>Clear</button>
          <button onClick={()=>exportCSV(['Chassis','Plate','Vehicle','Service','Qty','Unit Price','Total','Invoice SAP','Deal ID','Client','Duplicate','Dup Ref'],lines.map(l=>[l.chassis,l.plate,l.version,l.service,l.units,l.unitPrice,l.total,l.invoiceSAP,l.dealId,l.dealName,l._dup?'YES':'',l._dupRef]),'invoice_review')} style={{padding:'8px 14px',borderRadius:8,border:'1px solid '+C.border,background:'transparent',color:C.mt,fontSize:11,cursor:'pointer'}}>Export CSV</button>
        </div>
      </div>
    </div>}
  </div>);
}

// ══════════════════════════════════════════
//  SETTINGS
// ══════════════════════════════════════════
function Settings({aux,setAux}){
  const [ec,setEc]=useState(null);const [nv,setNv]=useState('');
  const cats=[{k:'BD',l:'Business Developers'},{k:'CAR_BRAND',l:'Car Brands'},{k:'BUSINESS_LINE',l:'Business Lines'},{k:'ENGINE',l:'Engine Types'},{k:'SUPPLIERS',l:'Suppliers'},{k:'BANKS',l:'Bank Accounts'},{k:'COST_TYPE',l:'Cost Types'},{k:'COST_LINE',l:'Cost Lines'},{k:'PNL',l:'PnL'}];
  const add=k=>{if(!nv.trim())return;setAux(p=>({...p,[k]:[...new Set([...(p[k]||[]),nv.trim()])]}));setNv('');};const rm=(k,v)=>setAux(p=>({...p,[k]:(p[k]||[]).filter(x=>x!==v)}));
  return(<div><div style={{fontSize:15,fontWeight:700,marginBottom:4}}>Settings</div><div style={{fontSize:11,color:C.mt,marginBottom:16}}>Manage dropdown options across all modules</div>
    <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))',gap:12}}>{cats.map(cat=>(<div key={cat.k} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:14}}><div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}><div style={{fontSize:12,fontWeight:600}}>{cat.l} ({(aux[cat.k]||[]).length})</div><button onClick={()=>setEc(ec===cat.k?null:cat.k)} style={{padding:'3px 10px',borderRadius:5,border:`1px solid ${C.border}`,background:'transparent',color:C.mt,cursor:'pointer',fontSize:10}}>{ec===cat.k?'Done':'Edit'}</button></div><div style={{display:'flex',flexWrap:'wrap',gap:3}}>{(aux[cat.k]||[]).map(v=>(<span key={v} style={{display:'inline-flex',alignItems:'center',gap:3,padding:'2px 7px',borderRadius:4,fontSize:10,background:C.bg,color:C.tx,border:`1px solid ${C.border}`}}>{v}{ec===cat.k&&<button onClick={()=>rm(cat.k,v)} style={{background:'none',border:'none',color:C.red,cursor:'pointer',fontSize:11,padding:0}}>×</button>}</span>))}</div>{ec===cat.k&&<div style={{display:'flex',gap:4,marginTop:8}}><input value={nv} onChange={e=>setNv(e.target.value)} onKeyDown={e=>e.key==='Enter'&&add(cat.k)} placeholder="Add..." style={{flex:1,padding:'5px 8px',borderRadius:5,border:`1px solid ${C.border}`,background:C.bg,color:C.tx,fontSize:11,outline:'none'}}/><button onClick={()=>add(cat.k)} style={{padding:'5px 10px',borderRadius:5,border:'none',background:C.ac,color:'#fff',cursor:'pointer',fontSize:11,fontWeight:600}}>+</button></div>}</div>))}</div>
    <div style={{marginTop:16,background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:14}}><div style={{fontSize:12,fontWeight:600,marginBottom:6}}>Financial Configuration</div><div style={{fontSize:11,color:C.mt}}>VAT: 21/121 = {(VAT_RATE*100).toFixed(4)}% · Commission: 10% MIT + €{FIN_BONUS} if financed · REBU: VAT on margin only</div></div>
  </div>);
}
